// Bare shared-library import (NOT a deep `.../functions/workspacePrefix.js`
// subpath) — a per-file deep specifier into @symbo-ls/workspace-shared (a
// restricted/private-registry package) cannot resolve through the platform's
// public importmap and threw an uncaught "Failed to resolve module
// specifier" that killed the entire workspace-shell catalog page (P0,
// tickets/catalog-fixall.md). The bare package root IS resolvable (same
// pattern already proven by sharedLibraries.js across every consumer).
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './functions/shellSharedDelegates.js'
// Deferred-call wrappers, not a module-scope destructure — app.js is the
// entry point (nothing imports it), but Parcel's bundle execution order is
// not provably identical to ESM import order, and two OTHER "provably
// safe" files (functions/searchApps.js's VIEW_NAV, functions/
// externalData.js's stripWorkspacePrefix) already crashed the production
// build this exact way. No exceptions, per batch-2's own lesson.
const isGuestAllowedPath = (...args) => _wsShared.functions.isGuestAllowedPath(...args)
const applyA11yPrefs = (...args) => _wsShared.functions.applyA11yPrefs(...args)
const commAssistantMode = (...args) => _wsShared.functions.commAssistantMode(...args)
const shellFeatureEnabled = (...args) => _wsShared.functions.featureEnabled(...args)
const shellNavItemVisible = (...args) => _wsShared.functions.navItemVisible(...args)
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
const shellSyncWorkspaceUrl = (...args) => _wsShared.functions.syncWorkspaceUrl(...args)
const shellSetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.setActiveWorkspacePointer(...args)
const findWorkspaceInOrg = (...args) => _wsShared.functions.findWorkspaceInOrg(...args)
const moduleRoutesFromRoot = (...args) => _wsShared.functions.moduleRoutesFromRoot(...args)
const shellWorkspaceApiBase = (...args) => _wsShared.functions.workspaceApiBase(...args)
const shellIsModuleRoute = (...args) => _wsShared.functions.isModuleRoute(...args)
const shellIsModuleCanvasRoute = (...args) => _wsShared.functions.isModuleCanvasRoute(...args)
const syncPointerCapability = (...args) => _wsShared.functions.syncPointerCapability(...args)
const getPref = (...args) => _wsShared.functions.getPref(...args)
const workspaceSettingsOf = (...args) => _wsShared.functions.workspaceSettings(...args)
const registerWorkspaceHandleResolver = (...args) =>
  _wsShared.functions.registerWorkspaceHandleResolver(...args)
const shellGetWorkspacePrefixSlug = (...args) => _wsShared.functions.getWorkspacePrefixSlug(...args)
// "the active workspace just became a FULL record" — the ONE event every
// workspace-change path funnels through (see the language-policy subscription
// in onCreate). Same call-time indirection as every shared read above: a
// module-scope read of a shared-library value publishes as a stub under frank.
const onActiveWorkspaceFull = (...args) => _wsShared.functions.onActiveWorkspaceFull(...args)
// Deferred getters, NOT a module-scope destructure — proven necessary live
// (smbls push build failure: "Cannot destructure property 'appShellStyles'
// of '...default.components' as it is undefined", tickets/execution-
// board.md §1). app.js being the entry point does NOT make it safe: the
// same class of failure already hit two files reached only indirectly
// (functions/searchApps.js's VIEW_NAV, functions/externalData.js's
// stripWorkspacePrefix). shellApp below reads through these via per-key
// reactive CSS prop factories (called at render time, never at module
// scope) instead of a `...appShellStyles` spread.
const getOrgSwitcherInternals = () => _wsShared.components.__orgSwitcherInternals
import { hydrateFeatureFlags } from './functions/featureFlags.js'
import {
  subscribeAuthSession,
  isChromelessShellPath,
  hasSessionToken,
  hasTrustedOrgList
} from './functions/auth.js'
import {
  loadPrivateData,
  onWorkspaceChange,
  beginDataLoad,
  endDataLoadFailsafe,
  _person as mapMemberRow
} from './functions/loadPrivateData.js'
import { registerShellRoot } from './functions/workspaceApps.js'
import {
  moduleImmersiveActive,
  moduleRailActive,
  moduleRailEdge,
  applyStageVar,
  moduleViewAsKey,
  slotFullscreenActive
} from './functions/workspaceProjects.js'
import {
  moduleComposerHiddenLive,
  releaseStaleModuleComposerLatch
} from './functions/moduleShellComposer.js'
import { DEMO_BLOCKED_ROUTE_RE } from './functions/demoRouteGuard.js'
import {
  orgSwitchStaleWorkspaces,
  orgSwitchUrlWorkspace,
  orgSwitchHopWorkspace
} from './functions/orgSwitchUrl.js'
import { mwtOnRenderRouter, reconcileMwtOrgFromUrl } from './functions/mwtRouter.js'
import { needTicketDetailModal } from './functions/ticketDetailModalLoad.js'
import { bootCustomDomainScope } from './functions/customDomainBoot.js'
import { isCustomDomainHost } from './functions/customDomainHost.js'
import {
  isProjectCanvasPath as _isProjectCanvasPath,
  urlOrgSlug as _urlOrgSlug
} from './functions/urlOrgSlug.js'
import { recordBootSnapshotSkin } from './functions/bootSnapshot.js'
import { writeBootLangHint } from './functions/bootLang.js'
import { resolveWorkspaceSkin } from './functions/workspaceSkin.js'
import {
  assistantDockStamp,
  lastAssistantSide,
  promoteAssistantDockForOpen,
  readStoredAssistantDock,
  restoreAssistantDockOnClose,
  setAssistantDock
} from './functions/assistantDock.js'
import { readStoredAssistantMode } from './functions/assistantMode.js'
import { agentModeFromSearch, readStoredAgentMode } from './functions/agentMode.js'
import { readStoredAssistantWidth } from './functions/assistantWidth.js'
import { isPublicShareViewPath } from './functions/shareLinks.js'
import { peekPrimedPeople, primedOrgSlug } from './functions/peoplePrime.js'
import { fetchOrgNotifCount } from './functions/notifications/fetchOrgNotifCount.js'
import { SHELL_STAGE, startShellStageLadder } from './functions/shellStages.js'
import {
  bootAnalyzing,
  identifyFromSdk,
  reidentifyOnOrgSwitch,
  resolveAnalyzingScopeId
} from './analyzing.js'
import { deferIdleBoot, bootMark, bootMeasure } from './functions/idleBoot.js'
import { withLiveQuery as _withLiveQuery } from './functions/bootRedirectQuery.js'
import {
  inboundPromptForBuilder,
  receiveInboundPrompt,
  takeParkedInboundPrompt
} from './functions/inboundPrompt.js'
import { untrackedState } from './functions/untrackedState.js'
import { orgSwitcherPanelPin } from './functions/orgSwitcherPanelPin.js'
import { queryClient } from '@symbo.ls/fetch'
import media from '@symbo-ls/brand/designSystem/media.js'

// Leave any MODULE route SYNCHRONOUSLY at tenant-switch entry, BEFORE the
// scoped-state wipe: module routes belong to the outgoing workspace's
// manifest, and a mounted module page re-rendering against the wiped (null)
// module bag can spin the renderer — observed live as a hard-frozen tab on
// an aihouse /users → greenig switch (2026-08-01). Home is the one safe,
// predictable parking spot for a tenant swap, and the hop happens behind
// the switch loader. Static intranet routes and canvas paths never bounce.
const _leaveModuleRouteForSwitch = (el, s, oldModuleRoutes) => {
  try {
    const win = typeof window !== 'undefined' ? window : null
    if (!win?.location) return
    const pathOnly = shellStripWorkspacePrefix(win.location.pathname || '/').split(/[?#]/)[0]
    if (pathOnly === '/' || !shellIsModuleRoute(pathOnly, oldModuleRoutes)) return
    el.router(_withLiveQuery('/'), el)
  } catch (err) {
    console.warn('[shell] module-route leave failed:', err?.message || err)
  }
}

const NOTIF_POLL_MS = 5 * 60 * 1000
// CALL-INVITATION (tickets/fable.md) — cadence for the CallInvitationBanner
// self-expire nudge (`_meetingInviteTick`, wired in onCreate below). 30s
// keeps a stale "meeting ended" banner from lingering more than half a
// minute past its own endAt without adding real timer pressure.
const MEETING_INVITE_TICK_MS = 30 * 1000
// Every top-row island AppHeader can render — HeaderLeft included. It used to
// be excluded here: a stale "HeaderLeft is a vertical side rail on tablet"
// rationale that no longer applies (that mode is fully retired — see
// AppHeader.js HeaderLeft, no tabletS flex-direction override remains).
// Primarily a @tabletS (769–1024px) concern now — see
// _measureHeaderNavClear below, which bypasses this array entirely at
// @mobileL in favor of measuring the merged "one deck" header's own rect —
// but excluding HeaderLeft was ALSO the direct cause of the pre-fix phone
// under-measurement this array existed to prevent (live-measured 62.6px
// header bottom vs a 43px --nav-clear) before the @mobileL bypass existed,
// so it stays included for @tabletS correctness (HeaderLeft can still be the
// tallest of the four floating islands there).
const HEADER_TOP_ISLAND_KEYS = ['HeaderLeft', 'HeaderSearch', 'HeaderTabs', 'HeaderRight']

// Live-call count for the NewCall "ongoing call" badge: rooms in THIS workspace
// still open (no endedAt), that I'm not already in (activeRoomId), with at least
// one still-connected participant who isn't me. Derives from the shell-level meet
// realtime (root.rooms + root.liveParticipants) and self-clears as those update.
// `currentUserSupabaseId` is the participant id-space (auth uuid, not the Mongo id).
const _liveCallCount = (root) => {
  const rooms = root?.rooms || []
  const live = root?.liveParticipants || {}
  const activeRoomId = root?.activeRoomId
  const me = root?.currentUserSupabaseId
  return rooms.filter(
    (r) =>
      r &&
      !r.endedAt &&
      r.id !== activeRoomId &&
      (live[r.id] || []).some((p) => !p.leftAt && p.userId !== me)
  ).length
}

// Shell DOMQL — AppHeader + smbls-default content + TranslationWatcher.
//
// AppHeader: persistent top header. Hidden on auth routes via `hide:`.
// content: smbls router renders the matched page here automatically —
//   no explicit Slot element needed. The default router places content
//   as row 2 (1fr) in the AppShell grid.
// TranslationWatcher: hidden i18n subscription (position:fixed, no grid row).
//
// AppShell grid (from appShellStyles):
//   gridTemplateRows: 'auto 1fr'
//   AppHeader → row 1 (auto). smbls content → row 2 (1fr, fills viewport).
//   When AppHeader is hidden (display:none on auth routes) it leaves the
//   grid — content expands to fill the full viewport automatically.
// Derived from the canonical guest-only route list (functions/auth.js
// AUTH_PATHS) so this shell gate (header hide + route-kind classification)
// can't drift from the redirect logic. `(\/|$)` boundary matches an exact
// auth path or a sub-path, same regex as before.
// ── Live-measured CSS-var maintenance ────────────────────────────────────
// One recipe for every "pin a chrome edge into a :root var" need (--nav-clear
// from the AppHeader, --prompt-clear from the composer): a ResizeObserver on
// the element + a window-resize re-apply + a post-mount settle pass, writing
// documentElement ONLY when the value changed (a same-value setProperty still
// risks recalc on some engines, and a changed write invalidates every
// var() consumer). Extracted 2026-07-12 (/simplify): the two inline copies had
// already diverged — one had the settle machinery, the other did not.
// Per-workspace stage colour writer (chrome.stage → --stage-bg on <html>).
// SAME pattern as _trackClearVar below and for the SAME reason: root-level
// element props do NOT re-evaluate when late root state (workspaceProject)
// lands — a background factory on shellApp painted its boot-time value
// forever (measured live 2026-08-16: stage "#F2EDE3" present in state,
// shell root still rgba(0,0,0,0)). The var IS the reactivity: this writer
// runs on every ModuleRailHost render/update (subscribeTo workspaceProject,
// and `hide` keeps that node mounted), and every ground references the
// var statically through ONE chain — `var(--stage-bg, var(--color-stage))`
// (shared/functions/canvasBackground.js STAGE_BACKGROUND; BF-WIDGET-INK-1).
// A workspace with no chrome.stage REMOVES the property, so every ground
// falls back to the design-system `stage` token together — root, content
// host, home page/board and canvas Stages can no longer disagree, because
// none of them holds a value of its own.
// ── Module rail geometry predicates (ONE definition each — the class
// factories, the chrome-inset negation and the `railFlush` prop the mounted
// rail receives all read these; a second copy of "is the rail on the edge"
// is exactly the duplicated-precedence drift this shell has paid for
// before). Edge state = the PageSlot's own isSlotFullscreen decision:
// immersive members, or an admin's edge-to-edge toggle.
const _railEdgeState = (root) =>
  (moduleImmersiveActive(root) && slotFullscreenActive(root)) || root?._slotFullscreen === true
// Flush = the manifest opted in (chrome.railEdge) AND the host really runs
// the edge state now. Bellforge's BfNav drops its corner radius on this
// prop (bellforge-ui lane, 2026-09-02) — the rail renders inside the
// module's state scope and cannot see shell root keys itself.
const _railFlush = (root) => moduleRailEdge(root) && _railEdgeState(root)
// THE PHONE DRAWER. On a phone with the dock (functions/phoneNavigation.js —
// a workspace, no immersive chrome) the rail stops being a column beside the
// page: it leaves the screen and opens OVER the page from the dock's Page
// button (`_moduleRailOpen`), so it never insets the page (--module-rail-clear
// reads 0) and never covers it at rest. Immersive members keep the column —
// the rail IS their chrome there, and no dock exists to open a drawer.
const _moduleRailDrawer = (root) =>
  root?._isPhoneViewport === true && !!root.workspace && !moduleImmersiveActive(root)
const _moduleRailDrawerShut = (root) => _moduleRailDrawer(root) && !root._moduleRailOpen
// Push a CHANGED flush value to the mounted rail (Body.content — the
// element `elm.set(def)` created). Runs on every host render/update; the
// host already subscribes to the keys that flip the edge state
// (_slotFullscreen, workspaceProject, currentPath…). Same-value calls
// return early so the rail never re-renders for nothing.
const _pushRailFlush = (el, s) => {
  const flush = _railFlush(s.root)
  if (el.__railFlush === flush) return
  el.__railFlush = flush
  const rail = el.Body && el.Body.content
  if (rail && typeof rail.update === 'function') {
    try {
      rail.update({ railFlush: flush }, { preventFetch: true })
    } catch (_) {
      /* rail not mounted yet — the mount def carries the current value */
    }
  }
}
const _setStageVar = (el, s) => {
  if (typeof document === 'undefined') return
  // Var half delegated to the shared writer (workspaceProjects.applyStageVar)
  // so this render-path write and the settle-point repairs can never drift.
  applyStageVar(s?.root)
  // ALSO paint the shell root node inline — with the CHAIN, never a colour.
  // The declared background on shellApp loses to its `theme: 'document'`
  // class rule (measured 2026-08-16: computed rgb(10,10,10) in dark / white
  // in light over the var() declaration — that surface is exactly the
  // "black behind the sidebar" and the white after a theme flip). An inline
  // style wins over the theme's class rule with no !important; the chain is
  // theme-agnostic (var() re-resolves on every flip), so writing it once is
  // canvasBackground.js rule 1 compliant, and it never has to be cleared:
  // the ground is ALWAYS the stage — the module override or the token.
  const CHAIN = 'var(--stage-bg, var(--color-stage))'
  const rootEl = el && typeof el.getRoot === 'function' ? el.getRoot() : null
  const node = rootEl && rootEl.node
  if (node && node.style && node.style.background !== CHAIN) {
    node.style.background = CHAIN
  }
  // The router's `content` host paints the THEME's page surface over the
  // root (measured rgb(69,69,75) between the beige root and the page
  // cards) — so the stage was visible only in the rail strip, never on
  // home/studio/tickets. Same chain, same reason.
  const contentEl = rootEl && rootEl.content
  const contentNode = contentEl && contentEl.node
  if (contentNode && contentNode.style && contentNode.style.background !== CHAIN) {
    contentNode.style.background = CHAIN
  }
}

const _stopClearVar = (el, flag) => {
  // Invalidate queued deliveries before disconnecting: an observer callback
  // already in the delivery queue must not read a removed/replacement node.
  el[flag + 'Apply'] = null
  el[flag]?.disconnect()
  if (el[flag + 'Frame'] != null) cancelAnimationFrame(el[flag + 'Frame'])
  if (el[flag + 'Timer'] != null) clearTimeout(el[flag + 'Timer'])
  el[flag] = null
  el[flag + 'Node'] = null
  el[flag + 'Frame'] = null
  el[flag + 'Timer'] = null
  el[flag + 'Last'] = null
  el[flag + 'ExtraLast'] = null
}

// A SECOND variable measured off the same observer (`extra` of _trackClearVar:
// { varName, measure(rect, el) }), deduped on its own last value. Written
// BEFORE the primary, so the primary stays the newest write of every pass.
const _writeClearExtra = (el, flag, varName, value) => {
  if (el[flag + 'ExtraLast'] === value) return
  el[flag + 'ExtraLast'] = value
  document.documentElement.style.setProperty(varName, value)
}

// The composer's width for the island docked above it — the resting pill only
// (null = keep the last value while the composer is expanded, like
// --prompt-clear).
const _PROMPT_WIDTH = {
  varName: '--prompt-w',
  measure: (r, el) => (el.state && el.state.size && el.state.size !== 'small' ? null : r.width)
}

// THE COMPOSER'S STACK TOP — the resting pill, or the highest row DOCKED on it
// while one shows: the thread chips and the attached context (the shell mount's
// PromptBand). The band stands above the pill (absolute, `bottom: 100%`), so
// the pill's own top counted none of its rows, and every --slot-bottom
// consumer ended under them (fly-onboarding, ai-nino 2026-10-28: the saved
// board's name chip covered the board's last cards; the composer read 43px
// with the chips up). Never the AI island (it paints on focus only and stays
// laid out while it is invisible) nor a draft's image thumbs above it.
const _COMPOSER_DOCKED_ROWS = ['AssistantThreadChips', 'AiContextBar']
const _composerStackTop = (el, r) => {
  const band = el.PromptBand || {}
  let top = r.top
  for (const key of _COMPOSER_DOCKED_ROWS) {
    const node = band[key] && band[key].node
    if (!node || typeof node.getBoundingClientRect !== 'function') continue
    const row = node.getBoundingClientRect()
    if (row.height > 0 && row.top < top) top = row.top
  }
  return top
}

const _settleClearVar = (el, flag) => {
  const apply = el[flag + 'Apply']
  if (!apply) return
  // Preserve the immediate measurement and both settle opportunities, while
  // bounding boot/update storms to one pending callback of each kind.
  if (el[flag + 'Frame'] == null) {
    el[flag + 'Frame'] = requestAnimationFrame(() => {
      if (el[flag + 'Apply'] !== apply) return
      el[flag + 'Frame'] = null
      apply()
    })
  }
  // Follow the LAST render: a position-only transition can finish after the
  // first render's deadline without emitting a ResizeObserver size change.
  if (el[flag + 'Timer'] != null) clearTimeout(el[flag + 'Timer'])
  el[flag + 'Timer'] = setTimeout(() => {
    if (el[flag + 'Apply'] !== apply) return
    el[flag + 'Timer'] = null
    apply()
  }, 300)
}

// An island's top edge from the viewport bottom (a bottom-docked island's
// clearance).
const _clearFromTop = (r) => window.innerHeight - r.top

// `watch` (optional): one more node the same observer watches — a size change
// there re-measures too (the composer's band: a row docked on the pill grows
// the band, never the pill).
const _trackClearVar = (el, flag, varName, measure, extra, watch) => {
  if (typeof ResizeObserver === 'undefined' || !el.node) return
  // Re-arm on node identity change. Boot storms and hide-cycles can
  // RE-CREATE the element's DOM node; the old one-shot guard left the
  // ResizeObserver watching the dead node, which never resizes again — so
  // the var froze at its last write (measured live 2026-08-14: rail
  // rendered 273px wide with --module-rail-clear stuck at 0px, whole-shell
  // inset lost until a window resize happened to re-fire apply). Same-node
  // calls RE-MEASURE instead of returning: RO delivery around a
  // hidden→visible flip mid-boot-storm proved unreliable in practice
  // (observer verified armed on the live laid-out node, callback never
  // fired), so every lifecycle touch runs the value-deduped apply as the
  // guaranteed path — RO stays as the size-change fast path. Window resize
  // is owned by each component's onWindowResize lifecycle prop.
  if (el[flag] && el[flag + 'Node'] === el.node) {
    const reapply = el[flag + 'Apply']
    if (reapply) {
      reapply()
      _settleClearVar(el, flag)
    }
    return
  }
  _stopClearVar(el, flag)
  el[flag + 'Node'] = el.node
  el[flag + 'Last'] = null
  el[flag + 'ExtraLast'] = null
  const apply = () => {
    if (el[flag + 'Apply'] !== apply || !el.node) return
    const r = el.node.getBoundingClientRect()
    if (!r.height) {
      // Distinguish "not laid out yet" (keep the last value — the settle
      // pass will re-measure) from "chrome HIDDEN" (hide: → display:none):
      // a hidden chrome edge must ZERO its var, or content stays inset
      // below a navbar that no longer exists (immersive module chrome,
      // auth routes). Guarded on computed display so pre-layout zero-rects
      // never write.
      let hidden = false
      try {
        hidden = el.node.isConnected && getComputedStyle(el.node).display === 'none'
      } catch (_) {}
      if (!hidden) return
      if (extra) _writeClearExtra(el, flag, extra.varName, '0px')
      if (el[flag + 'Last'] === '0px') return
      el[flag + 'Last'] = '0px'
      document.documentElement.style.setProperty(varName, '0px')
      return
    }
    if (extra) {
      const more = extra.measure(r, el)
      if (more !== null && more !== undefined) {
        _writeClearExtra(el, flag, extra.varName, Math.round(more) + 'px')
      }
    }
    const measured = measure(r)
    // `null` = "not a measurable state right now, keep the last value"
    // (the composer's expanded overlay — see its onRender).
    if (measured === null || measured === undefined) return
    const v = Math.round(measured) + 'px'
    if (el[flag + 'Last'] === v) return
    el[flag + 'Last'] = v
    document.documentElement.style.setProperty(varName, v)
  }
  el[flag + 'Apply'] = apply
  el[flag] = new ResizeObserver(apply)
  el[flag].observe(el.node)
  if (watch) el[flag].observe(watch)
  // The window-resize half is DECLARED by each of the three callers as
  // `onWindowResize: (e, el) => el._<flag>Apply?.()`. It used to be a raw
  // `window.addEventListener('resize', apply)` here behind an
  // `el[flag + 'Win']` latch, removed nowhere (FA503) — one permanent window
  // listener per element, each holding a closure over that element for the
  // life of the tab. Reading through `el[flag + 'Apply']` keeps the original
  // property that made the latch safe: `apply` reads `el.node` LIVE, so a
  // re-arm on a new node re-points the SAME declared handler.
  // Settle pass — fonts/layout can shift the rect just after mount.
  _settleClearVar(el, flag)
  apply()
}

// Below @tabletS (desktop, incl. 1440px) this ALWAYS returns fallbackRect.bottom
// (the early return right below) — HEADER_TOP_ISLAND_KEYS is never even read at
// those widths, so including/excluding HeaderLeft there cannot change the
// desktop measurement; no extra phone-width gate needed on top of this one.
//
// At @tabletS (769–1024px), AppHeader's islands are still separate floating
// pills that can render TALLER than the header's own naive box in ways a
// single fallbackRect doesn't capture — so measure each visible top-row
// island's own bottom edge and take the max there, rather than trusting one
// bounding rect.
//
// At @mobileL (≤768px) the "one deck" merge (AppHeader.js) makes this
// unnecessary AND less accurate: every island is now vertically CENTERED
// inside ONE uniform-height box with its own top/bottom padding, so the
// tallest CHILD's bottom edge sits *above* the deck's own visual bottom by
// that padding amount — measured live, the per-child max read 39px against
// the deck's real 44.5px bottom. The deck's own fallbackRect IS the accurate
// boundary here, exactly like the desktop case above.
const _measureHeaderNavClear = (el, fallbackRect) => {
  if (typeof window === 'undefined') return fallbackRect.bottom
  // A navbar moved off the top (a rail or a bottom bar — shared
  // functions/navbarLayout.js) leaves no navbar to clear at the top: 0, and
  // the injected sheet's place rules set the top clearances to the edge gap.
  if (_navPlace(el) !== 'top') return 0
  // @mobileL keeps the deck's own rect (see the note above). Every WIDER
  // width now measures the ISLANDS' real bottom edge, desktop included:
  // the old desktop early-return trusted the mount box (48px), but the
  // floating islands END ~5px above it (measured 43.1px, 2026-09-01) —
  // the row's empty remainder inflated the PageSlot's top gap to ~15px
  // while the composer-side gap (same --nav-gap) stayed 10px. Nika:
  // "make top space between pageslot and top navs same as the bottom one."
  if (window.matchMedia(media.mobileL).matches) {
    return fallbackRect.bottom
  }
  const topIslandBottoms = HEADER_TOP_ISLAND_KEYS.map((key) =>
    el.node?.querySelector(`:scope > [data-key="${key}"]`)
  )
    .filter((node) => node && window.getComputedStyle(node).display !== 'none')
    .map((node) => node.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .map((rect) => rect.bottom)
  return topIslandBottoms.length ? Math.max(...topIslandBottoms) : fallbackRect.bottom
}

// Where the person put the navbar (shared functions/navbarLayout.js — 'top'
// on a phone and on a host without the look). Read through the registered
// function so the factories that call it subscribe to the look keys.
const _navPlace = (el) => {
  const nav =
    el && typeof el.call === 'function'
      ? el.call('navbarLayoutRead', el.state && el.state.root)
      : null
  return (nav && nav.position) || 'top'
}

// --nav-edge: how far the navbar reaches in from ITS OWN screen edge — a
// rail's far side (left: its right edge; right: the viewport width less its
// left edge), a bottom bar's islands' top from the viewport bottom; 0 at the
// top (--nav-clear measures that one). The injected sheet adds --nav-gap to
// make the frame's inset on that side (--nav-inset-left/right/bottom). A
// rail IS the header box (the shared rail rules zero its padding); at the
// bottom the box keeps its X2 padding, so the islands' own tops are read, as
// _measureHeaderNavClear reads their bottoms at the top.
const _measureHeaderNavEdge = (el, r) => {
  if (typeof window === 'undefined') return 0
  const place = _navPlace(el)
  let edge = 0
  if (place === 'left') edge = r.right
  else if (place === 'right') edge = window.innerWidth - r.left
  else if (place === 'bottom') {
    const islandTops = HEADER_TOP_ISLAND_KEYS.map((key) =>
      el.node?.querySelector(`:scope > [data-key="${key}"]`)
    )
      .filter((node) => node && window.getComputedStyle(node).display !== 'none')
      .map((node) => node.getBoundingClientRect())
      .filter((rect) => rect.width > 0 && rect.height > 0)
      .map((rect) => rect.top)
    edge = window.innerHeight - (islandTops.length ? Math.min(...islandTops) : r.top)
  }
  _composerFollowsNavEdge(el, Math.round(edge))
  return edge
}

// The composer sits on a bottom bar (the injected sheet lifts it by
// --nav-inset-bottom) and drops back when the bar leaves — a position change,
// which fires no ResizeObserver, so its --prompt-clear (the foot of the
// PageSlot and of every sheet) kept the old place (measured on dev 68d0bfe57:
// the composer at 92px from the bottom, --prompt-clear still 45px, the open
// window under it). When this edge changes, the composer — a sibling mount on
// the shell root — re-measures once the new value has landed: its own
// tracker, then its settle passes.
const _composerFollowsNavEdge = (el, edge) => {
  if (el.__navEdgeSeen === edge) return
  el.__navEdgeSeen = edge
  const composer = el.parent && el.parent.CanvasPromptTextarea
  if (!composer || typeof requestAnimationFrame === 'undefined') return
  requestAnimationFrame(() => {
    if (typeof composer._pcObsApply !== 'function') return
    composer._pcObsApply()
    _settleClearVar(composer, '_pcObs')
  })
}

// Chrome-kind classification ONLY (header hide, prompt hide, Zoombar hide,
// data-shell-route) — the actual auth bouncing lives in functions/auth.js.
// `s/<token>` rides this list for the same reason the auth pages do: the
// public share viewer (PUBLIC-SHARE-LINKS-1) is a CHROMELESS surface for a
// visitor with no account, and rendering the workspace nav (Studio / Files /
// Tasks / Team) around it would offer a signed-out recipient a set of links
// that all bounce to /login. Measured live 2026-08-22 before this line
// existed: the viewer rendered the note correctly WITH the full member nav
// above it.
// The chromeless rule lives in functions/auth.js (isChromelessShellPath):
// the guest-only auth paths, the share viewer above, and the invitation
// landings.

// The URL → org classifiers (`_isProjectCanvasPath`, `_urlOrgSlug` and the
// reserved-segment list behind them) live in functions/urlOrgSlug.js since
// WHITELABEL-STRICT-TENANT-SCOPE-1: the white-label route guard and the
// custom-domain boot ask the same "which org does this URL name" question,
// and one definition keeps the answers from drifting. Imported under their
// old local names (see the import block at the top of this file).

// tickets/workspace.md "neutral chrome when the URL's owner is not one of
// your orgs" — residual from workspace 9deb1386. `mwtOrgToReconcile`
// correctly refuses to switchOrg onto a stranger's public project (never
// attempted — see its own comment), but that leaves `root.org` sitting on
// whatever IS active (your persisted org), so the chrome kept showing YOUR
// org's name/switcher beside a URL that names someone else's — the same
// "chrome contradicts the address bar" lie the member-org fix removed.
//
// DECISION (product, recorded here — flagged for Nika's review, not
// blocking): NEUTRAL chrome, not read-only owner branding and not a route
// block.
//   • Read-only owner branding would need to fetch org-level identity for an
//     org you have NO membership relationship with — new surface area, and
//     it re-introduces a privacy question (is a public project's owner-org
//     name/logo meant to be exposed to every anonymous/foreign viewer?) that
//     nothing here has answered yet.
//   • Blocking the route is wrong outright — public projects (canvas) are
//     BY DESIGN viewable by non-members; that's the whole reason
//     `mwtOrgToReconcile` returns null instead of erroring.
//   • Neutral costs nothing extra (no new fetch, no new privacy surface) and
//     removes the lie completely: nobody's org identity renders next to
//     content that isn't theirs.
//
// Detection must hold on a BARE COLD LOAD, not just an in-app nav — this
// reads `root.currentPath` (seeded synchronously from `window.location`
// in onCreate, before first paint — see the `_livePath` seed above) and
// `root.orgs` (populated async post-auth). Before `root.orgs` lands this
// returns false (unknown, not foreign) rather than guessing — the label
// briefly shows the default resolving state and self-corrects the instant
// `root.orgs` arrives, because it's a live reactive read (not a cached
// snapshot), the same "recompute from source, never mirror" contract every
// other MWT reconcile in this file already follows (see
// reconcileMwtOrgFromUrl's own orgs-not-loaded retry gate).
//
// Deliberately reuses `_urlOrgSlug` (project-canvas / /studio) AND
// `getWorkspacePrefixSlug` (/w/<org>) rather than re-deriving either — one
// slug-detection implementation, shared with the reconcile that already
// solved this exact "which shapes name an org" problem.
export const _foreignOrgUrl = (root) => {
  if (!root) return false
  const path = root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
  const slug = shellGetWorkspacePrefixSlug(path) || _urlOrgSlug(path, root)
  if (!slug) return false
  // YOUR OWN NAMESPACE IS NEVER FOREIGN — checked BEFORE the `orgs` scan
  // (2026-09-02 per Nika "orgs is failing to load and it just shows
  // 'Organization' in orgswitcher without dropdown").
  //
  // `root.orgs` lists TEAM organizations only; a user's personal namespace
  // is never a member of it. Measured live on my.symbols.app at
  // /w/nikoloza/ws_603boj34rdryhf/studio: root.orgs held 13 orgs, every one
  // type 'team' (gita, fractyco, bellforge, … system), while the URL slug and
  // root.org.slug were both 'nikoloza'. The scan below therefore found no
  // match and declared the user's OWN workspace foreign, which neutralized
  // the switcher — bare "Organization", no dropdown, on every personal-
  // namespace URL. It read as "orgs failed to load" but the orgs were
  // loaded; only the membership test was wrong.
  const own = root.org
  if (own && (own.slug === slug || own.username === slug)) return false
  if (slug && slug === root.currentUserUsername) return false
  // Orgs list not loaded yet — unknown, not foreign. Never flag on a guess.
  if (!Array.isArray(root.orgs) || !root.orgs.length) return false
  // Same rule for a list we cannot match against: a claims-minted `orgs` row
  // can arrive with NO `slug`/`username` (personal orgs are dropped at JWT
  // mint time, and a token-decoded session carries no Mongo `organizations`
  // at all). Matching a slug against rows that have none is a guaranteed
  // miss, which would neutralize the switcher for a org the user really is
  // in. Only a list with at least one matchable row can prove foreignness.
  if (!root.orgs.some((o) => o && (o.slug || o.username))) return false
  return !root.orgs.some((o) => o && (o.slug === slug || o.username === slug))
}

// Shared gate for BOTH AppOrgSwitcher mounts' demo + foreign-URL lockdown —
// see the HeaderLeft/HeaderRight override comments below for the full
// reasoning. One function so the two mounts (+ their aria-label/Label
// siblings) can never drift on which condition neutralizes the switcher.
const _neutralizeOrgSwitcher = (root) => !!root?.isDemo || _foreignOrgUrl(root)

// Returns a bundle KEY; the two mount sites below resolve it. This shell
// OVERRIDES the shared AppOrgSwitcher.Trigger's own `aria-label`, so
// translating the shared component alone changed nothing on screen (measured
// live on the natali board, 2026-09-03: the trigger still read "Switch
// organization" inside a fully Georgian session).
const _orgSwitcherAriaLabel = (root) => {
  if (root?.isDemo) return 'shell.orgSwitcher.ariaDemo'
  if (_foreignOrgUrl(root)) return 'shell.orgSwitcher.ariaForeign'
  return 'shell.orgSwitcher.switchAria'
}

// Visible Trigger label. Demo keeps showing the (single, real) demo org's
// name — that org genuinely IS the active session, nothing to hide. The
// foreign-URL case is different: `root.org` is YOUR org, not the URL's, so
// showing its name would just relocate the lie from "switchable" to
// "read-only" — swap to the shared component's own generic "no org
// resolved" fallback instead (same word it already shows itself before an
// active org resolves), never a real org name from either party.
// The generic word is the shared component's own bundle key
// (`shell.orgSwitcher.orgLabel`), so the trigger reads "ორგანიზაცია" on a
// Georgian board where it read the English word "Organization".
const _orgSwitcherLabel = (el, root) => {
  const generic = () => el.call('polyglot', 'shell.orgSwitcher.orgLabel')
  if (_foreignOrgUrl(root)) return generic()
  return getOrgSwitcherInternals().activeOrg(root)?.name || generic()
}
// Matches the dashboard canvas surfaces — /studio AND the /canvas
// duplicate (2026-06-09) — so both get the 'canvas' route-kind (no app
// header-offset, header is a full-bleed overlay) and surface the Zoombar.
const _isStudioCanvasPath = (p) => /^\/(studio|canvas)(\/|$)/.test(p || '')
// /files is a full-bleed desktop canvas too — same 'canvas' route-kind so the
// AppHeader stays a pure overlay and the desktop fills the whole viewport.
const _isFilesCanvasPath = (p) => /^\/files(\/|$)/.test(shellStripWorkspacePrefix(p || ''))
// /tickets board is a full-bleed pan/zoom canvas too (2026-06-10 per Nika
// "make tickets canvas fullscreen and header absolute, just like /studio").
// BOTH board routes — /tickets AND its /tickets/board alias map to the same
// canvas page (pages/index.js), so both must classify 'canvas'; the exact
// `=== '/tickets'` match left /tickets/board on the 'app' kind and the whole
// canvas shifted 48px below the header (2026-07-25 per Nika "content on
// /tickets page has a padding top"). Query/hash stripped for the same reason
// — a currentPath carrying ?drafts=1 must still match. Other /tickets/*
// subpages (feed, epics, …) stay 'app'.
const _isTicketsCanvasPath = (p) => {
  const path = shellStripWorkspacePrefix(p || '').split(/[?#]/)[0]
  return (
    path === '/tickets' || path === '/tickets/board' || path === '/tasks' || path === '/tasks/board'
  )
}
// /team-and-agents (+ /my-agents lens) is a pan/zoom canvas too — surfaces
// the Zoombar there (2026-06-10 per Nika "make /team canvas similar to
// /studio"); its page mirrors the panzoom into __canvasGlobalScope.
const _isTeamCanvasPath = (p) =>
  /^\/(team-and-agents|agents|my-agents)(\/|$)/.test(shellStripWorkspacePrefix(p || ''))
// /infra is the full-bleed Railway-style deployment canvas — same 'canvas'
// route-kind so the AppHeader stays a pure overlay (no 48px offset).
const _isInfraCanvasPath = (p) => shellStripWorkspacePrefix(p || '') === '/infra'
// An installed module's CANVAS page (its manifest `canvasRoutes`, e.g. the
// validators app's /validators/intel) — the same 'canvas' route-kind and the
// root Zoombar, which drives the instance the page registers
// (canvas/functions/registerCanvasPanzoom.js). Only routes the module serves.
const _isModuleCanvasPath = (p, root) =>
  shellIsModuleCanvasRoute(shellStripWorkspacePrefix(p || '').split(/[?#]/)[0], root)

// SINGLE open path for the composer's model menu — shared by ModelBtn's click
// AND its ArrowDown/ArrowUp keyboard open (2026-09-02 keyboard-navigation
// pass), so the two modalities can never drift the way a copied openDropdown
// call would. Geometry matches THE AI menu system (AiMenu.js AiMenuPanel):
// minWidth G / radiusCard / 60vh cap — the same shape as the ParamsBtn
// dropdown and the assistant's inline panels.
const _openAiModelDropdown = (el) => {
  // Refresh live per-provider diagnostics on open — cheap + best-effort (the
  // same contract as the assistant header trigger).
  try {
    el.call('fetchAiProviderDiagnostics')
  } catch (_) {}
  el.call('openDropdown', {
    id: 'aiModel',
    anchorEl: el,
    placement: 'top-end',
    minWidth: 'G',
    maxHeight: '60vh',
    round: 'radiusCard',
    // Shell paint opt-in — AiModelSwitcherBody paints no card of its own; the
    // plugin Panel is chromeless by default
    // (FW-DROPDOWN-PANEL-STALE-SHELL-PAINT-1).
    theme: 'canvas-card-glass',
    shadow: 'overlay',
    content: { extends: 'AiModelSwitcherBody', padding: 'Z' }
  })
}

// SINGLE SOURCE for "is the global AI composer visible right now" — shared by
// the CanvasPromptTextarea hide factory AND _syncShellRoute's <html
// data-prompt> mirror (the latter drives the app-page bottom-padding CSS
// rule below), so the two can never drift. `path` is the RAW (unstripped)
// currentPath — this strips it itself, matching what the hide factory always
// passed in. `el` is only used to resolve `el.context.methods.isActiveMember`
// (an el.call-style function-lookup, not a component `s`) — both call sites
// have an element handy (the factory's own `el`, _syncShellRoute's own `el`
// param), so this needed no new plumbing. `{ root }` reconstructs the shape
// isActiveMember expects (`s.root`) since neither caller holds a real
// component state object here.
//
// Two halves: `_composerAllowed` answers "may this route compose at all", and
// `_composerVisible` adds the phone dock's park — on a phone viewport with a
// workspace the composer waits behind the dock's AI button
// (functions/phoneNavigation.js phoneNavigationAssistant) until the member
// opens it. The dock asks the ALLOWED half, so the park never hides the very
// button that lifts it, and the hide factory and the <html data-prompt> mirror
// keep asking the visible half — one answer, no stylesheet override beside it.
//
// A composer that HOSTS a voice call (the in-composer call strip, root.voiceHost
// 'canvas') is never parked: the strip is the call's only surface, and parking
// it left a live microphone with nothing on screen to show or end it.
const _composerVisible = (root, path, el) =>
  _composerAllowed(root, path, el) &&
  !(
    root?._isPhoneViewport === true &&
    root.workspace &&
    !root._phoneComposerOpen &&
    !(root.voiceOpen && root.voiceHost === 'canvas')
  )

const _composerAllowed = (root, path, el) => {
  if (
    !shellFeatureEnabled(root, 'aiAssistant') ||
    !shellFeatureEnabled(root, 'aiAssistant.composer')
  )
    return false
  // Immersive module chrome (manifest `chrome.immersive`) hides the bottom
  // prompt bar along with the header — the module owns the whole viewport
  // for these members. Same gate AppHeader/presence dock use.
  if (moduleImmersiveActive(root)) return false
  // A module page that shows its OWN composer asked the shell to hide this
  // one (el.call('setShellComposer', false) — functions/moduleShellComposer.js).
  // The latch is route-bound: it reads false the moment the member leaves
  // that page or the pane that hosts it, so it can never leak to another
  // page or module. Header and every other chrome stay as they are.
  if (moduleComposerHiddenLive(root)) return false
  const p = shellStripWorkspacePrefix(
    path || (typeof window !== 'undefined' ? window.location.pathname : '/')
  )
  if (isChromelessShellPath(p) || p === '/add-app' || p === '/chat') return false
  // THE HOME'S BUILD TAB is the app builder — the /add-app page on the home
  // (functions/homeBuild.js; Nika 2026-10-23: "build tab should reuse ui/ux of
  // /add-apps"): its own conversation composer is the prompt there, as on
  // /add-app, so this one stands down while the tab shows (and the phone
  // dock's AI button opens the sidebar instead — PhoneNavigation canCompose).
  const buildShown = el?.context?.functions?.homeBuildShown
  if (typeof buildShown === 'function' && el.call('homeBuildShown', root)) return false
  // /meet-guest stays hidden: anonymous guests have no workspace AI.
  if (p === '/meet-guest') return false
  // While the assistant drawer's composer hosts the voice strip
  // (voiceHost === 'chat'), the global bottom prompt is a redundant second
  // prompt beside the very surface running the conversation — the residual
  // the voice⟷chat exclusivity wave (bee2f26c) flagged for this predicate.
  if (root?.voiceOpen && root?.voiceHost === 'chat') return false
  // AIUX-CHROME item 3 (tickets/sonnet.md, live-verified 2026-08-13) — the
  // AssistantDrawer (below, `hide: !assistantOpen || assistantDock ===
  // 'minimized'`) has its OWN "Describe a change…" composer; without this
  // check the global "What's up?" bar stayed visible underneath it too — two
  // prompt inputs on screen at once. Mirrors the drawer's own visibility
  // predicate exactly (inverse), so the two can never show together or both
  // hide together.
  if (root?.assistantOpen && root?.assistantDock !== 'minimized') return false
  // Members only — same gate the header actions + presence dock use; absent
  // gate fn (no-backend shells) keeps the composer visible.
  const isActiveFn = el?.context?.methods?.isActiveMember
  if (typeof isActiveFn === 'function' && !isActiveFn.call(el, { root })) return false
  return true
}

// Self-healing chat + notifications realtime, run opportunistically from
// _syncShellRoute (every route/state sync). Both underlying calls are
// internally guarded and workspace/user-keyed — subscribeRealtime reuses its
// existing SSE connection while `existing._wsId === activeWorkspaceId` and
// re-opens on drift (see chat/functions/subscribeRealtime.js:~310);
// subscribeNotifications no-ops once `root._notifChannel` is set — so
// calling them again here is a cheap no-op on the common path and a real
// re-subscribe exactly when either goes stale (a workspace switch racing the
// mount-time boot call in onCreate, or that boot call firing before
// `currentUserEmail`/`root.workspace` resolved). Throttled so a burst of
// route syncs (chat-drawer toggle, assistant dock drag) doesn't pay the
// guard-check cost on every tick.
// Mail realtime, flag-gated (architecture/MAIL.md §3.6). Split out because
// BOTH the mount-time boot and the route-sync self-heal need the identical
// gate + guard, and because the gate is not the same one chat's stream has:
// `navItemVisible(root, '/mail')` is the mail feature flag AND the app-access
// ACL — the predicate the header envelope itself hides on. A workspace with
// mail off never opens the connection.
//
// `subscribeMailStream` is registered by the mail EAGER slice (mail/functions/
// eagerSlice.js), so it resolves before any fold — that is the whole point,
// since the badge is a header surface on every route. `el.call` still throws
// on a shell where mail is not merged at all (a no-backend host), so the call
// stays guarded like its two siblings.
const _ensureMailRealtime = (el, root) => {
  if (!root || !shellNavItemVisible(root, '/mail')) return
  try {
    el.call?.('subscribeMailStream')
  } catch (err) {
    console.warn('[shell] subscribeMailStream ensure failed:', err?.message || err)
  }
}

// The shell's meet channel — the "Start a call" badge's feed (root.rooms,
// root.liveParticipants). Opens ONLY for a signed-in person whose workspace is
// known (functions/sessionStreams.js meetRealtimeScope, persona audit OV-2): a
// signed-out page used to open it at boot and the server answered 401 six to
// eighteen times; a signed-in boot opened it before the workspace landed
// (400). subscribeMeetRealtime is scope-keyed, so a repeat call is a no-op and
// a workspace switch re-scopes it. Returns the subscription's teardown, or
// null when nothing was opened.
const _ensureMeetRealtime = (el, root) => {
  if (!el?.call?.('meetRealtimeScope', root)) return null
  try {
    return el.call?.('subscribeMeetRealtime', { tables: ['meet_rooms', 'meet_participants'] })
  } catch (err) {
    console.warn('[shell] meet realtime ensure failed:', err?.message || err)
    return null
  }
}

let _lastRealtimeEnsureAt = 0
const _ensureShellRealtime = (el, root) => {
  if (!root?.currentUserEmail) return
  const now = Date.now()
  if (now - _lastRealtimeEnsureAt < 5000) return
  _lastRealtimeEnsureAt = now
  try {
    el.call?.('subscribeRealtime')
  } catch (err) {
    console.warn('[shell] subscribeRealtime ensure failed:', err?.message || err)
  }
  try {
    el.call?.('subscribeNotifications')
  } catch (err) {
    console.warn('[shell] subscribeNotifications ensure failed:', err?.message || err)
  }
  _ensureMailRealtime(el, root)
  _ensureMeetRealtime(el, root)
  // Due-time reminder notifications — the sweep throttles itself (~1/min)
  // and no-ops when nothing is due, so riding this ensure keeps reminders
  // firing on ANY route while the app is open (2026-08-01 per Nika:
  // reminders should send notifications).
  try {
    el.call?.('reminderDueSweep')
  } catch (err) {
    console.warn('[shell] reminderDueSweep ensure failed:', err?.message || err)
  }
}

// THE SKIN — <html data-skin="ambient|solid"> beside data-shell-route
// (DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1, HOME_AMBIENT_SKIN_SPEC
// §1). The rule — the workspace's own `settings.appearance.skin`, else solid
// on a white-label host or a workspace with its own ground, else ambient —
// lives in functions/workspaceSkin.js, shared with the admin card on
// /admin/workspace-settings so the two never disagree. While the record is
// on its way the rule answers null and the stamp stays as it is: no
// attribute is today's look (solid), so a cold boot never flashes a guess.
const _syncShellSkin = (root) => {
  if (typeof document === 'undefined') return
  const r = untrackedState(root)
  const { skin } = resolveWorkspaceSkin(r, {
    info: workspaceSettingsOf(r),
    brandedHost: isCustomDomainHost()
  })
  if (!skin) return
  const html = document.documentElement
  if (html.getAttribute('data-skin') === skin) return
  html.setAttribute('data-skin', skin)
  // Keep this scope's warm-boot row in step, so the NEXT warm boot stamps the
  // skin before its first paint (index.js → functions/bootSnapshot.js).
  recordBootSnapshotSkin({ userId: r?.currentUserId, workspaceId: r?.workspace?.id, skin })
}

// Route-kind synced to a <html data-shell-route> attribute so the injected
// content CSS can offset the right pages below the absolute AppHeader:
//   • 'app'    → tickets/chat/calendar/etc. flow BELOW the 48px header
//                (their own top toolbars otherwise collide with it).
//   • 'home'   → self-offsets via MainColumn padding — left untouched.
//   • 'canvas' → /studio, /canvas + project canvases keep the header as a
//                pure full-bleed overlay (no offset, no gap).
//   • 'auth'   → header is hidden anyway.
// (2026-06-09 per Nika "fix layout for all similar pages" — /tickets.)
// Tracks assistantOpen across _syncShellRoute runs so the reveal-on-open
// promotion below fires only on the false→true edge (an opener), never on an
// explicit minimize (which keeps assistantOpen true).
let _prevAssistantOpen = false
const _syncShellRoute = (root, el) => {
  if (typeof document === 'undefined') return
  root = untrackedState(root)
  // Normalize past any `/w/<org-slug>` prefix so the intranet home (`p === '/'`)
  // + every canvas-kind check below classify correctly under multi-workspace
  // tabs (§6d). No-op until Phase 3 emits the prefix.
  const p = shellStripWorkspacePrefix(root?.currentPath || window.location.pathname || '/')
  let kind = 'app'
  if (isChromelessShellPath(p)) kind = 'auth'
  else if (p === '/') kind = 'home'
  // /team-and-agents joined the canvas kind 2026-06-12 (per Nika "adjust
  // canvas size as on /files — full size") — it was 'app', so the shell
  // padded it 48px below the header instead of full-bleed under it.
  else if (
    _isStudioCanvasPath(p) ||
    _isFilesCanvasPath(p) ||
    _isTicketsCanvasPath(p) ||
    _isTeamCanvasPath(p) ||
    _isInfraCanvasPath(p) ||
    _isProjectCanvasPath(p, root) ||
    _isModuleCanvasPath(p, root)
  )
    kind = 'canvas'
  document.documentElement.setAttribute('data-shell-route', kind)
  // A module canvas page keeps its own page head (title, tabs) in flow: the
  // marker lets the canvas-kind rule below clear the floating header for it.
  document.documentElement.setAttribute(
    'data-module-canvas',
    kind === 'canvas' && _isModuleCanvasPath(p, root) ? 'true' : 'false'
  )
  // Messenger (chat drawer) open → offset the left page rails (CSS above) so
  // they clear the floating chat list. (2026-06-18 per Nika.) The docked
  // MailOverlay (mail drawer, 2026-09-05) floats in the SAME left band with
  // the same geometry, so it drives the same attribute — one styled hook for
  // "a left docked drawer is open", not a second attribute nothing styles.
  document.documentElement.setAttribute(
    'data-chat-drawer',
    root?._chatDrawerOpen || root?._mailDrawerOpen ? 'open' : 'closed'
  )
  // Where the navbar sits (shared functions/navbarLayout.js: the person's own
  // look, 'top' by default and always on a phone) → <html data-nav-place>,
  // the hook of the injected sheet's "THE NAVBAR'S PLACE" rules: the header
  // turns into a rail or a bottom bar and the frame clears it. ShellRouteSync
  // listens to the look keys.
  document.documentElement.setAttribute('data-nav-place', _navPlace(el))
  // Composer visibility mirror — feeds the app-page bottom-padding CSS rule
  // below (`html[data-shell-route="app"][data-prompt="shown"] …`). Shares
  // _composerVisible with CanvasPromptTextarea's own `hide` factory so the
  // padding gate can never drift from where the composer actually renders,
  // and — since it's gated on this attribute — also self-clears once the
  // composer hides even though _trackClearVar (below) deliberately RETAINS
  // the last live --prompt-clear measurement (a ResizeObserver has nothing to
  // observe once its node unmounts, so the var would otherwise keep the old
  // reading forever).
  // A module's "hide the shell composer" latch whose page is gone (route
  // change, pane closed) is cleared here, so returning to that page shows the
  // composer until the module asks again (functions/moduleShellComposer.js).
  // ShellRouteSync listens to currentPath and both pane keys — every way a
  // latch can go stale.
  releaseStaleModuleComposerLatch(root)
  document.documentElement.setAttribute(
    'data-prompt',
    _composerVisible(root, root?.currentPath, el) ? 'shown' : 'hidden'
  )
  // A PageSlot window is open (either pane) → <html data-slot="open">. The
  // injected sheet reads it at phone width, where the window is an iOS sheet
  // that COVERS the composer (PageSlot's phone rule); see the rule below.
  document.documentElement.setAttribute(
    'data-slot',
    root?._canvasSlot || root?._canvasSlotSecond ? 'open' : 'closed'
  )
  // Dockable AI assistant → reflect the effective dock onto <html
  // data-assistant-dock> so the injected shell CSS can (a) reserve a grid
  // column on [data-key="smblsapp"] to SHRINK the content instead of
  // overlaying it, (b) place the fixed drawer on the correct edge, and (c)
  // inset the viewport-fixed chrome (AppHeader / PageSlot / composer). Driven
  // here — NOT via reactive layout-prop factories on the drawer — because
  // width/left/right/grid factories freeze at mount in v3.14 (mirrors the
  // data-shell-route + data-chat-drawer mechanism above). Effective value:
  //   closed     → assistant not open (content full width, drawer hidden)
  //   right/left → docked, content shrinks
  //   minimized  → open but collapsed to the SimoneReasoningPill
  // Reveal-on-open: any opener that flips assistantOpen false→true while the dock
  // is 'minimized' should reveal the drawer, not open into the invisible
  // minimized chip. The star + pill promote themselves; this covers EVERY OTHER
  // opener (calendar Ask AI, ticket mention, deep-links, DailyUpdate, …) centrally
  // so none of them needs to know about the dock. Session-only (root.update, NOT
  // setAssistantDock) so it never overwrites the user's persisted 'minimized'.
  // Guarded on the false→true edge so it never fires on an explicit minimize
  // (which keeps assistantOpen true); the re-entrant run sees _prevAssistantOpen
  // already true and the now-'right' dock, so it can't recurse or double-fire.
  //
  // Page mode ('main') opens the MAIN THREAD over the current page instead
  // (AI_ASSISTANT_SPEC §12.2 — the drawer stays shut); with a page sheet open
  // in the PageSlot it promotes to the LAST side like before, and remembers it
  // came from Page so the close edge returns there — an opener asks for the
  // conversation, never for a new mode. The rules and the stamp live in
  // functions/assistantDock.js.
  const _openNow = !!root?.assistantOpen
  const _justOpened = _openNow && !_prevAssistantOpen
  const _justClosed = !_openNow && _prevAssistantOpen
  _prevAssistantOpen = _openNow
  if (_justOpened && root.update) {
    // The conversation an opener shows: with an empty chat in Fly, the Fly
    // session's thread (functions/assistantMainThread.js assistantOpenThread).
    // Read BEFORE the placement moves, so the view is the one opened from.
    if (el && typeof el.call === 'function') el.call('assistantOpenThread', root)
    // An opener inside a DIALOG (a ticket or event detail "Ask AI") sits over
    // the main area — the thread would open UNDER it — so it docks the sidebar
    // for the session instead, like a page sheet (design-d2's review, D3). The
    // dialog is read the way the shell keyboard guard reads an overlay: the
    // shell's overlay reader, or focus inside a dialog carrier.
    let _overlay = false
    let _opener = null
    try {
      const _layer = el && typeof el.call === 'function' ? el.call('shellOverlayOpen') : null
      _opener = typeof document !== 'undefined' ? document.activeElement : null
      const _inDialog = !!(
        _opener &&
        typeof _opener.closest === 'function' &&
        _opener.closest('[role="dialog"],[role="alertdialog"],[aria-modal="true"]')
      )
      _overlay = _layer === 'modalRoot' || _inDialog
    } catch (_) {
      _overlay = false
    }
    const promote = promoteAssistantDockForOpen(root, lastAssistantSide(), { overlay: _overlay })
    if (promote) root.update(promote)
    // Focus returns to the OPENER when Esc closes the thread it opened
    // (functions/assistantMainThread.js assistantMainThreadClose).
    const _app = el && typeof el.getRoot === 'function' ? el.getRoot() : null
    if (_app && promote && promote._homeThreadOpen === true) _app.__mainThreadOpener = _opener
  } else if (_justClosed && root.update) {
    const restore = restoreAssistantDockOnClose(root)
    if (restore) root.update(restore)
  }
  const _dockStamp = assistantDockStamp(root)
  document.documentElement.setAttribute('data-assistant-dock', _dockStamp)
  // Publish the sidebar width used by BOTH the reserved grid column AND the
  // drawer's own width so they stay in lockstep (a raw var() on `width`
  // compiles to an empty DOMQL atomic rule, so the drawer width lives in the
  // stylesheet keyed on the attribute — see the injected CSS). Clamp to the
  // same 320..50vw bounds the ResizeHandle drag enforces so the reserved
  // column can never diverge from the rendered drawer.
  if (typeof window !== 'undefined') {
    const _w = Number(root?.assistantWidth)
    const _half = Math.floor(window.innerWidth * 0.5)
    const _px = Math.max(320, Math.min(_half, Number.isFinite(_w) && _w > 0 ? _w : 640))
    document.documentElement.style.setProperty('--assistant-width', _px + 'px')
    // Per-side INSET vars for viewport-fixed chrome whose geometry lands
    // INLINE (SHELL-ASSISTANT-RESERVE-1, PageSlot half): a conditional
    // `.case` block compiles to inline style, so PageSlot's expanded /
    // fullscreen states (`right: 0`, `left: rail-clear`) outrank the
    // attribute-keyed `[data-key="PageSlot"]` rules below and the slot slid
    // UNDER the docked drawer. The slot's case blocks read these vars
    // instead: 0px whenever nothing is docked on that side, the same clamped
    // reserve as the grid column when the drawer is open on it — one number
    // for the column, the drawer and every inset, so none can diverge.
    // The stamp names the open drawer's edge ('right' | 'left') and nothing
    // else docked — Page mode ('main') and Minimized reserve no side.
    const _reserve = 'min(var(--assistant-width),50vw)'
    document.documentElement.style.setProperty(
      '--assistant-inset-right',
      _dockStamp === 'right' ? _reserve : '0px'
    )
    document.documentElement.style.setProperty(
      '--assistant-inset-left',
      _dockStamp === 'left' ? _reserve : '0px'
    )
  }
  // Publish the messenger sidebar's real rendered right edge so the PageSlot can
  // dock one gutter beside it (consumed by the `[data-key="PageSlot"]` rule in
  // shellApp's injected CSS). The sidebar inherits a smaller font-size than the
  // shell root, so its em-based `width:G1` can't be reconstructed with
  // root-context tokens — measure it instead. computedLeft+computedWidth is
  // transform-independent, so it's correct while the sidebar is mid-slide AND
  // while it's parked off-screen when closed (MessengerList stays mounted, just
  // translated away). Scoped to the root element's node via el.getRoot()
  // (mirrors the chat overlay's own lookup) rather than a global document query.
  // The mail drawer (MailOverlay → MailDrawer) shares the messenger's left
  // band and geometry; measure whichever of the two is laid out. A hidden
  // (display:none) panel computes a non-numeric width, so it is skipped.
  const rootNode = el?.getRoot?.()?.node
  const dockedNodes = rootNode?.querySelectorAll?.(
    '[data-key="MessengerList"], [data-key="MailDrawer"]'
  )
  for (const dockedNode of dockedNodes || []) {
    const mcs = window.getComputedStyle(dockedNode)
    const right = parseFloat(mcs.left) + parseFloat(mcs.width)
    if (right > 0) {
      document.documentElement.style.setProperty('--chat-sidebar-right', `${right}px`)
      break
    }
  }
  // BACKSTOP ONLY — this block does NOT run on a View-as click.
  // `ShellRouteSync` (the invisible component whose onUpdate calls
  // `_syncShellRoute`) is `subscribeTo`-gated to exactly
  // `['currentPath', '_chatDrawerOpen', 'assistantOpen', 'assistantDock',
  // 'assistantWidth']` — a persona's `bfPersonaOverride`/`bfView` write and
  // `moduleOpenPage`'s own `_canvasSlot` write touch NEITHER, so this never
  // fires on the actual gesture (Chrome-MCP-confirmed live, 2026-08-14:
  // `?page=` updated every switch, `?viewAs=` never once across ten — this
  // code looked plausible and was inert). `subscribeTo`'s key list is also
  // resolved ONCE at element-create time (smbls `registerSubscribeToEffects`),
  // before any module — and its `chrome.viewAsKey` — has loaded, so a
  // dynamic function-form `subscribeTo` can't fix this either: it would
  // still freeze on the pre-module-load empty key.
  //
  // The PRIMARY, deterministic `?viewAs=` sync now lives in
  // `functions/moduleOpenBridge.js` (`_syncViewAsUrl`, called imperatively
  // from `moduleOpenPage` — the one bridge every View-as switch and every
  // other module-page-open funnels through). This block is left as a
  // same-shape, harmless backstop: it re-applies whenever `ShellRouteSync`
  // DOES fire for its own listed reasons (route/chat-drawer/assistant
  // changes), so the URL can't drift stale across those either. Idempotent
  // (no-op-diff guarded) — safe to run twice. Generic: keyed off
  // `chrome.viewAsKey`, never a module-specific name — see
  // `_restoreViewAsParam` (workspaceProjects.js) for the read/restore half
  // (RAIL-TRANSITION, tickets/fable.md).
  const _viewAsKey = moduleViewAsKey(root)
  if (_viewAsKey && typeof window !== 'undefined') {
    try {
      const url = new URL(window.location.href)
      const cur = url.searchParams.get('viewAs')
      const val = root?.[_viewAsKey]
      const next = typeof val === 'string' && val && val !== 'admin' ? val : null
      if (next !== cur) {
        if (next) url.searchParams.set('viewAs', next)
        else url.searchParams.delete('viewAs')
        const nextUrl = url.pathname + url.search + url.hash
        const current = window.location.pathname + window.location.search + window.location.hash
        if (nextUrl !== current) window.history.replaceState(window.history.state, '', nextUrl)
      }
    } catch (err) {
      console.warn('[shell] viewAs url sync failed:', err?.message || err)
    }
  }
  _ensureShellRealtime(el, root)
  // The shell call panel's voice session (functions/calls/workspaceCall.js) —
  // idempotent and workspace-keyed, so a route sync is a no-op on the common
  // path, re-scopes after a workspace switch and stops after a sign-out. NOT
  // inside _ensureShellRealtime: that one is gated on a signed-in user, and the
  // sign-out half needs this call too.
  try {
    el.call?.('ensureWorkspaceVoice')
  } catch (err) {
    console.warn('[shell] ensureWorkspaceVoice failed:', err?.message || err)
  }
}

// DEMO LOCKDOWN — routes a demo session may NOT visit. These are the
// admin / cross-org / other-org-people / internal-features surfaces; a demo
// visitor is locked to their single demo org, so any of these is redirected
// home. The regex lives in functions/demoRouteGuard.js — the SAME source also
// powers the smbls router guard (context.routerOptions.guards) that blocks
// these routes pre-mount on every SPA navigation; this sentinel is the
// fallback for isDemo resolving true while already ON a blocked route.

// Redirect an isDemo session off a blocked route. No-op for real users
// (isDemo false) and until auth has resolved (so we don't bounce on the
// pre-auth '/' default). el.router keeps it a clean SPA redirect.
//
// The redirect is DEFERRED to a microtask: this guard fires from the
// `currentPath` signal flush, which the router runs SYNCHRONOUSLY in the
// middle of mounting the blocked route's content. Calling el.router
// re-entrantly from inside that flush let the inner '/' navigation finish
// and then the OUTER (blocked-route) set continue — appending the blocked
// page as a SECOND `content` child at the shell root: the grid split into
// two ~half-viewport rows (home on top, /admin below) and the dead page's
// helmet re-wrote the tab title. A microtask runs after the router's
// synchronous render completes but BEFORE paint, so the bounce is a normal
// non-re-entrant navigation and the blocked page never flashes.
const _guardDemoRoute = (el, s) => {
  const root = s.root
  if (!root || !root.isDemo) return
  const path = root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
  if (DEMO_BLOCKED_ROUTE_RE.test(path)) {
    queueMicrotask(() => {
      // Re-check — another navigation may have landed while queued.
      const now =
        root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
      if (!DEMO_BLOCKED_ROUTE_RE.test(now)) return
      try {
        el.router(_withLiveQuery('/'), el.getRoot())
      } catch (err) {
        console.warn('[shell] demo route guard redirect failed:', err?.message || err)
      }
    })
  }
}

// MainLoader covers the viewport while ANY "block the UI" flag is set:
//   • `loading`        — generic `applyLoader({value:true})` channel
//   • `_wsSwitching`   — org switch in flight (federated or otherwise)
//   • `_wpSwitching`   — workspace-project federation switch in flight
//   • `_loginLoading`  — auth submit / token round-trip
//   • `_langOwed`      — a sign-in ON this page has not applied the session's
//     interface language yet (functions/shellLangPolicy.js). It holds only a
//     signed-in shell whose workspace can still decide the language: with no
//     workspace, or a record that will never complete, nothing is coming.
// `s.loading` is the canonical global loader signal — pages that need
// a feature-scoped indicator (e.g. studios' node hydration) MUST use
// their own flag (`_labLoading`), never poach `s.loading`.
const _isLoaderActive = (s) =>
  !!(
    s.loading ||
    s._wsSwitching ||
    s._wpSwitching ||
    s._loginLoading ||
    s._dataLoading ||
    (s._langOwed === true &&
      s.signedIn === true &&
      !!s.workspace &&
      !!(s.workspace.id || s.workspace._id) &&
      !s.workspace._completionUnreachable)
  )

// Boot-timeline mark — the same performance.mark channel loadPrivateData's
// `wsboot:*` marks use, so one getEntriesByType('mark') read shows the data
// passes AND the loader edges side by side. Never throws (no Performance
// API in tests / SSR).
const _bootMark = (name) => {
  try {
    performance.mark(name)
  } catch (_) {
    /* noop */
  }
}

// Native startup owns the first visual. The shell still owns truthful status
// and the stable-ready gate while its own visual stays hidden underneath it.
// The status is a PHASE, never copy: the pre-bundle overlay (index.html)
// carries its own lines because no bundle can load before it paints, and the
// runtime loader renders translated copy from the i18n bundles.
const _loaderStatus = (s) => {
  // Stages describe completed prerequisites, not an estimated percentage.
  const step = s._loginLoading || !s._authReady ? 1 : 2
  if (s._wsSwitching || s._wpSwitching) return { phase: 'switching', step }
  if (s._loginLoading || !s._authReady) return { phase: 'session', step }
  if (s._wsAppsActivating && _isLoaderActive(s)) return { phase: 'apps', step }
  if (s._dataLoading) return { phase: 'workspace', step }
  if (s.loading) return { phase: 'loading', step }
  // Only the language an in-page sign-in owes holds it now: the last of the
  // workspace's own prerequisites, so the fill never steps back a stage.
  if (_isLoaderActive(s)) return { phase: 'workspace', step }
  return null
}

// The loader's fill — the mountain range, and the rising mark — moves to REAL
// milestones only (DESIGN-WL-BOOT-LOADER-MOTION-1): 1 while the session is
// checked, 2 once it is (the workspace and apps load), 3 when the loader is
// off (ready). MainLoader stamps it as data-stage; the injected sheet below
// turns each stage into a creep that stops short of the next milestone, the
// same numbers as the pre-bundle overlay in index.html.
const _loaderFillStage = (s) => {
  if (!_isLoaderActive(s)) return 3
  return _loaderStatus(s)?.step === 2 ? 2 : 1
}

// NO JUMP AT A STAGE CHANGE (Nika 2026-10-08 "fix loader animation"). Each
// stage's keyframes used to start at a FIXED point: a workspace switch (raised
// straight at stage 2) popped the range in a third full, and every later
// stage jumped to its own start. The fill is tracked here on the timing the
// sheet runs (target, duration, cubic-bezier per stage — the overlay's own
// numbers in index.html), and a stage starts from the CURRENT position,
// handed to the keyframes as --ws-loader-from (1 empty, 0 full). A fresh
// raise starts empty. Pure bookkeeping on the element: no DOM read.
const _LOADER_MOTION = {
  1: [0.7, 12000, [0.1, 0.7, 0.2, 1]],
  2: [0.34, 24000, [0.1, 0.7, 0.2, 1]],
  3: [0, 480, [0, 0, 0.58, 1]]
}
const _loaderClock = () =>
  typeof performance !== 'undefined' && performance && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
const _loaderBezier = (c, x) => {
  let lo = 0
  let hi = 1
  let t = x
  for (let i = 0; i < 30; i++) {
    t = (lo + hi) / 2
    const xt = 3 * (1 - t) * (1 - t) * t * c[0] + 3 * (1 - t) * t * t * c[2] + t * t * t
    if (xt < x) lo = t
    else hi = t
  }
  return 3 * (1 - t) * (1 - t) * t * c[1] + 3 * (1 - t) * t * t * c[3] + t * t * t
}
const _loaderFillNow = (m) => {
  const spec = _LOADER_MOTION[m.stage]
  const x = Math.min(1, Math.max(0, (_loaderClock() - m.at) / spec[1]))
  return Math.min(1, Math.max(0, m.from + (spec[0] - m.from) * _loaderBezier(spec[2], x)))
}
const _trackLoaderMotion = (el, stage, shown) => {
  const m = el._loaderMotion
  if (!shown) {
    el._loaderMotion = { stage: 3, from: 0, at: _loaderClock(), shown: false }
    return
  }
  if (!m || !m.shown) {
    el._loaderMotion = { stage, from: 1, at: _loaderClock(), shown: true }
    return
  }
  if (m.stage !== stage && _LOADER_MOTION[stage]) {
    el._loaderMotion = { stage, from: _loaderFillNow(m), at: _loaderClock(), shown: true }
  }
}
const _loaderFrom = (el) => {
  const m = el._loaderMotion
  return m && m.shown ? String(Math.round(m.from * 10000) / 10000) : '1'
}

// The resolved session language, handed to the PRE-BUNDLE boot overlay
// (index.html) — WS-BOOT-LOADER-PAINTS-ENGLISH-ONLY-BEFORE-THE-BUNDLE-1.
//
// That overlay carries its own copy, because it paints before any bundle and
// so before any i18n catalog. It resolves a language for its FIRST frame from
// signals that bind to the arrival (a `?lang=` hint, the per-workspace boot
// cache, the tenant brand). A COLD arrival has none of those, so it paints
// English and this is what corrects it: the shell publishes the language the
// moment it resolves, and the overlay retranslates the line on screen, the
// recovery notice, its accessible name and the Reload label.
//
// It also records the answer for the NEXT load's first frame. That write is
// per host AND per workspace scope (functions/bootLang.js carries the law it
// obeys), never account-wide: a workspace renders in the language IT is set
// to, and a scope with no entry reads English rather than inheriting another
// workspace's language.
//
// The shipped set is INLINED, not read off `_wsShared`, for the same reason
// every helper around it is self-contained: `__tests__/bootLoaderHandoff.
// test.js` evals this whole region in a bare VM with no module bindings, so a
// delegate here would throw on every render it tests. Drift is pinned instead
// — `__tests__/bootLoaderLanguage.test.js` holds this list against
// `PLATFORM_LANGUAGES`, so a third language reds the suite.
const _publishBootLang = (lang) => {
  const SHIPPED = ['en', 'ka']
  if (typeof window === 'undefined' || SHIPPED.indexOf(lang) === -1) return
  if (window.__smblsBootLangPublished === lang) return
  window.__smblsBootLangPublished = lang
  writeBootLangHint(lang, window)
  // Only when the overlay's own guess differs: an arrival that already
  // painted the right language needs no event and no re-render.
  if (window.__smblsBootLang === lang) return
  window.__smblsBootLang = lang
  try {
    window.dispatchEvent(new CustomEvent('smbls:boot-lang', { detail: { lang } }))
  } catch (_) {
    /* The cached value also serves a pre-bundle consumer. */
  }
}

const _publishBootStatus = (status) => {
  if (!status || typeof window === 'undefined') return
  // The label never steps back within one boot: apps and the rest of the
  // workspace data share step two, and _dataLoading can outlive the apps pass,
  // so "Opening apps" used to fall back to "Loading workspace" (peer sweep,
  // deploy 11: 7.5 s workspace, 10.2 s apps, 11.0 s workspace). A switch, a
  // session reset, ready or an error starts a new sequence.
  const last = window.__smblsBootStatus
  if (
    last?.phase === 'apps' &&
    status.phase === 'workspace' &&
    status.step === last.step &&
    !status.message
  )
    return
  if (
    window.__smblsBootStatus?.phase === status.phase &&
    window.__smblsBootStatus?.step === status.step
  )
    return
  window.__smblsBootStatus = status
  try {
    if (typeof performance !== 'undefined' && performance?.mark) {
      const name = 'wsboot:loader:' + status.phase
      // Keep one mark per phase, including across repeated workspace switches.
      performance.clearMarks?.(name)
      performance.mark(name)
    }
  } catch (_) {
    /* Diagnostics cannot block the loader. */
  }
  try {
    window.dispatchEvent(new CustomEvent('smbls:boot-status', { detail: status }))
  } catch (_) {
    /* The cached status also serves pre-bundle consumers. */
  }
}

const _bootLoaderOwnerAlive = (el, owner) => {
  if (!owner || el.__destroyed || el.__ref === null || el._bootReadyOwner !== owner) return false
  const rootEl = el.getRoot?.() || owner.root.__element
  const liveRoot = el.state?.root || el.state
  const boundRoot = rootEl?.state?.root || rootEl?.state
  return (
    rootEl === owner.rootEl &&
    !rootEl?.__destroyed &&
    rootEl?.__ref !== null &&
    (!liveRoot || liveRoot === owner.root) &&
    (!boundRoot || boundRoot === owner.root) &&
    (!owner.root.__element || owner.root.__element === rootEl) &&
    (!owner.node || el.node === owner.node) &&
    (typeof window === 'undefined' || window.__smblsBootOwner === owner.token)
  )
}

const _cancelBootReady = (el) => {
  if (el._bootReadyTimer != null) clearTimeout(el._bootReadyTimer)
  el._bootReadyTimer = null
  _dropReadyHold(el)
}

// THE READY HOLD (Nika 2026-10-01 "why loading mountains dont fully reveal?").
// The runtime loader used to leave (display none) in the same render that set
// its stage 3, so on a workspace switch or a sign-in its ready fill never
// showed. At the ready edge it now stays on screen for the fill with
// pointer-events none (the page takes input at once) and leaves on the front's
// own animationend (wsLoaderFrontReady) or a 700 ms cap. Reduced motion leaves
// at once. A new load during the hold drops it; the owner guard drops it with
// the loader. `_loaderReadyHold` on the root is only the re-render signal.
const _READY_HOLD_CAP_MS = 700
const _loaderMotionReduced = (root) =>
  !!root?.a11yMotion ||
  (typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !!window.matchMedia('(prefers-reduced-motion: reduce)').matches)
const _dropReadyHold = (el) => {
  if (el._readyHoldTimer != null) clearTimeout(el._readyHoldTimer)
  el._readyHoldTimer = null
  el._readyHold = false
}
const _endReadyHold = (el, root) => {
  if (!el._readyHold) return
  _dropReadyHold(el)
  try {
    root?.update?.({ _loaderReadyHold: (root._loaderReadyHold || 0) + 1 }, { preventFetch: true })
  } catch (_) {
    /* the next root update re-reads the props */
  }
}
// True while the loader holds for its fill, or in the render that crosses the
// ready edge before display (which runs the sync) has started the hold.
const _loaderHolding = (el, s) => {
  const root = s.root || s
  void root._loaderReadyHold
  if (el._readyHold) return true
  return el._loaderMarked === true && !_isLoaderActive(root) && !_loaderMotionReduced(root)
}

// BOOT LOADER MAXIMUM (work.astio.ai 2026-10-08: signed in, the overlay sat on
// "Switching workspace" for ever — a loader flag raised and never lowered,
// after the boot gate had answered, so the gate's own deadline never saw it).
// A loader still up _LOADER_MAX_MS into a boot that is not ready publishes the
// error phase, its reason naming the stuck flags: the pre-bundle overlay then
// shows its error line, the Reload button and the step. Nothing is forced
// down — a flag nobody lowers is a defect to see, not to hide.
const _LOADER_MAX_MS = 45000
const _LOADER_FLAGS = [
  'loading',
  '_wsSwitching',
  '_wpSwitching',
  '_loginLoading',
  '_dataLoading',
  '_langOwed'
]
const _clearLoaderMax = (el) => {
  if (el._loaderMaxTimer != null) clearTimeout(el._loaderMaxTimer)
  el._loaderMaxTimer = null
  el._loaderStuck = false
}
const _armLoaderMax = (el, owner) => {
  if (el._loaderMaxTimer != null || el._loaderStuck) return
  const timer = setTimeout(() => {
    if (el._loaderMaxTimer !== timer) return
    el._loaderMaxTimer = null
    if (!_bootLoaderOwnerAlive(el, owner)) return
    if (typeof window !== 'undefined' && window.__smblsBootReady) return
    const r = owner.root
    if (!_isLoaderActive(r)) return
    el._loaderStuck = true
    _bootMark('wsboot:loader:stuck')
    _publishBootStatus({
      phase: 'error',
      step: _loaderStatus(r)?.step || 1,
      reason: 'loader_stuck:' + _LOADER_FLAGS.filter((k) => r[k]).join('+')
    })
  }, _LOADER_MAX_MS)
  el._loaderMaxTimer = timer
}

const _syncBootLoader = (el, s) => {
  const root = s.root || s
  const rootEl = el.getRoot?.() || root.__element
  // Validate the binding before claiming global ownership. A late read from
  // a dead tree must not invalidate the healthy replacement's timer.
  const boundRoot = rootEl?.state?.root || rootEl?.state
  const liveRoot = el.state?.root || el.state
  if (
    el.__destroyed ||
    el.__ref === null ||
    rootEl?.__destroyed ||
    rootEl?.__ref === null ||
    (boundRoot && boundRoot !== root) ||
    (liveRoot && liveRoot !== root) ||
    (root.__element && root.__element !== rootEl)
  ) {
    _cancelBootReady(el)
    return false
  }
  let owner = el._bootReadyOwner
  if (!owner || owner.root !== root || owner.rootEl !== rootEl || owner.node !== el.node) {
    _cancelBootReady(el)
    owner = el._bootReadyOwner = { root, rootEl, node: el.node, token: {} }
    el._loaderSeenActive = false
    if (typeof window !== 'undefined') window.__smblsBootOwner = owner.token
  }
  if (!_bootLoaderOwnerAlive(el, owner)) {
    _cancelBootReady(el)
    return false
  }
  const active = _isLoaderActive(root)
  const wasActive = el._loaderMarked === true
  const nativeVisible =
    typeof window !== 'undefined' && !window.__smblsBootReady && window.__smblsBootOverlay === true
  if (active) _dropReadyHold(el)
  else if (wasActive && el._loaderSeenActive && !nativeVisible && !_loaderMotionReduced(root)) {
    _dropReadyHold(el)
    el._readyHold = true
    const holdTimer = setTimeout(() => {
      if (el._readyHoldTimer !== holdTimer) return
      el._readyHoldTimer = null
      if (!_bootLoaderOwnerAlive(el, owner)) {
        _dropReadyHold(el)
        return
      }
      _endReadyHold(el, owner.root)
    }, _READY_HOLD_CAP_MS)
    el._readyHoldTimer = holdTimer
  }
  // Boot timeline marks (read with performance.getEntriesByType('mark')):
  // one per EDGE, so a boot can be audited after the fact — which flag
  // armed the loader, when it fell, and whether it re-armed after the
  // spiral handoff (2026-09-02 per Nika "spiral loader loads twice").
  if (el._loaderMarked !== active) {
    el._loaderMarked = active
    _bootMark(
      active
        ? 'wsboot:loader:on:' +
            [
              'loading',
              '_wsSwitching',
              '_wpSwitching',
              '_loginLoading',
              '_dataLoading',
              '_langOwed'
            ]
              .filter((k) => root[k])
              .join('+')
        : 'wsboot:loader:off'
    )
  }
  if (active) el._loaderSeenActive = true
  _trackLoaderMotion(el, _loaderFillStage(root), active || el._readyHold === true)
  // Before the status, and before the stuck-error return below: the overlay's
  // own copy is useless to a Georgian workspace until it knows the language.
  _publishBootLang(root.lang)
  const bootReady = typeof window !== 'undefined' && !!window.__smblsBootReady
  if (active && !bootReady) _armLoaderMax(el, owner)
  else _clearLoaderMax(el)
  // the stuck error stays on screen until the loader comes down
  if (el._loaderStuck) return active
  _publishBootStatus(_loaderStatus(root))
  if (active || !root._authReady) _cancelBootReady(el)
  else if (typeof window !== 'undefined' && window.__smblsBootReady) {
    _publishBootStatus({ phase: 'ready', step: 3 })
  } else if (
    typeof window !== 'undefined' &&
    !window.__smblsBootReady &&
    (el._loaderSeenActive || root._authReady) &&
    el._bootReadyTimer == null
  ) {
    const timer = setTimeout(() => {
      // A cancelled callback may already be queued. It cannot clear a newer timer.
      if (el._bootReadyTimer !== timer) return
      el._bootReadyTimer = null
      if (!_bootLoaderOwnerAlive(el, owner) || window.__smblsBootReady) return
      if (_isLoaderActive(owner.root) || !owner.root._authReady) return
      window.__smblsBootReady = true
      if (typeof performance !== 'undefined' && performance?.now)
        window.__smblsBootReadyAt = performance.now()
      _bootMark('wsboot:boot-ready')
      _publishBootStatus({ phase: 'ready', step: 3 })
      try {
        window.dispatchEvent(new Event('smbls:boot-ready'))
      } catch (_) {
        /* noop */
      }
    }, 400)
    el._bootReadyTimer = timer
  }
  return active
}

// The shell root's base styles come from packages/shared/components/
// AppShell.js `appShellStyles` — the single source of truth — resolved BY
// KEY through the DOMQL extends chain, deliberately not by module-scope
// spread and not by per-key factories:
//   • NOT `...appShellStyles` — that spread evaluates at module scope and
//     crashed the PRODUCTION BUILD ("Cannot destructure property
//     'appShellStyles' of '...default.components' as it is undefined",
//     smbls push, tickets/execution-board.md §1): workspace-shell's own
//     identity-index.js → pages/admin/* cycle can leave `_wsShared`
//     mid-construction when Parcel runs this module's top level.
//   • NOT per-key factories (`gridTemplateColumns: () => getAppShellStyles()
//     .gridTemplateColumns`, the 2026-08-13 f85b5f557 shape) — a factory-
//     valued CSS prop is applied INLINE by the framework, and an inline
//     `grid-template-columns: 1fr` outranks the attribute-keyed stylesheet
//     rule `html[data-assistant-dock] [data-key="smblsapp"]{grid-template-
//     columns:1fr min(var(--assistant-width),50vw)}` (injected below) that
//     reserves the AI-drawer column. From that commit on the drawer only
//     OVERLAID the content — SHELL-ASSISTANT-RESERVE-1 (Nika 2026-08-16:
//     "opening ai sidebar does not shift content on / page anymore").
//     Same for `transition` (inline `margin-right 200ms` beat the injected
//     `grid-template-columns .22s` shrink).
// `extends: ['appShellStyles']` is read from `context.components` at CREATE
// time (the shared library registers it under exactly that key —
// mergeSharedLibraries copies every components key), long after every
// module finished loading, and merges the values as STATIC props → atomic
// classes, which the attribute rules outrank again. `marginRight` stays the
// reactive factory it always was inside appShellStyles. ARRAY form on
// purpose: smbls normalises the ROOT app's `extends` through
// `initializeExtend` (createDomql.js → syncExtend.js), which keeps only an
// object-like value and drops a bare string to `[]` — measured live: the
// string form left the shell root `display: block` with no width/theme.
// ── Deferred shell subtrees (WS-SHELL-MOUNT-TREE-BUILD-2700MS-1) ─────────
// The shell boot builds ONE synchronous element tree. Measured on
// workspace.localhost/w/system/hq (imac-ws-39, in-framework phase profiler):
// 4012 elements, LINEAR at ~2 ms each, 2727 ms in a single task. Three
// top-level subtrees are 991 of those elements and 1848 ms of that task —
// and NONE of them is on screen at first paint:
//   TicketsDetailModal  620 ms / 457 el
//   AssistantDrawer     760 ms / 288 el
//   ModalRoot           468 ms / 246 el
//
// They now carry an `if:` latch. smbls `create()` returns EARLY on a false
// `if:` — before createChildren / registerEffects / applyStaticMixins — so
// the subtree is never built, while `registerIfEffect` keeps the element
// reactive so the FIRST true evaluation runs the full creation in place,
// under the same key and the same parent chain (no slot, no `set()`
// indirection, so bare-key resolution and `resolveModalRoot`'s tree walk
// are both unchanged).
//
// Every latch is ONE-WAY: once a subtree has been built it stays built, and
// its pre-existing `hide:` predicate owns visibility from then on. So after
// the first open the runtime shape is byte-identical to the eager mount —
// no re-create on close, no lost chat scroll, no lost modal state.
const _latchOnce = (el, flag, need) => {
  if (el[flag]) return true
  if (!need) return false
  el[flag] = true
  return true
}

// TicketsDetailModal's `if:` is functions/ticketDetailModalLoad.js
// `needTicketDetailModal`. Need = a seeded `selectedTicket`, the /ticket/:id
// full page, or a `?ticket=<id>` deep link (its own onCreate owns the URL
// restore, so a boot that carries the param must build it). Not reactive by
// itself: the `_routeNudge` selector in its `ifDeps` re-evaluates it on a
// client-side navigation. The modal's MODULE is loaded and folded on first
// need, and the latch opens once `_ticketModalReady` flips - smbls >= 3.14.798
// re-merges the parked element with the now-registered component. Same
// one-way `__wsDeferredMounted` latch as the others.

// ── Staged mount ladder (WS-SHELL-BOOT-TASK-LOAF-ACCEPTANCE-1) ───────────
// The three latches above take a subtree out of the boot task ENTIRELY, until
// a user action asks for it. That is the right trade for a modal or a drawer,
// and it is not the right trade for shell chrome that must simply BE there —
// the dropdown/tooltip hosts, the toast host, the presence dock, the gates.
// Those keep their eager semantics; they only stop being built in the SAME
// task as the rest of the shell.
//
// `_shellStage` is the ladder's counter (functions/shellStages.js): the shell
// root's onCreate starts it, and it steps 1 → 2 → 3 one macrotask apart right
// after the mount. Reading `s.root._shellStage` inside `if:` is what registers
// the dependency, so each step builds exactly that stage's subtrees and
// nothing else — one long task becomes four bounded ones.
//
// The latch is ONE-WAY, exactly like the three above: once a subtree is built
// its own `hide:` predicate owns visibility again, so past the ladder the
// runtime shape is identical to an eager mount.
//
// MEASURED, so the next reader does not re-derive it (dev.my.symbols.app,
// visible tab, /w/silverbreeze-labs, 2026-08-28): the mounted shell tree is
// 2532 DOM nodes, of which the route content is 1569 (62%) and AppHeader 70.
// The stageable chrome below is the rest. Route content is NOT stageable —
// smbls' router renders the entry route inside the same `create()` call
// (workspace/index.js dispatches no initial route of its own), so cutting
// that last block needs `createChildren` itself to yield — the framework half
// this ticket family already tracks separately.
const _atStage = (stage) => (el, s) =>
  _latchOnce(el, '__wsDeferredMounted', (s?.root?._shellStage || 0) >= stage)

// ── SPA-navigation guards (FA503) ──────────────────────────────────────────
// Both of these ran as raw `window.addEventListener('locationchange' |
// 'popstate', …)` pairs armed from shellApp.onCreate, each parked on an
// element latch nothing ever removed. They are module-scope functions now,
// driven by the shell root's declared `onWindowLocationchange` /
// `onWindowPopstate`. ONE declared prop can carry ONE handler per event, so
// the two guards are composed there, in their original registration order:
// the dropdown closer ran first (onCreate armed it first) and still does.

// Close any open dropdown layer on SPA navigation — the DropdownRoot lives at
// the shell root, so an open menu otherwise SURVIVES route changes as a stale
// (now-empty) glass panel (observed 2026-06-10 on /studio → /team-and-agents
// with the create menu open). AppShell's patched history dispatches
// `locationchange` on push/replace; `popstate` covers back/forward.
//
// Close on REAL navigation only — a PATHNAME change. `locationchange` fires on
// EVERY patched push/replaceState; same-URL no-op writes are guarded at their
// sources now, but slot-hosted pages also legitimately edit the QUERY/HASH in
// place (?page= slot sync, tickets ?ticket=, chat ?channel=, notes #note=) —
// in-route state, not navigation. An open dropdown must survive those;
// outside-click + ESC (owned by DropdownRoot) handle within-route dismissal.
// The full-href version of this guard still let every param edit dismiss
// chrome dropdowns whenever a PageSlot was active (2026-08-06, the "dropdowns
// are having problems opening when PageSlot is active" class).
//
// `_lastNavPath` was a closure variable; a declared prop has no closure, so it
// lives on the element — same value, same lifetime, no module-scope state that
// two shells in one process would share.
const _closeDropdownOnNav = (el) => {
  if (typeof window === 'undefined' || !el?.node) return // FA210 — bypass render
  const path = window.location.pathname
  if (path === el.__lastNavPath) return
  el.__lastNavPath = path
  try {
    el.call('closeDropdown')
  } catch (err) {
    console.warn('[shell] closeDropdown on nav failed:', err)
  }
}

const _authRouteGuardTick = (el, s) => {
  if (typeof window === 'undefined') return // FA210 — bypass render

  try {
    if (hasSessionToken()) return
    // Auth is TRI-STATE here, and "not resolved yet" is NOT "signed out".
    // `signedIn` is a plain boolean defaulting to false (state.js), so
    // before the seed lands it is indistinguishable from a real sign-out
    // — which is exactly how a signed-in deep link bounced through
    // /login: this guard's mount-time run used to execute ~30 lines
    // BEFORE the `preResolvedAuth` seed, so it read `signedIn: false` on
    // a fully authed session and hard-redirected. `_authReady` is the
    // flag that says the getMe snapshot has been applied; until it flips
    // there is nothing to decide on, so bail.
    //
    // Trusting the seed over a transient synchronous token read also
    // keeps the earlier fix intact: on slow devices hasSessionToken()
    // can miss DURING boot (the MWT canonicalize `replaceState` fires a
    // `locationchange` before the credential settles), and bouncing a
    // KNOWN-authed user then caused a `/`↔`/login` reload loop (the
    // reverted 2026-07-20 default-on incident; iPad-only). A real
    // sign-out clears `signedIn` with `_authReady` still true, so
    // genuine unauth still redirects.
    if (!s?._authReady) return
    if (s.signedIn) return
    const path = window.location.pathname
    if (isGuestAllowedPath(path)) return
    // /s/<token> — the public share-link viewer (PUBLIC-SHARE-LINKS-1).
    // The canonical guest allowlist lives in the SHARED package
    // (guestRoutes.js) and this squad does not own it, so the shell
    // exempts its own public route here — the same place the shell
    // already decides what its chrome does on a nav. The recipient of a
    // shared file or note has no account by design: the token IS the
    // credential, so a bounce here makes every shared link unopenable
    // for exactly the people it was created for. Kept byte-identical in
    // shape to the entry gate's prefix (index.js publicPathPrefixes) so
    // the two cannot disagree about what a share URL looks like.
    if (isPublicShareViewPath(path)) return
    // LOGINLOOP-1 (Nika 2026-08-16, mobile): this used to be
    //   encodeURIComponent(path + window.location.search)
    // which appended the CURRENT search verbatim. When that search
    // already carried a `next`, the value got wrapped instead of reused,
    // so every bounce nested one level deeper:
    //   /?next=%2F  ->  /login?next=%2F%3Fnext%3D%252F  ->  …
    // On mobile the boot race this guard already documents (a
    // `locationchange` fires before the credential settles) makes the
    // guard run repeatedly mid-boot, so the nesting became a visible
    // reload/loading loop.
    //
    // Two rules, owned by the TESTED helper every other /login bounce already
    // calls (shared functions/resolveAuthNext.js — bootShell.js ×3,
    // functions/auth.js). This guard carried the last inline copy of it:
    //   1. an existing `next` is REUSED, never re-wrapped — and unwrapped
    //      REPEATEDLY, because the reported URL was already nested two deep;
    //   2. bare `/` produces a plain `/login` with no noise `?next=/`.
    // Only the URL arithmetic moved. WHEN this guard fires (`_authReady`,
    // `signedIn`, the public-path exits above) is untouched — that timing has
    // its own reverted-loop precedent (2026-07-20, iPad).
    const next = _wsShared.functions.resolveAuthNext(path, window.location.search)
    window.location.replace(next ? `/login?next=${encodeURIComponent(next)}` : '/login')
  } catch (err) {
    console.warn('[shell] auth route guard failed:', err)
  }
}

// Close the calendar event popover when a navigation leaves its event. The
// popover is a global windows layer like the dropdown, so it SURVIVED route
// changes: Back from `/calendar?event=A` to the AI thread left event A open
// over the thread. Runs on popstate (Back / Forward, same path or not) and on
// a PATHNAME change; never on an in-route replaceState — the calendar writes
// its own `#date=` / `?event=` sync that way. The helper keeps the popover
// while `?event=` still names the open event
// (functions/eventDetailModal.js `closeStaleEventDetailModal`).
const _closeEventPopoverOnNav = (e, el, pathChanged) => {
  if (typeof window === 'undefined' || !el?.node) return // FA210 — bypass render
  if (e?.type !== 'popstate' && !pathChanged) return
  try {
    el.call('closeStaleEventDetailModal')
  } catch (err) {
    console.warn('[shell] event popover close on nav failed:', err)
  }
}

// ONE handler for both SPA navigation events (`locationchange` from the
// patched history, `popstate` for back/forward): close the open dropdown and
// a stale event popover, then run the auth guard — in that order. It was
// written out twice.
const _onSpaNavigation = (e, el, s) => {
  // The dropdown closer re-seeds `__lastNavPath` on a pathname change only,
  // so a moved value IS the path change — one reader of the path for both.
  const fromPath = el?.__lastNavPath
  _closeDropdownOnNav(el)
  _closeEventPopoverOnNav(e, el, el?.__lastNavPath !== fromPath)
  _authRouteGuardTick(el, s)
}

// The shell root's create pass — the shell's own auth subscription, data
// load, analytics and listeners. app.onCreate below runs it at once on a
// parcel boot (bootShell settled the boot before create()), and after the
// boot settles on the published shell (see app.onCreate).
const _shellOnCreate = (el, s) => {
    // Boot segment G (BOOT-8S.md): the shell root is being created.
    bootMark('wsboot:shell:create')
    // Scheme A URL parsing (`/w/<org>/<ws-handle>/<route…>`): segment 2 is a
    // workspace IFF the session knows a workspace of that org by that handle
    // — this resolver is that knowledge, reading the LIVE root each call so
    // claims refreshes are picked up automatically. Registered before any
    // route dispatch (bootShell dispatches after create()); until claims
    // land, unmatched segments read as routes on the HQ — the legacy
    // interpretation — and the boot canonicalizer re-stamps once they do.
    registerWorkspaceHandleResolver((orgSlug, seg) => {
      const root = s.root
      const want = String(seg ?? '').toLowerCase()
      if (!want) return false
      // Fast path, list-independent: the ACTIVE workspace record itself. On a
      // 2-segment boot the segment IS the active workspace by construction
      // (boot resolved it from the URL), and root.workspace is a full record
      // — so this recognizes the handle even when the workspaces lists are
      // still empty (SDK-only sessions fill them late, and ticks can wipe
      // them).
      const aw = root?.workspace
      const rOrg = root?.org
      if (aw && rOrg && (rOrg.slug === orgSlug || rOrg.username === orgSlug)) {
        const awOrgId = String(aw.org_id ?? aw.organization ?? '')
        const rOrgId = String(rOrg.id ?? rOrg._id ?? '')
        if (!awOrgId || !rOrgId || awOrgId === rOrgId) {
          const own = [aw.slug, aw.key, aw.id ?? aw._id]
            .filter(Boolean)
            .map((v) => String(v).toLowerCase())
          if (own.includes(want)) return true
        }
      }
      const orgs = Array.isArray(root?.orgs) ? root.orgs : []
      const org = orgs.find((o) => o && (o.slug === orgSlug || o.username === orgSlug))
      if (!org) return false
      const oid = String(org.id ?? org._id ?? '')
      const fromMap = root?.orgWorkspaces?.[oid]
      const list = Array.isArray(fromMap)
        ? fromMap
        : Array.isArray(root?.workspaces)
          ? root.workspaces
          : []
      return !!findWorkspaceInOrg(oid, list, seg)
    })

    // Feature flags: mirror the cached per-workspace overrides onto root
    // state before children render, so navigation gates resolve on first
    // paint (server truth takes over when root.workspace lands).
    // KEPT EAGER (iPad P1 boot audit): pre-first-paint by DESIGN — nav
    // gates read these on their first render; deferring re-introduces the
    // gate flash. Cost is one localStorage read + JSON.parse (marks prove
    // it's sub-ms — check the wsboot:feature-flags measure).
    bootMark('wsboot:feature-flags:start')
    hydrateFeatureFlags(s.root)
    bootMark('wsboot:feature-flags:end')
    bootMeasure('wsboot:feature-flags', 'wsboot:feature-flags:start', 'wsboot:feature-flags:end')
    el.call?.('installAppShellSync', s)

    // Start the staged-mount ladder (WS-SHELL-BOOT-TASK-LOAF-ACCEPTANCE-1).
    // Scheduled from HERE on purpose: `onCreate` runs while the root is being
    // created, so the first tick it queues cannot possibly run until the whole
    // synchronous mount has drained — the exact task boundary the stages need.
    // Every stage is a macrotask of its own, so the boot's one long build
    // becomes several bounded ones. See functions/shellStages.js.
    startShellStageLadder(s.root)

    // Register the shell state root so reactivateWorkspaceModule (driven from
    // the marketplace's isolated window-layer, which can't reach this root via
    // the element tree) can hot-activate installs/uninstalls live — without it
    // the navbar only updated on reload.
    registerShellRoot(s.root)

    // Both SPA-navigation guards below — the dropdown closer and the auth
    // route guard — are DECLARED on this root as `onWindowLocationchange` /
    // `onWindowPopstate` (see above). They used to be four raw
    // `window.addEventListener` calls fired from here behind two element
    // latches (`el.__dropdownNavCloser`, `el.__authRouteGuard`) that nothing
    // ever removed — the latches existed only because the registration had no
    // owner. A declared prop has one, so both are gone (FA503).
    //
    // Seed the closer's "last path" the way the old closure did at arm time
    // (`let _lastNavPath = window.location.pathname`). Without the seed the
    // FIRST same-path `replaceState` of the boot would read as a real
    // navigation and fire one extra closeDropdown.
    if (typeof window !== 'undefined') el.__lastNavPath = window.location.pathname

    // Synchronously seed auth state from bootShell's pre-resolved
    // snapshot (computed off `sdk.getMe()` BEFORE create()). This
    // makes the shell mount with `signedIn / jwtClaims / orgs /
    // workspace / org / currentUserEmail / ...` already populated, so
    // every reactive consumer (NoOrgOverlay, AppHeader chrome, …) gets
    // the correct frame on its FIRST render instead of flashing
    // empty/default values between mount and the async
    // subscribeAuthSession registration. `_authReady` flips here so
    // gates that need an explicit "auth resolved" signal can wait.
    // Anonymous boot (no user): no snapshot, but we still flip
    // `_authReady` immediately — the modal hides on `!signedIn` anyway,
    // and consumers shouldn't block waiting for an auth event that
    // will never fire.
    // Seed the live route synchronously, alongside the auth snapshot.
    // `currentPath` defaults to '/' in the state schema (state.js) and is
    // only synced to the real URL by the smbls router AFTER first paint.
    // So on a hard load of an auth route (/login, /signup, …) the
    // AppHeader's route-based `hide:` first evaluated against '/' — not an
    // AUTH_PATH_RE match → the dashboard chrome SHOWED — then the router
    // corrected `currentPath` to /login and the chrome hid. That one-frame
    // chrome flash reads as "loading dashboard → login screen".
    //
    // onCreate runs before any child renders (same guarantee the
    // preResolvedAuth seed below relies on — see the AppHeader chrome note
    // above), so seeding the real path here makes every route-dependent
    // binding paint the correct frame first. The router sets the same
    // value on its own init, so this is idempotent (no extra dispatch).
    const _livePath = typeof window !== 'undefined' ? window.location.pathname : '/'
    const _liveSearch = typeof window !== 'undefined' ? window.location.search : ''
    const _preAuth = el.context?.preResolvedAuth || null
    if (_preAuth) {
      s.root.update({
        ..._preAuth,
        currentPath: _livePath,
        currentSearch: _liveSearch,
        _authReady: true,
        // A session exists → the data load WILL run. Arm the loader gate NOW,
        // synchronously at mount: loadPrivateData only fires from the async
        // subscribeAuthSession callback, and in that window the gate read
        // inactive — firing the boot-ready handoff prematurely and unveiling
        // the second loader (the boot double-load flicker). loadPrivateData's
        // finally clears it. Anonymous boots skip this (no data load — the
        // spiral hands off to /login immediately).
        _dataLoading: true
      })
      // Kick the private-data load NOW, synchronously at mount. It used to
      // fire only from subscribeAuthSession's async INITIAL_SESSION callback,
      // which lands seconds after mount (measured 2.9s of dead boot time
      // locally: loadPrivateData started at t=9.8s with the shell mounted at
      // ~6.9s). The claims are already seeded in this very update; the
      // _loadInFlight latch inside loadPrivateData turns the later
      // subscribe-driven call into a no-op instead of a duplicate, and its
      // own no-active-workspace canary keeps the anonymous/cold cases safe.
      try {
        loadPrivateData(s.root, el.sdk || el.context?.methods?.sdk, {
          bootPayload: el.context?.bootPayload || null
        })
      } catch (err) {
        console.warn('[shell] early loadPrivateData kick failed:', err?.message || err)
      }
    } else {
      s.root.update({
        currentPath: _livePath,
        currentSearch: _liveSearch,
        _authReady: true
      })
    }

    // A FLY ARRIVAL, read HERE — before any page strips `fly` from the address
    // (functions/onboarding.js onboardingNoteFlyArrival): the mark keeps the
    // new-account hold down and the full wizard closed for this load. It
    // writes no decision; Fly's own `pending` record is what owes the ready
    // screen.
    try {
      el.call?.('onboardingNoteFlyArrival', s.root, _liveSearch)
    } catch (err) {
      console.warn('[shell] fly arrival mark failed:', err?.message || err)
    }

    // NOW run the mount-time auth check — `_authReady` is true and `signedIn`
    // carries the server-verified answer, so the guard decides on real state
    // instead of on schema defaults. (Registration happened above; this is the
    // deferred one-shot run.)
    _authRouteGuardTick(el, s)

    // Seed the member roster at shell mount from the composite response, or
    // the legacy entry prime. The response must still belong to this workspace;
    // a legacy promise must also match the URL org and cannot land after a
    // tenant switch. Peek, don't consume: loadPrivateData shares the result.
    try {
      const rosterWorkspaceId = String(s.root.workspace?.id || s.root.workspace?._id || '')
      const rosterOrgId = String(s.root.org?.id || s.root.org?._id || '')
      const bootPayload = el.context?.bootPayload
      const urlOrgSlug = (/^\/w\/([^/]+)/.exec(_livePath) || [])[1]?.toLowerCase() || null
      const earlyRoster =
        rosterWorkspaceId &&
        String(bootPayload?.workspaceId || '') === rosterWorkspaceId &&
        Array.isArray(bootPayload?.members)
          ? Promise.resolve(bootPayload.members)
          : !urlOrgSlug || primedOrgSlug() === urlOrgSlug
            ? peekPrimedPeople()
            : null
      earlyRoster
        ?.then((rows) => {
          if (
            String(s.root.workspace?.id || s.root.workspace?._id || '') === rosterWorkspaceId &&
            String(s.root.org?.id || s.root.org?._id || '') === rosterOrgId &&
            Array.isArray(rows) &&
            !(s.root.people || []).length
          ) {
            const mapped = rows.map(mapMemberRow)
            s.root.update({
              peopleAll: mapped,
              people: mapped,
              peopleLoaded: true,
              peopleError: null
            })
          }
        })
        .catch(() => {})
    } catch (_) {
      /* prime not active */
    }

    // Resolve the unified AI service once at boot and cache it in the
    // shared aiService singleton. DOMQL factories (CanvasPromptTextarea
    // ParamsBtn picker, AppAssistant, ticket AI surfaces) read it back
    // synchronously via `getAiService()`. Storing it on root state
    // doesn't work — smbls' state clone strips the class prototype,
    // leaving plain own properties that lose getMode() / dispatch() /
    // setMode() etc.
    //
    // After resolution we bump `_aiSvcReady` in root state so reactive
    // factories that gate on the service's availability re-fire once
    // it's mounted.
    try {
      const _sdk = el.sdk
      if (_sdk && typeof _sdk.getService === 'function') {
        // `resolveAiService` reads straight off the `_wsShared` bare import
        // already at the top of this file — no separate dynamic import of a
        // deep aiService.js subpath needed (that subpath can't resolve
        // through the platform's public importmap once this package is
        // pushed as a library, tickets/execution-board.md §1 P0; the whole
        // package is already loaded by the time this async tail runs).
        Promise.resolve(_wsShared.functions.resolveAiService(_sdk))
          .then(() => {
            s.root?.update?.({ _aiSvcReady: true })
            // Install the simone-bridge → sdk.ai adapter so the
            // workspace Chat surface can dispatch through the unified
            // SDK without needing a real local bridge. This flips
            // `bridgeStatus` to 'open' so the composer enables its
            // Send button.
            el.call?.('installSimoneAiTransport', el)

            // `?aiChatSession=<threadId>` deep-link — opened by the
            // chrome-connect extension's logo/avatar click (see
            // `extensions/packages/ext-ui/symbols/pages/home.js`'s
            // `_openSessionOnSymbolsApp`). Hydrates the matching thread
            // into the AppAssistant drawer and opens it. Best fired
            // after `_aiSvcReady` so `sdk.getService('aiChat')` is up.
            try {
              const _qs =
                typeof URLSearchParams !== 'undefined'
                  ? new URLSearchParams(_liveSearch || '')
                  : null
              const _sessionId = _qs ? _qs.get('aiChatSession') : null
              if (_sessionId) {
                s.root.update({ assistantOpen: true, _aiThreadId: _sessionId })
                // el.call, not a relative import() — frank cannot rebuild a
                // project-local dynamic import (shellPublishedEmission.test.js).
                Promise.resolve(el.call('aiChatLoadThread', s.root, _sessionId)).catch((err) =>
                  console.warn('[shell] aiChatSession hydrate failed:', err?.message || err)
                )
              }
            } catch (err) {
              console.warn('[shell] aiChatSession parse threw:', err?.message || err)
            }

            // `?aiConversation=<id>` — a shared workspace-conversation link
            // (Share in the sessions menu). Opens the drawer and loads the
            // thread through the SAME server-backed switch the history menu
            // uses; a non-owner member can read it when its owner flipped
            // `shared` (the server's one widened read path). Fired here, after
            // `_aiSvcReady`, because switchSession needs the live AI service.
            try {
              const _cq =
                typeof URLSearchParams !== 'undefined'
                  ? new URLSearchParams(_liveSearch || '')
                  : null
              const _convId = _cq ? _cq.get('aiConversation') : null
              if (_convId) {
                const _openPatch = { assistantOpen: true }
                if (s.root.assistantDock === 'minimized') _openPatch.assistantDock = 'right'
                s.root.update(_openPatch)
                el.call?.('switchSession', _convId)
              }
            } catch (err) {
              console.warn('[shell] aiConversation link threw:', err?.message || err)
            }

            // `?prompt=<text>` landing hand-off
            // (WS-WORKSPACE-CONSUMES-THE-PROMPT-QUERY-FROM-THE-LANDING-HERO-
            // COMPOSER-1, supersedes this block's original WS-LANDING-PROMPT-
            // HANDOFF behaviour) — the marketing hero (company/landing
            // PromptBar + RevealEndCard) redirects to
            // `my.symbols.app/?prompt=<encoded>`. PREFILL the shell composer
            // with it — cursor at the end, nothing sent on the visitor's
            // behalf — via the bridge CanvasPromptTextarea's own onCreate
            // registers (this handler runs in the shell root's state scope,
            // a DIFFERENT DOMQL scope than that component's local `draft`).
            // The UNAUTHed path needs no code here — the security guard above
            // captures `path + search` into `?next=`, stashOAuthNext survives
            // the provider hop, and the callback replaces back to
            // `/?prompt=…` authed, so this same handler fires post-login for
            // new signups too.
            //
            // ORDER IS THE WHOLE FIX. This block used to strip the param
            // FIRST and call the bridge second, with a `?.` — and the bridge
            // belongs to CanvasPromptTextarea, which mounts 716 ms LATER
            // (measured on the signed-in dev.my seat 2026-09-23). The call
            // no-opped onto an absent bridge while the strip had already
            // deleted the only copy of the visitor's sentence. So now:
            // deliver, park when nobody took it, and strip only once the text
            // is held (functions/inboundPrompt.js owns that contract; the
            // composer drains the park on its own mount, below).
            //
            // On the APP BUILDER's route the prompt is the builder's
            // (`/add-app?prompt=` — functions/addAppPrompt.js starts a new
            // build thread with it): the home composer never also takes it.
            try {
              const _promptForBuilder = inboundPromptForBuilder(_livePath)
              let _promptStore = null
              try {
                _promptStore = typeof window !== 'undefined' ? window.sessionStorage : null
              } catch (_) {
                /* sandboxed frame — no park available, and receiveInboundPrompt
                   then KEEPS `?prompt=` rather than eating it */
              }
              const _inbound = _promptForBuilder
                ? { nextHref: null }
                : receiveInboundPrompt({
                    search: _liveSearch,
                    href: typeof window !== 'undefined' ? window.location.href : '',
                    deliver: (text) => s.root._prefillHomePrompt?.(text) === true,
                    storage: _promptStore
                  })
              if (_inbound.nextHref) {
                try {
                  window.history.replaceState(window.history.state, '', _inbound.nextHref)
                } catch (_) {
                  /* replaceState can throw in sandboxed frames — non-fatal */
                }
              }
            } catch (err) {
              console.warn('[shell] landing prompt hand-off threw:', err?.message || err)
            }
          })
          .catch((err) => console.warn('[shell] resolve sdk.ai failed:', err?.message || err))
      }
    } catch (err) {
      console.warn('[shell] sdk.ai resolve threw:', err?.message || err)
    }

    // Persist the resolved active-org back to Mongo on first boot when
    // `User.activeOrganization` is null OR differs from the shell's
    // resolved pick. The unified-auth-boot snapshot already picked a
    // default (Mongo → gov JWT → orgs[0]) via _resolveAuthSnapshot, but
    // that pick lives in-memory; without persistence every subsequent
    // server scope check (e.g. /core/analyzed/ingest's `scopedAnalyzed`,
    // which falls back to `req.user.activeOrganization`) keeps seeing
    // the stale/null value and 400s or 401s.
    //
    // The "differs" branch covers the root cause of GH #2238: when
    // `User.activeOrganization` is set to a personal org that has no
    // associated Workspace document (or no federated home workspace),
    // the SDK-Mongo auth path returns "no active workspace for user" →
    // 401 for tickets/chat/documents/etc. The shell's snapshot resolver
    // picks the correct TEAM org (governance JWT → orgs[0] fallback), so
    // writing it back to Mongo before `loadPrivateData` fires ensures the
    // userResolver finds the right workspace on the FIRST request — not
    // only after an explicit org switch.
    //
    // Fire-and-forget — `sdk.setActiveOrganization` PATCHes
    // /auth/me/active-org, hydrates `_currentUser.activeOrganization`,
    // and best-effort refreshes federation claims. Failures don't
    // block boot (and the next /auth/me re-derives the snapshot
    // anyway), but the happy path makes the user's active-org choice
    // sticky from this moment on.
    try {
      const _me = el.sdk?._currentUser || null
      const _resolvedOrgId = _preAuth?.org?.id || null
      const _mongoOrgId = _me?.activeOrganization ? String(_me.activeOrganization) : null
      // Only persist on a trustworthy membership list (never a gov-only fallback
      // guess). Still persists the #2238 correction — the team org is in
      // user.organizations. Without this, _resolvedOrgId can be an orgs[0] guess
      // from a partial list, and persisting that rotates the active org on reload.
      // WHITELABEL-STRICT-TENANT-SCOPE-1 — never from a white-label host: there
      // the resolved org is the HOST's binding (the boot states it in memory),
      // not the account's choice, and persisting it would move the account's
      // my.symbols tabs onto the customer's workspace after one visit.
      const _needsPersist =
        _resolvedOrgId &&
        _me &&
        _mongoOrgId !== _resolvedOrgId &&
        hasTrustedOrgList(_me) &&
        !isCustomDomainHost()
      if (_needsPersist) {
        Promise.resolve(el.sdk.setActiveOrganization(_resolvedOrgId)).catch((err) =>
          console.warn('[shell] persist activeOrganization failed:', err?.message || err)
        )
      }
    } catch (err) {
      console.warn('[shell] persist activeOrganization threw:', err?.message || err)
    }

    // Hydrate follow list from localStorage so the same follow-state
    // surfaces across workspace + marketplace navigation.
    try {
      const raw =
        typeof localStorage !== 'undefined' ? localStorage.getItem('marketplace.follows') : null
      const list = raw ? JSON.parse(raw) : []
      if (Array.isArray(list) && list.length) {
        s.root.update({
          followingEmails: list.map((e) => (e || '').toLowerCase())
        })
      }
    } catch (err) {
      console.warn('[shell] follow-list hydrate failed:', err?.message || err)
    }

    // Hydrate the AI assistant dock (right | left | minimized | main) from
    // localStorage so the user's choice survives reloads. Written by
    // setAssistantDock (functions/assistantDock.js); the user record
    // reconciles it once the prefs row lands (loadPrivateData). Invalid /
    // absent → keep the state default ('main', Page — Nika's pick 6 A).
    try {
      const dock = readStoredAssistantDock()
      if (dock) s.root.update({ assistantDock: dock })
    } catch (err) {
      console.warn('[shell] assistant-dock hydrate failed:', err?.message || err)
    }
    // The assistant's MODE (fly | assistant | dashboard | …) paints the same
    // way: the stored choice first, the user record reconciles it once the
    // prefs row lands (loadPrivateData). Absent → the state default ('fly',
    // Nika 2026-10-22: "ok make fly default view").
    try {
      // A comm host shows only its own modes (docs/COMM_HOST.md).
      const mode = readStoredAssistantMode()
      if (mode) s.root.update({ assistantMode: commAssistantMode(mode) })
    } catch (err) {
      console.warn('[shell] assistant-mode hydrate failed:', err?.message || err)
    }
    // Agent mode (functions/agentMode.js) paints the same way: this device's
    // choice first, the record reconciles it at the prefs load; a
    // `?mode=agent` link enters it for this visit without a write.
    try {
      const agent = readStoredAgentMode()
      const link = agentModeFromSearch(typeof window !== 'undefined' ? window.location.search : '')
      if (agent !== null || link) {
        s.root.update({
          ...(agent !== null ? { agentMode: agent } : {}),
          ...(link ? { _agentModeLink: true } : {})
        })
      }
    } catch (err) {
      console.warn('[shell] agent-mode hydrate failed:', err?.message || err)
    }
    // The sidebar's remembered width (functions/assistantWidth.js), the same
    // way: this device's copy first, the record reconciles it at the prefs load.
    try {
      const width = readStoredAssistantWidth()
      if (width) s.root.update({ assistantWidth: width })
    } catch (err) {
      console.warn('[shell] assistant-width hydrate failed:', err?.message || err)
    }
    // Checks for tips dismissal and updates state accordingly.
    const dismissedTips = [
      '_homeCtxTipDismissed', // home dashboard CtxMenuTip
      '_studioHelpDismissed', // /studio StudioHelpTip
      '_ticketsHelpDismissed', // /tickets TicketsHelpTip
      '_tagHelpDismissed' // /team-and-agents + /agents HelpBox
    ].filter((key) => getPref(key) === '1')
    if (dismissedTips.length) {
      s.root.update(Object.fromEntries(dismissedTips.map((key) => [key, true])))
    }
    // The person's accessibility choices, read out of localStorage into root
    // state HERE — the shell's own onCreate, before any page renders — so a
    // person who chose larger type or high contrast never sees a frame of the
    // base scale first (2026-09-04 per Nika "add same accessibility options as
    // we have in landing"). `_syncShellRoute` mirrors the result onto <html>
    // and the injected sheet below carries the rules.
    applyA11yPrefs(s.root)
    // The smbls default router mounts pages into a 'content' div that sits in
    // the grid's 1fr row. That div has display:flex with overflow:visible by
    // default — it expands beyond the grid cell and the shell clips it, making
    // pages unscrollable. overflow:auto + min-height:0 constrain content to
    // the grid cell so the user can scroll within it.
    //
    // `:where(…)` keeps these defaults at specificity 0 so any page that
    // declares its own overflow (e.g. full-bleed panzoom canvases like
    // /studio/<slug> with `overflow:'hidden'`) wins via its class rule.
    if (typeof document !== 'undefined' && !document.getElementById('__shell-content-scroll')) {
      const s = document.createElement('style')
      s.id = '__shell-content-scroll'
      s.textContent =
        // Single source of truth for the navbar-clearance top inset shared by
        // the app-route content padding (below) AND the canvas chrome that must
        // line up with a page opened in the CanvasPageSlot (the slot itself,
        // CanvasSidebarIsland, InfraSidebar — all reference var(--shell-top)
        // for their `top`). = spacing token C1 + a 3px navbar-gap nudge —
        // tightened 2026-07-01 (per Nika "big offset top from navbar, make it
        // shared"): was D1 (≈92px, ~2× the 48px header) which left a big empty
        // band; C1 (≈58px) clears the header snugly and, since this is the single
        // source, every aligned surface (app pages, PageSlot, canvas islands)
        // follows in lockstep. Defined here (raw CSS) because a DOMQL component
        // `top: 'calc(var(--spacing-D1) + 3px)'` compiles to an EMPTY atomic rule
        // (`._t-<hash>{}`) and silently never applies — a bare var() reference,
        // by contrast, resolves cleanly (`._t-var--shell-top{top:var(…)}`).
        // Keep the name SHORT: the atomic-CSS engine hashes any prop VALUE longer
        // than 20 chars (`var(--shell-top)` = 16 ✓), and the hashed path also drops
        // the rule body — so a longer var name reintroduces the empty-rule bug.
        // Change the +3px in ONE place here and every aligned surface follows.
        // Register --shell-top as a <length> so its em-based calc COMPUTES AT
        // :root (html font-size) and inherits as a fixed px value. Unregistered,
        // var() substitution re-resolves the calc's `1em` against EACH consumer's
        // own font-size — the canvas rails run fontSize:Z, so the "same"
        // --shell-top gave 49px on InfraSidebar vs 58px on the app content pad
        // (and the D1 rails sat at 74px): three different "aligned" tops.
        // (2026-07-03 per Nika "make sure layout is consistent" across
        // /studio /infra /tickets /agents.)
        '@property --shell-top{syntax:"<length>";inherits:true;initial-value:58px}' +
        // + env(safe-area-inset-top): on iPad/WKWebView the AppHeader is inset
        // below the status-bar notch (AppHeader.js top:env(...)), so every
        // --shell-top consumer (app content pad, canvas rails, sidebars) must
        // clear that extra inset too. 0 on non-notched devices.
        ':root{--shell-top:calc(var(--spacing-C1) + 3px + env(safe-area-inset-top))}' +
        // --prompt-clear: viewport-bottom clearance that lands a sheet's bottom
        // edge exactly on the s1m0ne composer's TOP edge (2026-07-10 per Nika
        // "make bottom offset aligned to chat textarea"). Live-maintained by a
        // ResizeObserver on CanvasPromptTextarea (the textarea grows multiline),
        // so the PageSlot tracks it without hardcoding the composer height; this
        // initial value only covers the pre-observer first paint.
        '@property --prompt-clear{syntax:"<length>";inherits:true;initial-value:56px}' +
        ':root{--prompt-clear:56px}' +
        // --nav-gap: the single visual gap between a floating sheet and a navbar
        // island. --shell-top already leaves this gap above the top header; the
        // PageSlot bottom reuses it (via --slot-bottom) to clear the s1m0ne composer
        // by the SAME amount → symmetric top/bottom insets (2026-07-11 per Nika
        // "same gaps from top and bottom navbars"). One knob moves both edges.
        '@property --nav-gap{syntax:"<length>";inherits:true;initial-value:10px}' +
        ':root{--nav-gap:10px}' +
        // --module-rail-clear: viewport-left clearance of the immersive
        // module rail (ModuleRailHost below) — 0 whenever the rail is
        // hidden (its RO zeroes on display:none). The immersive PageSlot
        // insets its LEFT edge by this, so the sheet opens BESIDE the rail
        // instead of under it.
        '@property --module-rail-clear{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--module-rail-clear:0px}' +
        // --stage-bg (the ACTIVE workspace module's chrome.stage, written
        // on <html> by _setStageVar) is DELIBERATELY NOT declared here and
        // NOT @property-registered: a declared value (or an @property
        // initial-value) is always "defined", so `var(--stage-bg,
        // var(--color-stage))` would never reach its token fallback. Set =
        // override; removed = design-system stage. See canvasBackground.js.
        // --stack-line: the composer stack's top + --nav-gap — the line the
        // bottom chrome docks ON (the toast stack, the call panel). Each frame
        // that moves the stack (a bottom navbar, the phone dock) redefines this
        // LINE, never --slot-bottom.
        // --toast-clear: what the toast stack adds above that line while a
        // toast shows — its box: the toasts, the --nav-gap above them and the
        // call panel's --call-clear below them — and 0 while none does.
        // Measured (the box HEIGHT only, never a position) by _trackClearVar on
        // the GlobalNotification mount below. The route toast ("Making a page
        // for this · Back to chat") sat over card text above the composer
        // (persona rounds ai-r1 + ai-nino, dev 2026-10-28).
        // --slot-bottom: the PageSlot's bottom inset — every sheet's and view's
        // bottom: the stack line, + the toast stack while one shows, so no
        // content sits under a toast. The sum stays CSS, so a stack that moves
        // under a showing toast (a docked row comes, the call panel rises)
        // carries the line with it, no re-measure. Derived HERE only. Plain
        // :root (not @property) so the calc RE-RESOLVES whenever an RO updates
        // --prompt-clear or --toast-clear.
        '@property --toast-clear{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--toast-clear:0px}' +
        ':root{--stack-line:calc(var(--prompt-clear) + var(--nav-gap));--slot-bottom:calc(var(--stack-line) + var(--toast-clear))}' +
        // SimoneReasoningPill (the compact "AI is working" small view) + its idle
        // twin SimoneSuggestions ride the SAME --slot-bottom the PageSlot uses, so
        // their bottom edge tracks the composer clearance live. Set here (not in
        // the component) because a raw var() on `bottom` compiles to an empty DOMQL
        // atomic rule.
        '[data-key="SimoneReasoningPill"],[data-key="SimoneSuggestions"]{bottom:var(--slot-bottom)}' +
        // ...and wherever the island docks above the composer as ONE stack (the
        // voice call bar, a thread preview — the island marks itself
        // data-gauge="composer"), it takes the composer's LIVE width: --prompt-w
        // is the resting pill's width (the composer's RO below), which grows past
        // `H` with its context chip. Never under `H`. The island paints its own
        // width inline, hence !important; phones keep its left/right inset.
        '@media (min-width:769px){[data-key="SimoneReasoningPill"][data-gauge="composer"]{width:max(var(--prompt-w,0px),var(--spacing-H))!important}}' +
        // --chip-row: on a PHONE the suggestion chips become one full-bleed
        // scrolling row docked at --slot-bottom (see _simone/Suggestions.js), so
        // every other bottom-docked island — the canvas Zoombar, the tickets
        // filter/create FABs — must sit one chip row higher or the chips cover
        // them (they win on z-index). One knob, so the band can never drift
        // apart across its consumers, exactly like --slot-bottom above.
        //
        // Reserved UNCONDITIONALLY at phone widths rather than keyed to whether
        // chips are currently visible: the chips come and go (hidden while a
        // turn streams), and a bottom bar that hops up and down under the thumb
        // as the assistant thinks is worse than a stable one. 0 elsewhere, so
        // desktop/tablet geometry is untouched.
        '@property --chip-row{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--chip-row:0px}' +
        // --zoombar-clear: the Zoombar's top edge from the viewport bottom
        // while it shows (the canvas routes), 0 while it hides — measured by
        // _trackClearVar on its mount below. The call dock (meet
        // MeetCallWidget) shares the bottom-right corner and adds it to its
        // own bottom; before, the bar (z 10310) covered the dock's controls.
        '@property --zoombar-clear{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--zoombar-clear:0px}' +
        // --calldock-clear: the call dock's top edge from the viewport bottom
        // while it shows, 0 while it hides — measured on its mount below. The
        // house help tips (shared CanvasHelpCard) stack above it.
        '@property --calldock-clear{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--calldock-clear:0px}' +
        '@media (max-width:768px){:root{--chip-row:calc(var(--spacing-C) + var(--nav-gap))}}' +
        // PHONE + an open window: the sheet covers the composer, the way an
        // iOS sheet covers the tab bar — PageSlot's own phone rule (full-bleed
        // to the bottom, the composer clearance suspended). The composer's
        // 1400 (above the PageSlot's 1300 — Nika 2026-08-06, so the desktop
        // window's scrim never dims it) drew it OVER the sheet's foot at
        // 390: an AppDialog phone sheet inside the window lost its Cancel /
        // Request row under it (design-d2, live on 8112be672). At phone width
        // only, while a window is open, it steps back to the composer's rung
        // of the chrome ladder (60 — above the page and the blur backdrop,
        // below the window); desktop keeps 1400.
        '@media (max-width:768px){html[data-slot="open"] [data-key="CanvasPromptTextarea"]{z-index:60}}' +
        // Thinking sheen — a soft diagonal shimmer sweeping the CanvasPromptTextarea
        // while the AI works (its ThinkingSheen child toggles via `hide`). Kept here,
        // not in the component, because a raw gradient/animation value empty-rules in
        // the DOMQL atomic compiler. Theme-agnostic white sheen at low opacity;
        // overflow:hidden on the composer clips it to the rounded card.
        '@keyframes simoneSheen{0%{background-position:250% 0}100%{background-position:-120% 0}}' +
        '[data-key="ThinkingSheen"]{position:absolute;inset:0;border-radius:inherit;background:linear-gradient(100deg,transparent 40%,rgba(255,255,255,0.10) 50%,transparent 60%);background-size:250% 100%;animation:simoneSheen 1.7s ease-in-out infinite}' +
        // --nav-clear: viewport-top → header-navbar BOTTOM edge, live-maintained
        // by a ResizeObserver on the AppHeader mount below (mirror of
        // --prompt-clear). --slot-top derives the PageSlot's top from it with
        // the SAME --nav-gap the bottom uses — the sheet's top gap to the navbar
        // and bottom gap to the composer are exactly equal (2026-07-12 per Nika
        // "equal top and bottom gaps from navbars top and bottom"). Plain :root
        // calc (not @property) so it re-resolves when the RO updates --nav-clear.
        '@property --nav-clear{syntax:"<length>";inherits:true;initial-value:48px}' +
        ':root{--nav-clear:48px}' +
        ':root{--slot-top:calc(var(--nav-clear) + var(--nav-gap))}' +
        ':root{--chat-hdr-cap:calc(50% - var(--spacing-F) - var(--spacing-A))}' +
        ':where([data-key="content"]){overflow:auto;min-height:0}' +
        // App pages (tickets/chat/…) flow below the 48px absolute AppHeader
        // instead of colliding with it. Gated on data-shell-route so home
        // (self-offsets) and canvas pages (pure overlay) are unaffected
        // (2026-06-09 per Nika "fix layout for all similar pages").
        // Uses --shell-top (= C1 + 3px) — the SAME top inset as the canvas
        // chrome: CanvasPageSlot, CanvasSidebarIsland and InfraSidebar all reference
        // the same var for their `top`. So a page loaded directly (/calendar,
        // /team-members) lines up with the navbar exactly as it does when opened in
        // the PageSlot. (Plain D matched neither the 48px header nor the slot; C was
        // shorter still and the content kissed the navbar.) (2026-06-22 per Nika
        // "load normal pages same offset from navbar as PageSlot"; +3px navbar-gap
        // nudge 2026-06-24 — single-sourced in --shell-top above.)
        //
        // MUST be scoped to the smbls router-mount div (the direct
        // `[data-key="smblsapp"] > [data-key="content"]`), NOT a bare
        // `[data-key="content"]` descendant selector. The default router mounts
        // package index pages (those exported as `home`: documents/calendar/…)
        // as a page-root that ITSELF carries `data-key="content"`, nested inside
        // the mount. A descendant selector then matched BOTH and applied D1
        // twice → 178px of dead space above the content (the navbar is only
        // ~48px). The direct-child combinator pins the clearance to the single
        // mount so every 'app' page clears the header exactly once, regardless
        // of whether its page-root duplicates the `content` key. (2026-06-24 per
        // Nika "too big space at the top — find the shared root cause".)
        // FULL-PAGE = SLOT-PANE PARITY (2026-09-02 per Nika "all pages
        // should have consistent background and layout ... consistent on
        // individual page + pageslot"): every 'app'-kind route now renders
        // inside the SAME sheet the PageSlot pane paints — the
        // content-surface pair over the stage board, radiusCard, the one
        // float shadow, and the slot's own insets (--slot-top /
        // --spacing-X2 / --slot-bottom), so a page opened full-page is
        // pixel-family-identical to the same page opened in the slot. The
        // pages themselves are proven inside this geometry: they run in the
        // pane daily. Loading --stage-bg with the sheet color is the pane's
        // dissolve trick, one step further: AppPage-family wrappers' inline
        // stage paints resolve to the sheet color itself, so they merge
        // into the sheet (PageSlot.js pane comment, measured 2026-09-01). The
        // 17px literal is shape.js `radiusCard` — the design system emits
        // no CSS var for radius tokens (probed live), so the token VALUE is
        // cited by name; change shape.js and this line together.
        // Replaces the old flat `padding-top: var(--shell-top)` offset —
        // margin, not padding, because the sheet floats below the header
        // like the pane floats below the navbar.
        // THE AMBIENT SKY (DESIGN-WL-AMBIENT-SKIN-BACKDROP-1,
        // HOME_AMBIENT_SKIN_SPEC §2). Two rules, both keyed on the skin stamp
        // this file writes — components never read the skin (the kit's skin
        // contract), so the shell does it here, once:
        //  1. AmbientBackdrop is display none by itself; the ambient skin
        //     shows it. No stamp / solid = no sky; the layer's own off
        //     switches (`hide`, inline) still win over this rule.
        //  2. Home route: the router content host, the home page and
        //     MainColumn all paint the ground chain `var(--stage-bg,
        //     var(--color-stage))` OVER the sky. Resolving the chain to
        //     transparent at the VARIABLE level lets it through; the shell
        //     root keeps painting the stage underneath, and app routes keep
        //     their sheet (their own --stage-bg rule below applies there).
        'html[data-skin="ambient"] [data-key="smblsapp"] > [data-key="AmbientBackdrop"]{display:block}' +
        // The clouds' drift stands still under the app's own Reduced motion
        // choice (<html data-motion="reduced">), as it does under the OS query
        // (the layers' own @reducedMotion block).
        'html[data-motion="reduced"] [data-key="AmbientBackdrop"] > [data-key="SkyNear"],html[data-motion="reduced"] [data-key="AmbientBackdrop"] > [data-key="SkyFar"]{animation:none}' +
        // THE GROUND (DESIGN-WL-HOME-SKY-TOP-GROUND-1, components/AmbientGround.js).
        // Shown for the ambient skin on the HOME route only (no stamp, the
        // solid skin, any other route = no ground; its own off switches —
        // the sky's — still win, inline). At rest Rise sits one band below
        // the edge (translateY(100%)); it climbs to 0 over the LAST 40vh of
        // the home scroll. Scroll-driven where the browser has it: the home
        // scroller ([data-home-board], MainColumn) names its block timeline,
        // the shell root scopes the name so the fixed ground (a sibling
        // subtree) can read it. `max(0px, …)`: a scroll range shorter than
        // the stretch rises over the whole range, so the ground is still
        // hidden at the top; a home with no scroll has an inactive timeline
        // and keeps the base (down) — functions/ambientBackdrop.js
        // ambientGroundProgress is the same rule in numbers. Reduced motion:
        // a step at the end, no motion. Transform only.
        'html[data-skin="ambient"][data-shell-route="home"] [data-key="smblsapp"] > [data-key="AmbientGround"]{display:block}' +
        '@keyframes wsGroundRise{from{transform:translateY(100%)}to{transform:translateY(0)}}' +
        // THE TOP SKY LEAVES WITH THE PAGE TOP: on the home the mirrored frame
        // slides up 1:1 with the scroll over one band (the art moves with the
        // page, so the summary never scrolls into it); the fallback fades it
        // out once the home leaves its top (data-sky-away, ambientSkyScrolled).
        '@keyframes wsSkyAway{from{transform:scaleY(-1)}to{transform:translateY(calc(min(66.667vw, 60vh) * -1)) scaleY(-1)}}' +
        '@supports (animation-timeline:scroll()){' +
        'html[data-shell-route="home"] [data-key="smblsapp"]{timeline-scope:--wsHomeScroll}' +
        'html[data-shell-route="home"] [data-home-board]{scroll-timeline:--wsHomeScroll block}' +
        'html[data-shell-route="home"] [data-key="smblsapp"] > [data-key="AmbientBackdrop"][data-sky-layout="top"]{animation:wsSkyAway linear both;animation-timeline:--wsHomeScroll;animation-range:0 min(66.667vw, 60vh)}' +
        '[data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{animation:wsGroundRise linear both;animation-timeline:--wsHomeScroll;animation-range:max(0px, calc(100% - 40vh)) 100%}' +
        'html[data-motion="reduced"] [data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{animation-timing-function:step-end}' +
        '@media (prefers-reduced-motion:reduce){[data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{animation-timing-function:step-end}}' +
        '}' +
        // No scroll-driven animations: the home's end sentinel toggles
        // data-ground (ambientGroundWatch) and Rise eases up; reduced motion
        // swaps it with no transition.
        '@supports not (animation-timeline:scroll()){' +
        '[data-key="smblsapp"] > [data-key="AmbientBackdrop"][data-sky-layout="top"]{transition:opacity .48s ease-out}' +
        'html[data-shell-route="home"] [data-key="smblsapp"] > [data-key="AmbientBackdrop"][data-sky-layout="top"][data-sky-away="true"]{opacity:0}' +
        'html[data-motion="reduced"] [data-key="smblsapp"] > [data-key="AmbientBackdrop"]{transition:none}' +
        '@media (prefers-reduced-motion:reduce){[data-key="smblsapp"] > [data-key="AmbientBackdrop"]{transition:none}}' +
        '[data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{transition:transform .48s ease-out}' +
        '[data-key="smblsapp"] > [data-key="AmbientGround"][data-ground="in"] > [data-key="Rise"]{transform:translateY(0)}' +
        'html[data-motion="reduced"] [data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{transition:none}' +
        '@media (prefers-reduced-motion:reduce){[data-key="smblsapp"] > [data-key="AmbientGround"] > [data-key="Rise"]{transition:none}}' +
        '}' +
        // THE LOADER (DESIGN-WL-BOOT-LOADER-MOTION-1) — MainLoader's motion,
        // the SAME numbers as the pre-bundle overlay in index.html (pinned by
        // __tests__/bootLoaderRange.test.js). data-stage is the real
        // milestone (1 session, 2 workspace, 3 ready): the fill creeps toward
        // 35 % in the session stage, from 35 % toward 70 % in the workspace
        // stage, never reaching the next milestone early; Ready completes it. The range shows under the ambient skin only, never
        // on a tenant host; ?loader=rise (index.html stamps <html
        // data-loader>) swaps the ring for the rising mark.
        'html[data-skin="ambient"]:not([data-whitelabel]) #main-loader > [data-key="LoaderRange"]{display:block}' +
        // THE FRONT: RangeFront (one feather taller than the band, its top
        // fading in) slides up by transform; RangeArt counter-moves inside it,
        // so the art stays put under a soft, rising mist line.
        '#main-loader[data-stage="1"] > [data-key="LoaderRange"] > [data-key="RangeFront"]{animation:wsLoaderFrontSession 12s cubic-bezier(.1,.7,.2,1) forwards}' +
        '#main-loader[data-stage="1"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{animation:wsLoaderArtSession 12s cubic-bezier(.1,.7,.2,1) forwards}' +
        '#main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"]{animation:wsLoaderFrontWorkspace 24s cubic-bezier(.1,.7,.2,1) forwards}' +
        '#main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{animation:wsLoaderArtWorkspace 24s cubic-bezier(.1,.7,.2,1) forwards}' +
        '#main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"]{transform:translateY(0);animation:wsLoaderFrontReady .48s ease-out forwards}' +
        '#main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{transform:translateY(0);animation:wsLoaderArtReady .48s ease-out forwards}' +
        '@keyframes wsLoaderFrontSession{from{transform:translateY(calc(var(--ws-loader-from, 1) * 100%))}to{transform:translateY(70%)}}' +
        '@keyframes wsLoaderArtSession{from{transform:translateY(calc((55vh + 44px) * -1 * var(--ws-loader-from, 1)))}to{transform:translateY(calc((55vh + 44px) * -0.7))}}' +
        '@keyframes wsLoaderFrontWorkspace{from{transform:translateY(calc(var(--ws-loader-from, 0.65) * 100%))}to{transform:translateY(34%)}}' +
        '@keyframes wsLoaderArtWorkspace{from{transform:translateY(calc((55vh + 44px) * -1 * var(--ws-loader-from, 0.65)))}to{transform:translateY(calc((55vh + 44px) * -0.34))}}' +
        '@keyframes wsLoaderFrontReady{from{transform:translateY(calc(var(--ws-loader-from, 0.3) * 100%))}to{transform:translateY(0)}}' +
        '@keyframes wsLoaderArtReady{from{transform:translateY(calc((55vh + 44px) * -1 * var(--ws-loader-from, 0.3)))}to{transform:translateY(0)}}' +
        // "rise": the solid mark fills on the same stages and times, with a hard
        // liquid line of its own (a clip on a small solid shape).
        'html[data-loader="rise"] #main-loader[data-stage="1"] > [data-key="LogoLoader_1"]{animation:wsLoaderFillSession 12s cubic-bezier(.1,.7,.2,1) forwards}' +
        'html[data-loader="rise"] #main-loader[data-stage="2"] > [data-key="LogoLoader_1"]{animation:wsLoaderFillWorkspace 24s cubic-bezier(.1,.7,.2,1) forwards}' +
        'html[data-loader="rise"] #main-loader[data-stage="3"] > [data-key="LogoLoader_1"]{clip-path:inset(0 0 0 0);animation:wsLoaderFillReady .48s ease-out forwards}' +
        '@keyframes wsLoaderFillSession{from{clip-path:inset(calc(var(--ws-loader-from, 1) * 100%) 0 0 0)}to{clip-path:inset(70% 0 0 0)}}' +
        '@keyframes wsLoaderFillWorkspace{from{clip-path:inset(calc(var(--ws-loader-from, 0.65) * 100%) 0 0 0)}to{clip-path:inset(34% 0 0 0)}}' +
        '@keyframes wsLoaderFillReady{from{clip-path:inset(calc(var(--ws-loader-from, 0.3) * 100%) 0 0 0)}to{clip-path:inset(0 0 0 0)}}' +
        // THE MARK IS THE PROGRESS (Nika 2026-09-29 "bring back logo linear
        // loading, remove circle around it"): the mark rests faint and its
        // outline draws along the 242-unit path on the milestones — the same
        // numbers as the overlay's draw — then turns solid at Ready. The faint
        // fill breathes only while a milestone waits. No ring, no disc.
        '#main-loader > [data-key="LogoLoader"] svg path{fill-opacity:.14;stroke:currentColor;stroke-width:.7;stroke-linejoin:round;stroke-dasharray:242;stroke-dashoffset:242}' +
        '#main-loader[data-stage="1"] > [data-key="LogoLoader"] svg path{animation:wsLoaderDrawSession 12s cubic-bezier(.1,.7,.2,1) forwards,wsLoaderMarkShimmer 2.4s ease-in-out infinite}' +
        '#main-loader[data-stage="2"] > [data-key="LogoLoader"] svg path{animation:wsLoaderDrawWorkspace 24s cubic-bezier(.1,.7,.2,1) forwards,wsLoaderMarkShimmer 2.4s ease-in-out infinite}' +
        '#main-loader[data-stage="3"] > [data-key="LogoLoader"] svg path{stroke-dashoffset:0;fill-opacity:1;animation:wsLoaderDrawReady .48s ease-out forwards}' +
        '@keyframes wsLoaderDrawSession{from{stroke-dashoffset:calc(242px * var(--ws-loader-from, 1))}to{stroke-dashoffset:169.4}}' +
        '@keyframes wsLoaderDrawWorkspace{from{stroke-dashoffset:calc(242px * var(--ws-loader-from, 0.65))}to{stroke-dashoffset:82.28}}' +
        '@keyframes wsLoaderDrawReady{from{stroke-dashoffset:calc(242px * var(--ws-loader-from, 0.3));fill-opacity:.14}to{stroke-dashoffset:0;fill-opacity:1}}' +
        '@keyframes wsLoaderMarkShimmer{0%,100%{fill-opacity:.1}50%{fill-opacity:.24}}' +
        // "rise": nothing else moves.
        'html[data-loader="rise"] #main-loader > [data-key="LogoLoader_1"]{display:block}' +
        'html[data-loader="rise"] #main-loader > [data-key="LogoLoader"]{opacity:.2}' +
        'html[data-loader="rise"] #main-loader > [data-key="LogoLoader"] svg path{fill-opacity:1;stroke:none;animation:none}' +
        // REDUCED MOTION (the OS query and the app's own choice): no breath,
        // no ring, no creep. The fill shows the milestone actually reached.
        '@media (prefers-reduced-motion:reduce){#main-loader > [data-key="LoaderRange"] > [data-key="RangeFront"],#main-loader > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"],#main-loader > [data-key="LogoLoader_1"],#main-loader > [data-key="LogoLoader"] svg path{animation:none}#main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"]{transform:translateY(65%)}#main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{transform:translateY(calc((55vh + 44px) * -0.65))}#main-loader[data-stage="2"] > [data-key="LogoLoader_1"]{clip-path:inset(65% 0 0 0)}#main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"],#main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{transform:translateY(0)}#main-loader[data-stage="3"] > [data-key="LogoLoader_1"]{clip-path:inset(0 0 0 0)}#main-loader[data-stage="2"] > [data-key="LogoLoader"] svg path{stroke-dashoffset:157.3}#main-loader[data-stage="3"] > [data-key="LogoLoader"] svg path{stroke-dashoffset:0;fill-opacity:1}}' +
        'html[data-motion="reduced"] #main-loader > [data-key="LoaderRange"] > [data-key="RangeFront"],html[data-motion="reduced"] #main-loader > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"],html[data-motion="reduced"] #main-loader > [data-key="LogoLoader_1"],html[data-motion="reduced"] #main-loader > [data-key="LogoLoader"] svg path{animation:none}' +
        'html[data-motion="reduced"] #main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"]{transform:translateY(65%)}' +
        'html[data-motion="reduced"] #main-loader[data-stage="2"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{transform:translateY(calc((55vh + 44px) * -0.65))}' +
        'html[data-motion="reduced"] #main-loader[data-stage="2"] > [data-key="LogoLoader_1"]{clip-path:inset(65% 0 0 0)}' +
        'html[data-motion="reduced"] #main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"],html[data-motion="reduced"] #main-loader[data-stage="3"] > [data-key="LoaderRange"] > [data-key="RangeFront"] > [data-key="RangeArt"]{transform:translateY(0)}' +
        'html[data-motion="reduced"] #main-loader[data-stage="3"] > [data-key="LogoLoader_1"]{clip-path:inset(0 0 0 0)}' +
        'html[data-motion="reduced"] #main-loader[data-stage="2"] > [data-key="LogoLoader"] svg path{stroke-dashoffset:157.3}' +
        'html[data-motion="reduced"] #main-loader[data-stage="3"] > [data-key="LogoLoader"] svg path{stroke-dashoffset:0;fill-opacity:1}' +
        'html[data-skin="ambient"][data-shell-route="home"] [data-key="smblsapp"] > [data-key="content"]{--stage-bg:transparent}' +
        // MODULE CANVAS pages (manifest `canvasRoutes`): full-bleed like the
        // shell canvases — no sheet, the canvas runs edge to edge and under
        // the composer — but the module's own page head (title, tabs) stays
        // in flow, so the mount starts it below the floating header band.
        // THE AppDialog FRAME (shared AppDialog insets its root by
        // `--app-dialog-inset`) is this mount's band: below the floating
        // header (else a side sheet starts under the org name and the
        // avatar; persona audit 2026-10-25), past the shell root's padding
        // (a side navbar, the immersive module rail) and a docked
        // assistant's column. That is the app route's band sum below
        // WITHOUT its X2 sheet margin: the canvas has no sheet. The mount
        // takes no position, as on the app route, so a dialog resolves
        // against the viewport whatever the page root sets and a positioned
        // root cannot take the inset twice. Measured 2026-10-28 on dev
        // 60fb7d140 with the old `var(--slot-top) 0 0`: the frame and its
        // veil ran from x 0 over a 240px module rail and an 82px left
        // navbar, and on a phone to the viewport foot under the Dock
        // (__tests__/appDialogShellInset.test.js).
        'html[data-shell-route="canvas"][data-module-canvas="true"] [data-key="smblsapp"] > [data-key="content"]{padding-top:var(--slot-top);box-sizing:border-box;height:100dvh;min-height:0;' +
        'position:static;' +
        '--app-band-left:calc(max(var(--module-rail-clear, 0px), var(--nav-inset-left)) + var(--assistant-inset-left));' +
        '--app-band-right:calc(var(--nav-inset-right) + var(--assistant-inset-right));' +
        '--app-dialog-inset:var(--slot-top) var(--app-band-right) var(--nav-inset-bottom) var(--app-band-left)}' +
        // The canvas runs under the composer, but a dialog's foot (Save /
        // Cancel) clears it: the same live --slot-bottom the app route's
        // sheet takes.
        'html[data-shell-route="canvas"][data-module-canvas="true"][data-prompt="shown"] [data-key="smblsapp"] > [data-key="content"]{--app-dialog-inset:var(--slot-top) var(--app-band-right) var(--slot-bottom) var(--app-band-left)}' +
        // Phone: full-bleed sides and the foot above the fixed Dock
        // (--slot-bottom is the dock clearance there, see
        // functions/phoneNavigation.js). Each rule follows its desktop twin:
        // equal specificity, so the order decides.
        '@media (max-width:768px){html[data-shell-route="canvas"][data-module-canvas="true"] [data-key="smblsapp"] > [data-key="content"]{--app-dialog-inset:var(--slot-top) 0 var(--slot-bottom)}}' +
        '@media (max-width:768px){html[data-shell-route="canvas"][data-module-canvas="true"][data-prompt="shown"] [data-key="smblsapp"] > [data-key="content"]{--app-dialog-inset:var(--slot-top) 0 var(--slot-bottom)}}' +
        'html[data-shell-route="app"] [data-key="smblsapp"] > [data-key="content"]{' +
        // The paint channel is --stage-bg, NOT a `background` declaration:
        // DOMQL emits `background: var(--stage-bg, var(--color-stage))`
        // INLINE on this element, and inline beats any sheet rule (measured
        // live 2026-09-02 — a sheet `background` left the mount transparent
        // while its geometry landed). Loading the content-surface pair into
        // --stage-bg rides the inline declaration, and descendants that
        // dissolve into var(--stage-bg) paint the SAME sheet color, which
        // is the pane's seamless look.
        // LIGHT here, DARK in the scheme rule right after this one — never
        // the CSS function that picks by colour scheme (2026-09-30, Nika
        // "background missing on other Windows devices"): Chrome / Edge
        // before 123 do not know it, so `--stage-bg` held an unknown value,
        // the sheet's `var(--stage-bg, …)` paint was invalid at computed-value
        // time and the page ground was TRANSPARENT. The scheme key is the
        // root's `data-theme` — what the framework stamps for light, dark
        // and auto, and what every DOMQL '@dark' block compiles to
        // (__tests__/noLightDarkInShellStyles.test.js).
        // THE FRAME PAIR, gray16 light / gray2 dark, is written in THREE
        // places: here, shared/components/AppMain.js (the sheet) and
        // components/PageHeader.js (the sticky bar). The three move
        // together — one changed alone steps the bar, the sheet and the
        // frame apart. (The brand has no `frame` colour pair yet; one token
        // would make the three one name.)
        '--stage-bg:var(--color-gray16);' +
        'border-radius:17px;' +
        'box-shadow:var(--shadow-float);' +
        'margin:var(--slot-top) var(--spacing-X2) var(--nav-gap);' +
        // width:auto — on an installed app route the app's page element IS
        // this mount, and the shared AppPage's atomic `width: 100%` plus the
        // side margins ran the sheet 2 × X2 past its grid column (content
        // 6..1926 in a 1920 px window, the right gutter and corners cut off;
        // BESSI QA 2026-10-28). Auto stretches the grid item inside its
        // margins; this rule outranks the atomic class as it does for margin.
        'width:auto;' +
        'height:calc(100dvh - var(--slot-top) - var(--nav-gap));' +
        // The AppDialog frame = this sheet's band. The sheet keeps NO position
        // (position:static outranks a page root's own: on an installed app
        // route the page element IS this mount, and the shared
        // AppCollectionPage root is `relative` — a dialog then resolved
        // against the sheet and took the inset a second time, its frame
        // ending 55px above the sheet's foot; BESSI QA 2026-10-28, measured
        // on dev kestrel-assist). With no position a dialog in it resolves
        // against the viewport (see the module canvas rule above), where the
        // band's sides are the shell root's padding (the navbar's place and
        // the module rail — the smblsapp padding rules below) plus a docked
        // assistant's column plus this sheet's X2 margin: the PageSlot band's
        // own sum, so the frame never runs over a left navbar or a docked
        // drawer. Top and bottom are the margins' own vars.
        'position:static;' +
        '--app-band-left:calc(max(var(--module-rail-clear, 0px), var(--nav-inset-left)) + var(--assistant-inset-left) + var(--spacing-X2));' +
        '--app-band-right:calc(var(--nav-inset-right) + var(--assistant-inset-right) + var(--spacing-X2));' +
        '--app-dialog-inset:var(--slot-top) var(--app-band-right) var(--nav-gap) var(--app-band-left);' +
        // min-height:0 — the mount carries an atomic `min-height:100%`
        // (._mnh-100, from the shell styles' fill contract) that OUTRANKS
        // the `:where` reset below and beats this rule's height: measured
        // 2026-09-02 on /w/natali/…/profile, /settings and /notes the sheet
        // was 907px (100dvh) from y 53 — it ran under the composer with no
        // bottom gap or round, while the PageSlot pane ended at 852 (per
        // Nika "profile page should match all other pageslots"). The band
        // below is the slot's band; the sheet must not exceed it.
        'min-height:0;' +
        // The `app-surface` size-query container — the same one the PageSlot
        // pane declares (canvas/components/PageSlot.js). Brand media
        // `appMobile` is `@container app-surface (max-width: 768px)`, so
        // without a container of that name on this mount every `@appMobile`
        // block — shell AND module — was inert on a dedicated route and only
        // applied inside the slot. Scoped to 'app'-kind routes: only the app
        // surface answers to `app-surface`; 'home' / 'canvas' / 'auth' are not
        // one. `container-type` does NOT make this box the containing block
        // of a fixed descendant, nor a stacking context: the spec took layout
        // containment out of `container-type`, and Chrome 154 and WebKit 26.4
        // keep a fixed child on the viewport (measured 2026-10-05,
        // DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1). So this scroller stays clear
        // of the iOS rule (a scroller that is a stacking context clips every
        // fixed layer inside it); workspace/__tests__/iosFixedLayerScrollers
        // fails if a stacking trigger joins it.
        'container-type:inline-size;container-name:app-surface;' +
        'overflow:auto}' +
        // The DARK half of the frame pair: the same selector under the dark
        // scheme — one more attribute, so it outranks the rule above by
        // specificity, whatever the order.
        'html[data-theme="dark"][data-shell-route="app"] [data-key="smblsapp"] > [data-key="content"]{--stage-bg:var(--color-gray2)}' +
        // Phone: mirror the pane's iOS-sheet posture — full-bleed sides,
        // only the top corners round, no float shadow.
        '@media (max-width:768px){html[data-shell-route="app"] [data-key="smblsapp"] > [data-key="content"]{' +
        'margin:var(--slot-top) 0 0;border-radius:17px 17px 0 0;box-shadow:none;--app-dialog-inset:var(--slot-top) 0 0;' +
        'min-height:0;height:calc(100dvh - var(--slot-top))}}' +
        // Bottom counterpart — standalone app pages (/calendar, /documents,
        // /tickets, …) had NO clearance from the fixed AI composer bar at the
        // viewport bottom, so their last row/action ran underneath it
        // (2026-08-01 per Nika). --slot-bottom is already the exact right
        // value — the PageSlot's own bottom inset — and it already folds in
        // BOTH the live composer height (--prompt-clear, ResizeObserver-
        // maintained above) AND env(safe-area-inset-bottom) (via the
        // composer's own margin-bottom rule below), so this reuses that one
        // measurement rather than re-deriving a second one. Gated on
        // data-prompt="shown" (written by _syncShellRoute via the SAME
        // _composerVisible predicate CanvasPromptTextarea's `hide` uses) for
        // two reasons: (a) a route where the composer is hidden (e.g. a
        // non-member session) needs no reserved space, and (b) it neutralizes
        // _trackClearVar's deliberate "retain the last measurement" behavior
        // — the composer's ResizeObserver stops firing once its node
        // unmounts, so --prompt-clear would otherwise keep reserving space
        // for a composer that's no longer on screen. Same direct-child scope
        // as the padding-top rule above, for the same duplicate-`content`-key
        // reason.
        // With the composer on screen the sheet's bottom edge tracks the
        // SAME live measurement the PageSlot's does (--slot-bottom folds
        // the composer height + safe area) — margin now, not padding, per
        // the sheet treatment above. Still gated on data-prompt="shown"
        // for the original reason: --prompt-clear deliberately retains its
        // last value after the composer unmounts.
        'html[data-shell-route="app"][data-prompt="shown"] [data-key="smblsapp"] > [data-key="content"]{' +
        'margin-bottom:var(--slot-bottom);' +
        '--app-dialog-inset:var(--slot-top) var(--app-band-right) var(--slot-bottom) var(--app-band-left);' +
        'min-height:0;height:calc(100dvh - var(--slot-top) - var(--slot-bottom))}' +
        // Phone: the sheet is full-bleed (the phone rule above), so the
        // dialog frame keeps no side inset there either.
        '@media (max-width:768px){html[data-shell-route="app"][data-prompt="shown"] [data-key="smblsapp"] > [data-key="content"]{--app-dialog-inset:var(--slot-top) 0 var(--slot-bottom)}}' +
        // THE MAIN LAYER over a page (components/AssistantMainLayer.js,
        // AI_ASSISTANT_SPEC §12.2): while it holds the main area, the page
        // sheet under it leaves the eye, the pointer, the Tab order and
        // assistive technology — `visibility`, never `display`: the page keeps
        // its box, its scroll and its state, and is back the moment the layer
        // closes. `content-visibility: hidden` too: a child that sets its own
        // `visibility: visible` (a page's dialog, a dropdown) would otherwise
        // still take clicks and focus (design-d2's review, D2); the scroll
        // offset is kept (it reads 0 while hidden and returns on close — no
        // page stores its scroll from a scroll event). Keyed on the layer's
        // own `data-open` stamp (no second flag).
        '[data-key="smblsapp"]:has(> [data-key="AssistantMainLayer"][data-open="true"]) > [data-key="content"]{visibility:hidden;content-visibility:hidden}' +
        // AI-BAR-OVERLAP-1 (tickets/sonnet.md, 2026-08-14) — Home
        // (data-shell-route="home") was deliberately left OUT of the rule
        // above ("self-offsets via MainColumn padding" — see the route-kind
        // comment near _syncShellRoute) and instead relies on MainColumn's
        // own static `padding: 'D1 C G C'` (pages/main.js) to clear the
        // fixed composer bar. Measured live: the G token's bottom clearance
        // fell short of the bar's actual rendered height by ~23px, so the
        // last widget row (e.g. Team timezones) sat partly underneath the
        // bar with no further scroll available to reveal it — the same
        // "static guess vs. live composer height" failure --slot-bottom was
        // built to solve for every OTHER route. Rather than swap in a
        // bigger static letter token (still a guess, and the next letter up
        // the φ-scale is ~493px — wildly disproportionate for a ~40px
        // composer pill), reuse the SAME already-correct live measurement:
        // max() with the existing G token as a floor (never shrinks below
        // the original design-token minimum) and --slot-bottom as the
        // provably-sufficient ceiling (it IS the bar's live rendered height
        // + --nav-gap, ResizeObserver-maintained — never a magic number).
        // Same [data-key="content"] [data-key="MainColumn"] compound
        // selector the mobileL top-inset override (pages/main.js) already
        // uses to out-specify MainColumn's single-class atomic padding rule
        // — no !important needed. Gated on data-prompt="shown" for the same
        // two reasons as the rule above (no composer on screen → nothing to
        // clear; --prompt-clear retains its last value after unmount).
        'html[data-shell-route="home"][data-prompt="shown"] [data-key="content"] [data-key="MainColumn"]' +
        '{padding-bottom:max(var(--spacing-G),var(--slot-bottom))}' +
        // Edit mode on a phone: the customize panel is a bottom sheet over the
        // lower half (pages/main.js CustomizeDrawer `@mobileL`, height 50dvh),
        // so the board takes that much room below its last card — every card
        // can scroll up above the sheet and its size picker, remove button and
        // drag stay in reach with the sheet open. Same selector + the
        // `data-customizing` stamp (pages/main.js MainColumn), after the rule
        // above, so it wins in edit mode; a tucked sheet keeps the normal room.
        '@media (max-width:768px){html[data-shell-route="home"] [data-key="content"] [data-key="MainColumn"][data-customizing="open"]' +
        '{padding-bottom:calc(50dvh + var(--spacing-D1))}}' +
        // (Vertical-rail lane rules removed 2026-08-01 — the tablet vertical
        // HeaderLeft mode is retired in AppHeader.js per Nika "fix navbar
        // consistency"; data-header-left-rail is never set anymore, so page
        // sidebars/PageSlot keep their base top-offset positioning, which
        // already clears the now-horizontal header via --shell-top.)
        // Once the mobile rail contracts to origin/main's logo-only navbar it
        // no longer owns a side lane. Restore PageSlot's normal symmetric X2
        // viewport gutters instead of leaving a blank rail-width column.
        'html[data-header-left-compact="true"] [data-key="PageSlot"]' +
        '{left:var(--spacing-X2)}' +
        // Messenger (chat drawer) open → shift the left page rails right so they
        // clear the floating chat list (MessengerList: left X2, width G1). Driven
        // by <html data-chat-drawer> (synced in _syncShellRoute) because the page
        // rails are page-root-mounted and freeze reactive layout props in v3.14.
        // (2026-06-18 per Nika "internal page sidebars should offset so they don't
        // overlap the messenger sidebar".) A rail on the page-sidebar rule that
        // sits on the RIGHT (beside a left navbar — shared
        // functions/navbarLayout.js `sidebarSide`) shares no edge with the list.
        'html[data-chat-drawer="open"] [data-key="TicketsSidebar"]:not([data-sidebar-side="right"]),' +
        'html[data-chat-drawer="open"] [data-key="InfraSidebar"]:not([data-sidebar-side="right"]),' +
        'html[data-chat-drawer="open"] [data-key="CanvasViewRail"],' +
        'html[data-chat-drawer="open"] [data-key="FilesSidebar"]:not([data-sidebar-side="right"]),' +
        'html[data-chat-drawer="open"] [data-key="ProjectCanvasSidebar"]:not([data-sidebar-side="right"])' +
        // (+ the navbar rail's inset when the person put it on the left — 0px
        // everywhere else; "THE NAVBAR'S PLACE" below)
        '{left:calc(var(--spacing-X2) * 2 + var(--spacing-G1) + var(--nav-inset-left, 0px)) !important;transition:left .2s ease}' +
        // The PageSlot modal sits flush beside the messenger sidebar (one X2
        // gutter, matching its own right inset), anchored to the sidebar's REAL
        // rendered right edge published as `--chat-sidebar-right` by
        // _syncShellRoute. We can't reconstruct that edge from tokens here: the
        // sidebar (MessengerList: left X2, width G1) inherits a smaller font-size
        // than the shell root, so its em-based `width:G1` renders ~63px narrower
        // than G1 evaluated in the PageSlot's own font context — a token calc
        // overshoots (~69px gap). The var() fallback (X2 + G1, the naive
        // root-context edge) degrades safely if the measure hasn't run yet.
        // NO `transition:left`: the slot rewrites `left` on its own WAAPI-open +
        // resize-observer cadence, so a CSS transition on left never settles (it
        // stays perpetually `running`, pinning left at the start value and
        // defeating the offset — transitions outrank !important; verified
        // 2026-07-17). The slot's own open animation covers the motion.
        // (2026-07-17 per Nika "resize pageslot when chat sidebar is open" +
        // "fix gap between message and pageslot — it's too big".)
        'html[data-chat-drawer="open"] [data-key="PageSlot"]' +
        '{left:calc(var(--chat-sidebar-right, calc(var(--spacing-X2) + var(--spacing-G1))) + var(--spacing-X2)) !important}' +
        // ContentPage rail — the active section row. The row publishes its
        // state on `aria-current` (a component style prop cannot: these
        // `childrenAs: 'state'` rows freeze every style channel at create —
        // see components/ContentPage.js for the four measured dead ends), and
        // this sheet paints it, the same reacts-in-CSS idiom as the
        // `html[data-chat-drawer]` rules above. Sheet rules outrank the atomic
        // classes; a component-level `&[aria-current]` block did not.
        '[data-key="SectionNav"] > [aria-current="true"]' +
        '{background:var(--color-selected);color:var(--color-title)}' +
        // SECURITY/UX (platform#2278) — hide the LoginWindow X on the full-page
        // auth screens (login / signin / signup / reset-password →
        // data-shell-route "auth"). The brand ModalHeader's Close can't be
        // hidden via a prop override through `extends: 'ModalWindow'` (the merge
        // doesn't reach the deeply-nested Close), so gate it with this
        // route-scoped rule. Modals render on non-auth routes (e.g.
        // /marketplace = "app"), so their X still shows.
        'html[data-shell-route="auth"] [data-key="LoginWindow"] [data-key="Close"]{display:none!important}' +
        // ── Dockable AI assistant sidebar ───────────────────────────────────
        // The drawer is position:fixed, but the CONTENT must shrink (never be
        // overlaid) when it's docked left/right. The shell root
        // ([data-key="smblsapp"]) is a single-cell grid whose ONLY in-flow
        // child is [data-key="content"]; reserving a fixed-width column there
        // narrows content's grid cell — and since every page-root inside is
        // width:100% (min-width:0), both normal AND full-bleed canvas pages
        // reflow into the remaining width with no horizontal scroll (verified
        // live). The fixed drawer then sits exactly over the reserved column.
        // Everything keys off <html data-assistant-dock> (written in
        // _syncShellRoute) so no reactive layout-prop factories are needed
        // (they freeze at mount in v3.14). --assistant-width is published live
        // by _syncShellRoute (clamped 320..50vw) and shared by the reserved
        // column AND the drawer so they never diverge.
        ':root{--assistant-width:640px;--assistant-inset-right:0px;--assistant-inset-left:0px}' +
        // Smooth shrink/grow of the content column + the header inset that
        // rides along with it. PageSlot + composer intentionally snap (PageSlot
        // rewrites its own `left` on open — a CSS transition there never
        // settles, same reason as the chat-drawer rule above; the composer
        // keeps its focus-glow transition instead).
        '[data-key="smblsapp"]{transition:grid-template-columns .22s cubic-bezier(0.4,0,0.2,1)}' +
        '[data-key="AppHeader"]{transition:left .22s cubic-bezier(0.4,0,0.2,1),right .22s cubic-bezier(0.4,0,0.2,1)}' +
        // Right dock — reserve the column on the right; content stays in col 1.
        // Clamp the reserved track to min(var,50vw) so it can NEVER exceed the
        // drawer's own max-width:50vw — otherwise a stale --assistant-width (the
        // JS clamp doesn't re-fire on a bare window resize) leaves a dead band or
        // collapses the 1fr content track toward zero when the window narrows.
        // The content track is minmax(0,1fr), never bare 1fr (= minmax(auto,
        // 1fr)): a horizontal rail's min-content contribution would inflate a
        // bare track past the viewport, same defect as the shell base column
        // (WS-APPSHELL-1FR-COLUMN-BLOWS-OUT-ON-RAIL-CONTENT-1).
        'html[data-assistant-dock="right"] [data-key="smblsapp"]' +
        '{grid-template-columns:minmax(0,1fr) min(var(--assistant-width),50vw)}' +
        // Left dock — reserve the column on the left; push content into col 2.
        'html[data-assistant-dock="left"] [data-key="smblsapp"]' +
        '{grid-template-columns:min(var(--assistant-width),50vw) minmax(0,1fr)}' +
        'html[data-assistant-dock="left"] [data-key="smblsapp"] > [data-key="content"]{grid-column:2}' +
        // Drawer edge + width, flipped per dock. These live here (not on the
        // component) so the side can switch reactively without a frozen
        // layout-prop factory, and so width can reference the shared
        // --assistant-width var (a raw var() on `width` empty-rules in the
        // atomic compiler). THE DOCKED SIDEBAR (Nika 2026-09-26, "make
        // assistant looking as it was back as sidebar with no rounds" — this
        // reverses the floating panel of Q7 A): flush to the screen edge,
        // exactly over the reserved column, square. The divider and the depth
        // are the component's `overlay` elevation (its 1px ring is the edge
        // line), not a hairline border and a raw shadow per side.
        'html[data-assistant-dock="right"] [data-key="AssistantDrawer"]' +
        '{right:0;left:auto;width:var(--assistant-width);min-width:320px;max-width:50vw}' +
        'html[data-assistant-dock="left"] [data-key="AssistantDrawer"]' +
        '{left:0;right:auto;width:var(--assistant-width);min-width:320px;max-width:50vw}' +
        // ——— OUT OF CREDITS TAKES THE ASSISTANT'S OWN BOX ———
        // 2026-09-04 per Nika "move 'Out of credits — metered actions paused'
        // section above ai chat zIndex on top of chatsidebar, make it
        // beautiful and same size as chatbot to perfectly override it (just in
        // ui)".
        //
        // When the assistant is OUT OF CREDITS every metered action in it is
        // already refused, so the drawer is a dead surface — the notice covers
        // it exactly rather than floating over the composer as a pill and
        // leaving a chat you cannot use visible underneath.
        //
        // EVERY WORD OF THAT depends on the credit state, and on the pill
        // actually being on screen. Both are conditions this sheet cannot
        // derive, so `CreditPillDock` publishes them as `data-credit-dock`
        // ('cover' | 'pill' | absent — components/CreditBanner.js, off
        // functions/creditGuard.js's `creditDockMode`) and the cover keys on
        // THAT, not on the mere presence of the wrapper. Keying it on the dock
        // attribute alone — which is what shipped — made an always-rendered,
        // EMPTY wrapper the assistant's whole box on every workspace that is
        // not out of credits: an opaque z-index-10001 panel over a live AI
        // thread, answer + source chips + confidence pill all invisible
        // (WS-ASSISTANT-DRAWER-COVERED-BY-EMPTY-CREDIT-DOCK-1, found on
        // staging 2026-09-09 with `_creditGuard` total 3977 on an enterprise
        // tier). The pill's own `hide:` writes `display:none!important`
        // inline, so a hidden pill takes its dock out of the paint and the
        // hit test regardless of this sheet — the attribute gate is the
        // second, declarative half of the same statement.
        //
        // The geometry stays DECLARED HERE, beside the drawer's own rules and
        // off the SAME `--assistant-width`, so the cover can never drift from
        // the thing it covers when the user drags the resize handle.
        // `="right"`/`="left"` only match while the assistant is OPEN (the
        // attribute reads `closed` otherwise), so the pill keeps its centered
        // above-composer slot everywhere else.
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="cover"],' +
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        // The docked drawer's own box: the full viewport height, the shared
        // width var, square.
        '{top:0;bottom:0;height:100dvh;transform:none;' +
        'width:var(--assistant-width);min-width:320px;max-width:50vw;' +
        // Above the drawer (10000) by one, not by a round number — the only
        // thing it has to clear is the surface it covers.
        'z-index:10001;' +
        // Opaque, or the dead chat reads through the notice. Same `document`
        // ground and the same elevation the drawer paints (the side rules
        // below), so the panel that appears is visually the drawer, with the
        // notice on it.
        'background:var(--color-document);' +
        'align-items:center;justify-content:center;' +
        'padding:var(--spacing-B)}' +
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        '{right:0;left:auto;box-shadow:var(--shadow-overlay)}' +
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        '{left:0;right:auto;box-shadow:var(--shadow-overlay)}' +
        // The pill inside stops being a pill: centered, full-width of the
        // panel's padding box and free to grow, so the copy sets on two
        // comfortable lines instead of one long strip. Its own glass surface
        // is dropped — it is the only thing on an opaque panel now, and a
        // card-on-card would read as a dialog that lost its backdrop.
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="cover"] > *,' +
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="cover"] > *' +
        '{width:100%;max-width:var(--spacing-H);flex-direction:column;' +
        'align-items:center;text-align:center;gap:var(--spacing-A);' +
        // (+ the -webkit- twin: the atomic engine emits both forms for the
        // glass this cancels, and Safari 17 reads only the prefixed one.)
        'background:transparent;box-shadow:none;backdrop-filter:none;-webkit-backdrop-filter:none}' +
        // LOW / CRITICAL while docked — the assistant still works, so the
        // warning stays the compact pill and must not sit on top of a live
        // thread. It keeps Nika's 2026-09-02 anchor ("above AI textarea") by
        // riding the SAME shift the composer takes below: the dock is
        // viewport-centered (left:50% + translateX(-50%)), and the composer
        // re-centers into the visible content band when a drawer is docked,
        // so without this the pill would straddle the drawer's edge.
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="pill"]' +
        '{transform:translateX(calc(-50% - var(--assistant-width) / 2))}' +
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="pill"]' +
        '{transform:translateX(calc(-50% + var(--assistant-width) / 2))}' +
        // Resize handle rides the INNER edge — left edge for a right dock,
        // right edge for a left dock (its drag-direction sign is flipped in
        // the handler by reading assistantDock).
        'html[data-assistant-dock="left"] [data-key="AssistantDrawer"] [data-key="ResizeHandle"]' +
        '{left:auto;right:0}' +
        // Viewport-fixed chrome clears the drawer: the absolute AppHeader insets
        // its inner edge (its centered island re-centers on the narrower header
        // automatically), and the fixed PageSlot insets so a split-view sheet
        // never slides under the drawer.
        'html[data-assistant-dock="right"] [data-key="AppHeader"]' +
        '{right:calc(env(safe-area-inset-right) + var(--assistant-width))}' +
        'html[data-assistant-dock="left"] [data-key="AppHeader"]' +
        '{left:calc(env(safe-area-inset-left) + var(--assistant-width))}' +
        'html[data-assistant-dock="right"] [data-key="PageSlot"]' +
        '{right:calc(var(--spacing-X2) + var(--assistant-width))}' +
        'html[data-assistant-dock="left"] [data-key="PageSlot"]' +
        '{left:calc(var(--spacing-X2) + var(--assistant-width))}' +
        // Recenter the bottom composer (fixed, left:50% + translateX(-50%)) in
        // the visible content band by shifting it half the drawer width toward
        // the open side. The mobile @media block below overrides this with
        // transform:none on small screens.
        'html[data-assistant-dock="right"] [data-key="CanvasPromptTextarea"]' +
        '{transform:translateX(calc(-50% - var(--assistant-width) / 2))}' +
        'html[data-assistant-dock="left"] [data-key="CanvasPromptTextarea"]' +
        '{transform:translateX(calc(-50% + var(--assistant-width) / 2))}' +
        // ─────────────────────────────────────────────────────────────────────
        // The global bottom prompt inherits desktop translate/min-width inline
        // from CanvasPromptTextarea. On mobile/tablet, force viewport insets so
        // the prompt cannot shift left and clip across routes. Insets are the
        // chrome-island X2 family — the same edge gap the AppHeader padding,
        // HeaderRight's fixed right/top, PageSlot's sides and the composer's
        // own desktop `bottom: 'X2'` use, with HeaderRight's exact
        // env(safe-area) form on the sides — so the composer's edges line up
        // with the vertical rail and the top pills instead of floating an
        // --spacing-A off every edge (iPad, 2026-08-01 per Nika "canvas
        // prompt textarea not consistent with others"). Bottom stays bare X2:
        // the margin-bottom safe-area rule below already lifts it off the
        // home indicator, and it keeps the OnlineMembersDock baseline.
        // `--composer-bottom` lets the phone dock lift it above the dock
        // (functions/phoneNavigation.js phoneNavigationStyles) without a
        // second `!important` competing with this one.
        '@media ' +
        media.tabletS +
        '{' +
        '[data-key="CanvasPromptTextarea"]{' +
        'position:fixed!important;' +
        // max(…, --nav-inset-*): beside a navbar rail or above a bottom bar
        // ("THE NAVBAR'S PLACE" below) the edge is the bar's, 0px elsewhere.
        'left:calc(env(safe-area-inset-left) + max(var(--spacing-X2), var(--nav-inset-left)))!important;' +
        'right:calc(env(safe-area-inset-right) + max(var(--spacing-X2), var(--nav-inset-right)))!important;' +
        'bottom:var(--composer-bottom,max(var(--spacing-X2), var(--nav-inset-bottom)))!important;' +
        'width:auto!important;max-width:none!important;' +
        'min-width:0!important;transform:none!important}' +
        '[data-key="CanvasPromptTextarea"] textarea{' +
        'width:100%!important;max-width:100%!important;min-width:0!important}}' +
        '@media ' +
        media.mobileL +
        '{' +
        // Docked assistant becomes a full-width OVERLAY on phones instead of
        // shrinking content — a reserved column can't coexist with a usable
        // content track on a ~360px viewport (it would crush content to a sliver,
        // and the drawer's own min-width:320px would exceed a 50vw column). Drop
        // the reservation, overlay the drawer full-width, clear the chrome insets.
        // (Later than the desktop rules in the sheet, same specificity → wins.)
        'html[data-assistant-dock="left"] [data-key="smblsapp"],' +
        'html[data-assistant-dock="right"] [data-key="smblsapp"]{grid-template-columns:minmax(0,1fr)}' +
        'html[data-assistant-dock="left"] [data-key="smblsapp"]>[data-key="content"]{grid-column:1}' +
        'html[data-assistant-dock="left"] [data-key="AssistantDrawer"],' +
        'html[data-assistant-dock="right"] [data-key="AssistantDrawer"]{width:100vw;max-width:100vw;min-width:0}' +
        'html[data-assistant-dock="left"] [data-key="AppHeader"],' +
        'html[data-assistant-dock="right"] [data-key="AppHeader"]{left:0;right:0}' +
        'html[data-assistant-dock="left"] [data-key="PageSlot"],' +
        'html[data-assistant-dock="right"] [data-key="PageSlot"]{left:var(--spacing-X2);right:var(--spacing-X2)}' +
        // The out-of-credits cover follows the drawer it covers: full width
        // here, since the drawer itself is a full-width overlay on a phone
        // (rule above). The desktop clamp (var width, min 320, max 50vw)
        // would leave a 320px strip beside an uncovered dead chat.
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="cover"],' +
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        '{width:100vw;max-width:100vw;min-width:0}' +
        // …and the LOW/CRITICAL pill drops the desktop content-band shift:
        // there is no reserved column to re-center against, and the dock's
        // own @mobileL block already pins it edge-to-edge (left/right A,
        // transform none) — an attribute-scoped rule would outrank that
        // single-class atomic media rule, so it is restated here.
        'html[data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="pill"],' +
        'html[data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="pill"]' +
        '{transform:none}' +
        // …and then every chrome-offset stands down entirely on a phone. At
        // ≤768 the PageSlot is no longer a floating island beside the chrome —
        // it is a FULL-BLEED iOS sheet (canvas/components/PageSlot.js
        // '@mobileL': left/right/bottom 0, top = safe-area + a reveal strip).
        // Nothing sits beside it to clear: the assistant drawer is a
        // full-width overlay here (rule above) and the messenger drawer is the
        // same story, so an inset would only leave a dead gutter beside a sheet
        // that is supposed to own the screen edge.
        //
        // It has to be stated here rather than in the component: these three
        // rules are attribute-scoped descendants ((0,2,1)) and the chat-drawer
        // one is `!important` — both outrank the component's single-class
        // atomic media rule, so PageSlot.js's own '@mobileL' geometry loses to
        // them on specificity every time the drawer/dock state is live.
        // `!important` only to match the chat-drawer declaration it must
        // override; the rest is plain cascade order inside this same block.
        'html[data-chat-drawer="open"] [data-key="PageSlot"],' +
        'html[data-assistant-dock="left"] [data-key="PageSlot"],' +
        'html[data-assistant-dock="right"] [data-key="PageSlot"],' +
        'html[data-header-left-compact="true"] [data-key="PageSlot"]' +
        '{left:0!important;right:0!important}}' +
        // ── THE NAVBAR'S PLACE ─────────────────────────────────────────────
        // Nika 2026-10-24: "on both workspaces - com and normal make sidebar
        // optional to be positioned at the top left right or bottom. for
        // normal workspace keep as is"; 2026-10-27: "also make navbar left
        // by default" (shared functions/navbarLayout.js — the person's own
        // look; the avatar menu's "Navbar" row). <html data-nav-place>
        // (written in _syncShellRoute) names the place; 'top' — a person's
        // choice, and always on a phone — matches no rule below, so the top
        // navbar's frame is exactly as before. On a rail
        // (left / right) or at the bottom the AppHeader floats at the X2 edge
        // gap and the shared header turns itself into the rail
        // (`data-nav-rail` rules in shared AppHeader.js); the frame clears
        // it instead of a top navbar. --nav-edge is the bar's reach from its
        // own screen edge (live-measured on the AppHeader mount,
        // _measureHeaderNavEdge); + --nav-gap it is that side's inset, the
        // same gap a sheet keeps from the top navbar. The top clearances
        // drop to the X2 edge gap. The same reacts-in-CSS idiom as the
        // assistant-dock rules above, and AFTER them in this sheet, so on a
        // shared side the navbar stays outermost.
        '@property --nav-edge{syntax:"<length>";inherits:true;initial-value:0px}' +
        ':root{--nav-edge:0px;--nav-inset-left:0px;--nav-inset-right:0px;--nav-inset-bottom:0px}' +
        'html[data-nav-place="left"]{--nav-inset-left:calc(var(--nav-edge) + var(--nav-gap))}' +
        'html[data-nav-place="right"]{--nav-inset-right:calc(var(--nav-edge) + var(--nav-gap))}' +
        // At the bottom the sheets' foot clears the bar even while the
        // composer (which rises above the bar, below) is hidden: the stack's
        // LINE moves (--slot-bottom derives from it, above).
        'html[data-nav-place="bottom"]{--nav-inset-bottom:calc(var(--nav-edge) + var(--nav-gap));' +
        '--stack-line:calc(max(var(--prompt-clear), var(--nav-edge)) + var(--nav-gap))}' +
        'html[data-nav-place="left"],html[data-nav-place="right"],html[data-nav-place="bottom"]' +
        '{--shell-top:calc(var(--spacing-X2) + env(safe-area-inset-top));' +
        '--slot-top:calc(var(--spacing-X2) + env(safe-area-inset-top))}' +
        // The bar itself: a rail runs the screen's height at the X2 gap, a
        // bottom bar is the top navbar's box moved to the foot (its own X2
        // padding keeps the islands off the edge).
        'html[data-nav-place="left"] [data-key="AppHeader"],html[data-nav-place="right"] [data-key="AppHeader"]' +
        '{position:fixed;top:calc(env(safe-area-inset-top) + var(--spacing-X2));' +
        'bottom:calc(env(safe-area-inset-bottom) + var(--spacing-X2))}' +
        'html[data-nav-place="left"] [data-key="AppHeader"]{left:calc(env(safe-area-inset-left) + var(--spacing-X2));right:auto}' +
        'html[data-nav-place="right"] [data-key="AppHeader"]{right:calc(env(safe-area-inset-right) + var(--spacing-X2));left:auto}' +
        'html[data-nav-place="bottom"] [data-key="AppHeader"]{top:auto;bottom:env(safe-area-inset-bottom)}' +
        // Every in-flow page: the shell root's padding, as the module rail's
        // (shellApp paddingLeft) — max(), because a module rail beside a
        // left navbar already reaches past it (its clearance is measured
        // from the screen edge).
        'html[data-nav-place="left"] [data-key="smblsapp"]{padding-left:max(var(--module-rail-clear, 0px), var(--nav-inset-left))}' +
        'html[data-nav-place="right"] [data-key="smblsapp"]{padding-right:var(--nav-inset-right)}' +
        'html[data-nav-place="bottom"] [data-key="smblsapp"]{padding-bottom:var(--nav-inset-bottom)}' +
        'html[data-nav-place="left"] [data-key="ModuleRailHost"]{left:var(--nav-edge)}' +
        // The home sets its own top (pages/main.js MainColumn: a static D1 —
        // the floating top navbar plus its gap; the 'home' route self-offsets,
        // _syncShellRoute's note). With the bar off the top that D1 is empty
        // space over the page (Nika 2026-10-25: "when navbar at the left,
        // reduce top size on content"; 89px on dev 579269d80): the board's
        // own side gutter instead, C. The MainColumn compound out-specifies
        // the component's single-class padding, as the composer rule does.
        'html[data-nav-place="left"] [data-key="content"] [data-key="MainColumn"],' +
        'html[data-nav-place="right"] [data-key="content"] [data-key="MainColumn"],' +
        'html[data-nav-place="bottom"] [data-key="content"] [data-key="MainColumn"]' +
        '{padding-top:calc(var(--spacing-C) + env(safe-area-inset-top))}' +
        // The PageSlot band, alone and beside a docked drawer on the same side
        // (the drawer sits between the bar and the sheet).
        'html[data-nav-place="left"] [data-key="PageSlot"]{left:var(--nav-inset-left)}' +
        'html[data-nav-place="right"] [data-key="PageSlot"]{right:var(--nav-inset-right)}' +
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="PageSlot"]' +
        '{left:calc(var(--nav-inset-left) + var(--assistant-inset-left) + var(--spacing-X2))}' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="PageSlot"]' +
        '{right:calc(var(--nav-inset-right) + var(--assistant-inset-right) + var(--spacing-X2))}' +
        // The docked assistant and its out-of-credits cover: beside the bar.
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="AssistantDrawer"],' +
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        '{left:var(--nav-inset-left)}' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="AssistantDrawer"],' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="CreditPillDock"][data-credit-dock="cover"]' +
        '{right:var(--nav-inset-right)}' +
        // The composer and the credit pill above it: centred in the band the
        // bar and a docked drawer leave (the dock rules' shift, plus the bar),
        // and above a bottom bar.
        'html[data-nav-place="left"] [data-key="CanvasPromptTextarea"],' +
        'html[data-nav-place="left"] [data-key="CreditPillDock"][data-credit-dock="pill"]' +
        '{transform:translateX(calc(-50% + (var(--nav-inset-left) + var(--assistant-inset-left) - var(--assistant-inset-right)) / 2))}' +
        'html[data-nav-place="right"] [data-key="CanvasPromptTextarea"],' +
        'html[data-nav-place="right"] [data-key="CreditPillDock"][data-credit-dock="pill"]' +
        '{transform:translateX(calc(-50% + (var(--assistant-inset-left) - var(--assistant-inset-right) - var(--nav-inset-right)) / 2))}' +
        'html[data-nav-place="bottom"] [data-key="CanvasPromptTextarea"]{bottom:var(--nav-inset-bottom)}' +
        // The page rails and the docked chat / mail lists: off the bar. (A
        // page rail on the shared side rule — the tasks, files, infra and
        // project canvas rails, and the infra detail drawer that opens at its
        // rail's place — never shares an edge with a rail navbar: it moves to
        // the right beside a left one, shared functions/navbarLayout.js
        // `sidebarSide`.)
        'html[data-nav-place="left"] [data-key="CanvasViewRail"],' +
        'html[data-nav-place="left"] [data-key="MessengerList"],' +
        'html[data-nav-place="left"] [data-key="MailDrawer"]' +
        '{left:var(--nav-inset-left)}' +
        // The view rail beside a drawer docked on the bar's side: the drawer
        // sits between the bar and the rail (the PageSlot rule's shape).
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="CanvasViewRail"]' +
        '{left:calc(var(--nav-inset-left) + var(--assistant-inset-left) + var(--spacing-X2))}' +
        'html[data-nav-place="right"] [data-key="VersionHistoryPanel"],' +
        'html[data-nav-place="right"] [data-key="CanvasV2CodeEditor"],' +
        'html[data-nav-place="right"] [data-key="CustomizeDrawer"]' +
        '{right:var(--nav-inset-right)}' +
        'html[data-nav-place="right"] [data-key="VersionDetailPanel"]' +
        '{right:calc(var(--nav-inset-right) + var(--spacing-X2) + var(--spacing-G1))}' +
        // The project canvas's tool panels take the side its rail does not
        // (canvas VersionHistoryPanel / VersionDetailPanel / CanvasV2CodeEditor
        // ask for the side): beside a left navbar the rail is on the right, so
        // they sit on the left, off the bar, and clear the docked chat list
        // there as the left rails do. (`data-nav-place` in the chat rules
        // outranks the bar rules above them; no !important.)
        'html[data-nav-place="left"] [data-key="VersionHistoryPanel"][data-sidebar-side="right"],' +
        'html[data-nav-place="left"] [data-key="CanvasV2CodeEditor"][data-sidebar-side="right"]' +
        '{left:var(--nav-inset-left)}' +
        'html[data-nav-place="left"] [data-key="VersionDetailPanel"][data-sidebar-side="right"]' +
        '{left:calc(var(--nav-inset-left) + var(--spacing-X2) + var(--spacing-G1))}' +
        // …and clear of a drawer the assistant docks on their side, on both
        // sides (the PageSlot rules' shape: the drawer sits at the edge or
        // beside the bar, the panel one X2 after it; the detail card one G1
        // and one X2 further on). Before the chat-list rules, which keep
        // their place when the list is open.
        'html[data-assistant-dock="right"] [data-key="VersionHistoryPanel"]:not([data-sidebar-side="right"]),' +
        'html[data-assistant-dock="right"] [data-key="CanvasV2CodeEditor"]:not([data-sidebar-side="right"])' +
        '{right:calc(var(--assistant-inset-right) + var(--spacing-X2))}' +
        'html[data-assistant-dock="right"] [data-key="VersionDetailPanel"]:not([data-sidebar-side="right"])' +
        '{right:calc(var(--assistant-inset-right) + var(--spacing-X2) * 2 + var(--spacing-G1))}' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="VersionHistoryPanel"],' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="CanvasV2CodeEditor"]' +
        '{right:calc(var(--nav-inset-right) + var(--assistant-inset-right) + var(--spacing-X2))}' +
        'html[data-nav-place="right"][data-assistant-dock="right"] [data-key="VersionDetailPanel"]' +
        '{right:calc(var(--nav-inset-right) + var(--assistant-inset-right) + var(--spacing-X2) * 2 + var(--spacing-G1))}' +
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="VersionHistoryPanel"][data-sidebar-side="right"],' +
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="CanvasV2CodeEditor"][data-sidebar-side="right"]' +
        '{left:calc(var(--nav-inset-left) + var(--assistant-inset-left) + var(--spacing-X2))}' +
        'html[data-nav-place="left"][data-assistant-dock="left"] [data-key="VersionDetailPanel"][data-sidebar-side="right"]' +
        '{left:calc(var(--nav-inset-left) + var(--assistant-inset-left) + var(--spacing-X2) * 2 + var(--spacing-G1))}' +
        'html[data-nav-place="left"][data-chat-drawer="open"] [data-key="VersionHistoryPanel"][data-sidebar-side="right"],' +
        'html[data-nav-place="left"][data-chat-drawer="open"] [data-key="CanvasV2CodeEditor"][data-sidebar-side="right"]' +
        '{left:calc(var(--spacing-X2) * 2 + var(--spacing-G1) + var(--nav-inset-left))}' +
        'html[data-nav-place="left"][data-chat-drawer="open"] [data-key="VersionDetailPanel"][data-sidebar-side="right"]' +
        '{left:calc(var(--spacing-X2) * 3 + var(--spacing-G1) * 2 + var(--nav-inset-left))}' +
        // The online members beside the composer's baseline (bottom left).
        'html[data-nav-place="left"] [data-key="OnlineMembersDock"]{left:var(--nav-inset-left)}' +
        'html[data-nav-place="bottom"] [data-key="OnlineMembersDock"]{bottom:var(--nav-inset-bottom)}' +
        // The right-edge docks: parked windows and the call.
        'html[data-nav-place="right"] [data-key="MinimizedDock"]' +
        '{right:calc(var(--assistant-inset-right, 0px) + var(--spacing-A) + var(--nav-inset-right))}' +
        'html[data-nav-place="bottom"] [data-key="MinimizedDock"]{bottom:calc(var(--spacing-C) + var(--nav-inset-bottom))}' +
        'html[data-nav-place="right"] [data-key="MeetCallWidget"]{right:calc(var(--spacing-A) + var(--nav-inset-right))}' +
        'html[data-nav-place="bottom"] [data-key="MeetCallWidget"]' +
        '{bottom:calc(var(--spacing-A) + var(--zoombar-clear) + var(--nav-inset-bottom))}' +
        // Phone header scroll-condense (2026-08-02) — the ONE deck (AppHeader,
        // merged @mobileL — see AppHeader.js) slides fully off-screen while
        // <html data-nav-condensed="true">, written by ONE document-level
        // capture-phase scroll listener (packages/shared/functions/
        // navCondense.js, armed from AppHeader's onCreate). Same attribute-
        // reacts-in-CSS idiom as html[data-chat-drawer] above: this is a pure
        // selector + transform, no component-level reactive prop, so toggling
        // the attribute costs one style recalc, never a re-render. The
        // transition itself lives on the component (AppHeader.js '@mobileL'
        // transition: '240ms defaultBezier transform') so it goes through the
        // normal token pipeline; only the conditional VALUE needs the
        // attribute-selector this raw sheet provides. Clears the safe-area
        // inset too (translateY alone would leave the bottom edge sitting
        // exactly at the inset boundary) plus a small Z buffer so the glass
        // surface's own box-shadow/blur fully leaves the viewport.
        '@media ' +
        media.mobileL +
        '{' +
        'html[data-nav-condensed="true"] [data-key="AppHeader"]{' +
        'transform:translateY(calc(-100% - env(safe-area-inset-top) - var(--spacing-Z)))}}' +
        // iOS zoom-on-focus guard — Safari auto-zooms the page when a focused
        // input/textarea/select computes a font-size below 16px, and the
        // workspace type scale drops under 16px at @tabletS (≤1024). max(16px,1em)
        // floors an inherited field size at 16px without ever shrinking a
        // larger one. Workspace-local, applies only ≤1024px; desktop untouched.
        '@media ' +
        media.tabletS +
        '{input,textarea,select{font-size:max(16px,1em)}}' +
        // …and on a TOUCH screen at every width, where the zoom happens — and
        // here the floor must outrank a field's OWN size. The rule above is
        // (0,0,1): every field that names a size (one atomic class, 0,1,0)
        // beat it, so it only ever covered unstyled fields. Measured on dev
        // DFI 2026-10-05 (DESIGN-SHELL-RESPONSIVE-IOS-SAFARI-1): the composer
        // at 14.875px on an 834px iPad (14px root), the AppField kit at `Z2`
        // (≈15.3px at a phone's 16px root, ≈13.4px at the iPad's) and the
        // AppSelect under 16px at @tabletS — a zoom on every form field
        // focus. `:root :is(…)` (0,1,1) outranks the field's own size; 1em is
        // the parent's size, so a field never drops below its surroundings.
        // `any-pointer`, not `pointer`: an iPad with a trackpad attached still
        // has its touch screen. A field that shows DISPLAY-size text (at least
        // 16px at every root, so it never zooms) opts out with
        // `data-display-text`, or the floor would cut it to its parent's size.
        '@media (any-pointer:coarse){' +
        ':root :is(input,textarea,select):not([data-display-text]){font-size:max(16px,1em)}}' +
        // ——— iPad/touch native-feel + a11y floor (2026-07-31 per Nika "review
        // ipad use … native-like + motion, touch gestures, accessibility") ———
        // Body rubber-band: the shell is app chrome — every surface owns its own
        // scroller, so a chained overscroll bounce on <body> (WKWebView pulls the
        // whole page revealing the backdrop) reads as "website", never "app".
        // Inner scrollers keep their native momentum + bounce.
        'html,body{overscroll-behavior:none}' +
        // Reduced-motion floor (WCAG 2.3.3): when the OS asks, clamp EVERY CSS
        // animation/transition. ~21 components ship curated reduced variants
        // already — those still apply; this is the global floor so no surface is
        // left animating. WAAPI surfaces (PageSlot) self-gate on the same query.
        '@media (prefers-reduced-motion:reduce){*,*::before,*::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important;scroll-behavior:auto!important}}' +
        // Keyboard-focus floor (iPad hardware keyboards + desktop tabbing): a
        // ZERO-specificity :where() ring so any focusable reached by keyboard is
        // visible. The ~24 components with curated :focus-visible styles win by
        // ordinary cascade (real specificity beats :where's zero).
        ':where(a,button,[role="button"],[role="tab"],[role="menuitem"],input,select,textarea,[tabindex]):focus-visible{outline:2px solid var(--color-blue);outline-offset:2px}' +
        // Placeholder floor: unstyled fields leak Chrome\'s UA default gray
        // (#757575 — measured 3.9:1 on the composer, needs 4.5). Zero-
        // specificity :where() + the adaptive `placeholder` token pair; the
        // ~4 components with curated ::placeholder colors win by cascade.
        // opacity:1 defeats the UA\'s extra placeholder dimming.
        ':where(input,textarea)::placeholder{color:var(--color-placeholder);opacity:1}' +
        // (Coarse tap-slop selectors removed 2026-08-01 per Nika "why such
        // selectors … go with frank rules": the 44pt tap affordance now lives
        // INSIDE the brand buttons — NavbarButton/IconButton '@coarse'
        // ':after' with the `tapSlop` sizes token — replacing both their old
        // size floors and this block. HeaderTabs' bespoke tab buttons carry
        // the same slop declaratively in HeaderTabs.js.)
        // Home-indicator clearance: the composer is fixed at a token bottom with
        // no safe-area component (measured live: bottom 15.9px, padding 0). In
        // the iPad/iPhone shell (viewport-fit=cover) the home-indicator inset
        // would swallow that gap. margin-bottom lifts the fixed box by exactly
        // the inset; env()=0 everywhere else so web is untouched. The
        // --prompt-clear ResizeObserver reads r.top, so PageSlot clearance
        // follows automatically.
        '[data-key="CanvasPromptTextarea"]{margin-bottom:env(safe-area-inset-bottom,0px)}' +
        // ——— ACCESSIBILITY PREFERENCES (2026-09-04 per Nika "add same
        // accessibility options as we have in landing") ———
        // The four personal choices from shared/functions/a11yPrefs.js, which
        // _syncShellRoute mirrors onto <html>. Same attribute names and same
        // effects as the public site's designSystem/reset.js, so the two
        // products answer a preference identically.
        //
        // TYPE SCALE. One number, `--a11y-type-scale`, multiplied into the
        // root font-size — every `em` token in the design system is derived
        // from it, so one declaration moves the whole product. No attribute is
        // written at the base step, which is why the var carries a `1`
        // fallback rather than a `[data-font-scale="md"]` rule.
        'html[data-font-scale="sm"]{--a11y-type-scale:.9;font-size:calc(var(--font-size-A) * var(--a11y-type-scale))}' +
        'html[data-font-scale="lg"]{--a11y-type-scale:1.15;font-size:calc(var(--font-size-A) * var(--a11y-type-scale))}' +
        // HIGH CONTRAST. The quiet inks become the ink. Token-level, not a
        // per-component sweep: `caption` and `inactive` are the two adaptive
        // quiet greys the whole product reads for secondary text, so
        // repointing them at `title` lifts every surface at once and nothing
        // has to opt in. The line token firms up with them so borders do not
        // vanish against the raised text.
        'html[data-contrast="high"]{--color-caption:var(--color-title);--color-inactive:var(--color-title);--color-placeholder:var(--color-title);--color-line:var(--color-title)}' +
        // REDUCED MOTION. The SAME declaration the OS floor above already
        // ships for `prefers-reduced-motion`, now also reachable by choice —
        // a person on a device that cannot express the OS setting (or who
        // wants it for this app only) gets the identical result.
        'html[data-motion="reduced"] *,html[data-motion="reduced"] *::before,html[data-motion="reduced"] *::after{animation-duration:.01ms!important;animation-iteration-count:1!important;transition-duration:.01ms!important;scroll-behavior:auto!important}' +
        // UNDERLINE LINKS. WCAG 1.4.1: never colour alone. Scoped to real
        // anchors with an href — the product routes plenty of BUTTONS that
        // look like links, and underlining those would be noise, not help.
        'html[data-links="underline"] a[href]{text-decoration:underline}' +
        el.call('phoneNavigationStyles')
      document.head.appendChild(s)

      // Native shell only: pin the page scale so a Magic-Keyboard TRACKPAD
      // pinch cannot zoom the whole app — WKWebView performs that
      // magnification at the native scroll-view layer, BEFORE any cancelable
      // JS event, so the canvas gesture handlers never get a say (verified on
      // device 2026-08-01: screen pinch zooms the canvas correctly via the
      // touch path; trackpad pinch zoomed the app). WKWebView respects
      // maximum-scale (unlike Safari, which ignores it for accessibility);
      // native apps don't page-zoom, and iPadOS system a11y zoom still works.
      // Browsers never hit this branch — web pinch-zoom stays untouched.
      if (el.call('isNativeShell')) {
        const vp = document.querySelector('meta[name="viewport"]')
        if (vp && !/maximum-scale/.test(vp.content)) {
          vp.content += ', maximum-scale=1'
        }
      }
    }
    const resolveSdk = () => el.sdk || el.context?.methods?.sdk || null

    // Boot the analyzing singleton FIRST so any error fired by the auth /
    // workspace-load chain below is captured + shipped. createAnalyzing's
    // pre-boot window listeners catch errors that fire before plugin.activate;
    // the singleton's user identification kicks in once this call returns.
    // The transport routes through sdk.execute('workspaceProject.analyzed',
    // 'ingest', …) — a deprecated alias that delegates to the main API
    // server's Mongo-backed POST /core/analyzed/ingest route, which stamps
    // workspace_id from the caller's JWT / envelope, so the shell never has
    // to ship org context.
    //
    // KEPT EAGER (iPad P1 boot audit) — deliberately NOT deferIdleBoot'd:
    //   1. The window error/rejection listeners are installed BY
    //      createAnalyzing (not at module import). Deferring blinds
    //      telemetry to exactly the boot-window errors this singleton
    //      exists to catch — the auth/workspace-load chain right below.
    //   2. reidentifyOnOrgSwitch reads the module-level `_instance` at
    //      CALL time and returns a permanent no-op when it isn't booted
    //      yet (analyzing.js) — the two calls are ordering-coupled.
    // Cost is tracked by the wsboot:analyzing measure below; if it ever
    // shows up in the long-task profile, fix it inside @symbo.ls/analyzing
    // (buffer pre-boot events), not by deferring this call.
    bootMark('wsboot:analyzing:start')
    try {
      // Workspace id is workspace-scope data — the analyzing client
      // declares it per envelope rather than letting the server
      // re-derive it from `req.user.activeOrganization` (which 400s
      // for any user whose Mongo activeOrg is null). Per-tenant by
      // design — embeds / standalones pass their own id.
      bootAnalyzing({
        sdk: resolveSdk(),
        // Same resolver as onWorkspaceChange + the orgSwitched handler, so
        // the boot stamp and the first post-boot stamp AGREE (a stamp
        // change rotates the session — ANALYZING-SESSION-ROTATE-1). Falls
        // back to the pre-auth org id only when no workspace id is known.
        workspaceId:
          resolveAnalyzingScopeId({ sdk: resolveSdk(), root: s?.root }) || _preAuth?.org?.id || null
      })
      // identifyFromSdk is fired by `subscribeAuthSession` below — its
      // onSignIn callback runs on first sign-in AND on initial-session
      // restoration (the page-reload-with-stored-token path), so the
      // eager mount-time call would only ever fire when there's no
      // token in storage. That spammed /logs with `fetch.error
      // /core/auth/me` + `identifyFromSdk failed` warnings on every
      // anonymous /login + / visit
      // (WORKSPACE-ANALYZING-IDENTIFY-PRE-AUTH-NOISE).
      el._unsubAnalyzingOrgSwitch = reidentifyOnOrgSwitch(resolveSdk(), () => s.root)
    } catch (err) {
      console.warn('[shell] analyzing boot failed:', err?.message || err)
    }
    bootMark('wsboot:analyzing:end')
    bootMeasure('wsboot:analyzing', 'wsboot:analyzing:start', 'wsboot:analyzing:end')

    el._unsubShellAuth = subscribeAuthSession(
      s.root,
      (signin) => {
        // Boot segment G: the session tick that starts loadPrivateData below.
        bootMark('wsboot:shell:signin')
        // A sign-in ON this page (the boot answered signed out) owes the
        // session its interface language: the loader, and its word, wait for
        // the workspace language (functions/shellLangPolicy.js). Before the
        // data load raises the loader, so its first frame has no English word.
        // Wrapped: a throw here must never starve loadPrivateData below.
        try {
          el.call('markShellLangOwed')
        } catch (err) {
          console.warn('[shell] language debt mark failed:', err?.message || err)
        }
        // Re-stamp user on auth refresh — the user record can change shape
        // (email verified, plan upgraded, etc.) between subscribeAuthSession
        // fires; we want telemetry to reflect the latest traits. The tick's
        // own session user rides along so identify never re-fetches /auth/me
        // for traits the boot already holds (was a 1.7s duplicate mid-boot).
        try {
          identifyFromSdk(resolveSdk(), signin?.user)
        } catch (_) {}
        // Multi-workspace-tabs cross-org (Phase 5): if this tab's URL pins an org
        // different from the one boot resolved, client-only switch to it so the
        // tab shows the org named in its `/w/<org-slug>` URL. Guarded + no-op when
        // the MWT flag is off. Wrapped so a synchronous throw here can NEVER starve
        // the loadPrivateData call below — that's what seeds people / avatar /
        // workspace data, and skipping it leaves every surface blank.
        // WHITELABEL-1: a white-label host (app.bellforge.eu) names its org in
        // DNS, not the URL — resolve host → /w/<org>/<ws> and hand off to the
        // existing machinery. Fired, never awaited: root.orgs is populated by
        // loadPrivateData below, so awaiting here would deadlock the ladder on
        // the very data it waits for. Only the URL reconcile is sequenced
        // behind it — otherwise its bare-path canonicalize stamps the ACTIVE
        // org's URL first and the white-label path looks already-scoped.
        // Platform hosts resolve 'none' synchronously, so their reconcile
        // timing is unchanged apart from one microtask.
        bootCustomDomainScope(s.root, el, {
          // WS-WHITELABEL-ORG-PIN-INERT-WITH-MWT-OFF-1 — with the MWT flag off
          // the `/w/<org>` handoff below is inert (nothing consumes the prefix),
          // so the boot states the host's org through the SDK's client-only
          // rescope instead. Same sdk instance the reconcile uses.
          sdk: resolveSdk(),
          apiBase: shellWorkspaceApiBase(),
          fetchImpl:
            typeof window !== 'undefined' && typeof window.fetch === 'function'
              ? window.fetch.bind(window)
              : null
        }).then(() => {
          try {
            reconcileMwtOrgFromUrl(s.root, resolveSdk(), el, 0, {
              // Lets the reconcile see the org named by a project-canvas or
              // /studio URL, not just a `/w/<org>` prefix — see _urlOrgSlug.
              ownerSlugForPath: (p) => _urlOrgSlug(p, s.root)
            })
          } catch (err) {
            console.warn('[shell] reconcileMwtOrgFromUrl failed:', err?.message || err)
          }
        })
        return loadPrivateData(s.root, resolveSdk())
      },
      (claims) => onWorkspaceChange(s.root, resolveSdk(), claims)
    )

    // External org-switch propagation. Anyone calling `sdk.switchOrg(id)`
    // (the AppOrgSwitcherItem dropdown, future cross-tab storage syncs,
    // programmatic switches) emits `sdk.orgSwitched` on the SDK root bus.
    // We mirror the new org into `root.org` + `root.jwtClaims.workspace_org_id`
    // so every consumer keyed off those fields (filterToActiveOrg,
    // _loadRoles, AppNavbar's `/studio/<slug>` resolver, _person mapper,
    // …) re-keys in place, then refire the workspace-change refetch
    // pipeline so people / teams / roles / announcements / stories /
    // resource-links / user-docs / org-notifications / calendar all
    // reload for the new org. No reload required.
    //
    // The bus is `el.sdk.rootBus` — DOMQL wires the SDK onto every element
    // before any onCreate hook runs (bootShell sets `context.methods.sdk`
    // BEFORE `create()`), and `sdk.rootBus` is set on SDK construction
    // (before initialize()), so the chain is safe to read synchronously.
    el._handleOrgSwitched = async ({ newOrgId, previousOrgId, workspaceHop } = {}) => {
      if (!newOrgId) return
      // Bus replays the last payload to late subscribers — SYNCHRONOUSLY,
      // inside rootBus.on (sdk rootEventBus keeps lastPayloads). A remount
      // of this element after a real switch therefore re-receives that
      // switch's payload during onCreate. The claims stamp below is
      // synchronous in this handler's prelude, so "claims already read
      // newOrgId" means the switch is SETTLED — replay or duplicate, skip.
      // (The previous guard also required previousOrgId === newOrgId,
      // which only catches self-echoes: a replayed REAL switch carries
      // previousOrgId ≠ newOrgId and sailed through into a full spurious
      // onWorkspaceChange — observed as a module teardown-after-mount,
      // and the pre-hardening root.call TypeError came from this same
      // replay running mid-createDomqlElement.)
      if (s.root.jwtClaims?.workspace_org_id === newOrgId) return
      void previousOrgId

      // The address bar still holds the OUTGOING org's URL, so segment 2 is
      // the OUTGOING workspace's handle. Capture that scope NOW: the refetch
      // pipeline below re-scopes `root.workspace` / `root.workspaces` to the
      // new org, and syncWorkspaceUrl's stale-segment strip can only drop a
      // SLUG handle (`hq`) when it is given a workspace record that carries
      // it. Passing only the new org's list left `/w/system/hq/tasks` →
      // `/w/bellforge/ws_UYAOhQyNjm/hq/tasks` — two workspace segments
      // (WS-ORG-SWITCH-URL-DOUBLE-WORKSPACE-SEGMENT-1, measured live).
      const outgoingWorkspace = s.root.workspace || null
      const outgoingWorkspaces = Array.isArray(s.root.workspaces) ? s.root.workspaces : []

      // Loader up at the ABSOLUTE entry (echo guards above are the only thing
      // allowed to skip it): every prelude below — cache invalidation, claims
      // stamp, URL sync — runs with the old tenant still painted, and
      // onWorkspaceChange's own raise lands only after this handler yields to
      // it. Raised via the ownership epoch (beginDataLoad) so a concurrently
      // finishing boot loadPrivateData can't clear it in the gap before
      // onWorkspaceChange re-raises — that gap was a visible mid-teardown
      // reveal (BOOT-RESHUFFLE F3).
      beginDataLoad(s.root)
      // Park OFF any module route of the outgoing manifest NOW — before the
      // pipeline's wipe can null the module bag under a mounted module page
      // (see _leaveModuleRouteForSwitch).
      _leaveModuleRouteForSwitch(el, s, moduleRoutesFromRoot(s.root))

      // Invalidate every cached fetch BEFORE the refetch pipeline runs.
      // Without this, page-level `fetch: [{ ..., cache: 'Ns' }]` declarations
      // (calendar events, ticket lists, channel rosters, …) serve the
      // previous org's cached payload right back into state, so the UI
      // "refreshes" with stale rows. queryClient is the @symbo.ls/fetch
      // adapter-level cache shared across every fetch in the app.
      try {
        queryClient.invalidateQueries()
      } catch (err) {
        console.warn('[shell] queryClient.invalidate failed:', err?.message || err)
      }

      const orgs = s.root.orgs || s.root.jwtClaims?.orgs || []
      const newOrg = orgs.find((o) => o.id === newOrgId)
      const nextClaims = {
        ...(s.root.jwtClaims || {}),
        workspace_org_id: newOrgId
      }
      // Update state.org when we can resolve the row from membership claims;
      // when claims haven't refreshed yet, jwtClaims.workspace_org_id is the
      // canonical pointer the rest of the app reads. Either way, fire the
      // refetch pipeline — bailing on missing newOrg was leaving stale data
      // on screen for any switch that happened before /auth/me hydrated.
      const stateDelta = { jwtClaims: nextClaims }
      if (newOrg) stateDelta.org = newOrg
      // Cross-org: the target WORKSPACE isn't known synchronously (it resolves
      // inside onWorkspaceChange), but the previous scope is definitively DEAD
      // — leaving it in place let elements mounting mid-switch read + write
      // the fetch cache under the old tenant's keys (isolation audit #9). A
      // per-switch sentinel can't collide with any real workspace id, so
      // those transitional fetches stay tenant-clean; onWorkspaceChange
      // writes the real id once the workspace record settles.
      stateDelta.__fetchScope = `switching-org:${String(newOrgId)}`
      s.root.update(stateDelta)

      // Cross-org `?ws=` + pointer sync, as ONE idempotent pass that runs the
      // instant this switch's identity is known — see the WHY block on
      // `orgSwitchUrlWorkspace`. It is invoked three times, deliberately:
      //   1. NOW, in the same tick as the `root.org` re-scope, for a pass that
      //      already holds a new-org workspace record;
      //   2. from `onWorkspaceChange`'s `onWorkspaceResolved` callback, at the
      //      workspace-record adoption — one fetch into the pipeline, not
      //      after all ~18 of them (this is the ~9 s the URL used to lie for);
      //   3. after the pipeline, as the corrective pass.
      // Every later call is a NO-OP when the URL is already correct
      // (`syncWorkspaceUrl` returns null when `next === current`) and the
      // pointer write is idempotent.
      //
      // A cross-org switch to a NON-HQ workspace (org switcher drill-down →
      // e.g. GreenIG) federates via switchOrg, whose inner workspaceSwitched
      // event this shell DEFERS on (`__switchingOrg`) — so the workspace-switch
      // handler's pointer/URL sync never runs on this path, the address bar
      // kept naming only the org, and the next reload resolved it back to the
      // org's HQ: layout read from the WRONG workspace's row + the module never
      // loading — Nika's "sign in to greenig, widgets rearranged / data not
      // loading" (2026-07-30).
      const syncOrgSwitchedUrl = (candidate) => {
        try {
          const org = s.root.org
          const orgId = String(org?.id ?? org?._id ?? '')
          // `rewriteOrg` stamps `org.slug`, so the org ROW must already be the
          // new org. When claims never carried it, `stateDelta.org` was never
          // set and the state org is still the OUTGOING one — rewriting there
          // would stamp the OLD slug onto the NEW workspace, a worse lie than
          // the stale URL. Leave it to the claims tick / boot canonicalizer.
          if (!orgId || orgId !== String(newOrgId)) return
          const resolved = orgSwitchUrlWorkspace(candidate, newOrgId)
          if (!resolved) return
          const wsId = String(resolved.id ?? resolved._id ?? '')
          if (wsId) shellSetActiveWorkspacePointer(wsId)
          // PROGRAMMATIC org switches (sdk.switchOrg from AI tools, cross-tab
          // convergence, tests) never route — only the switcher's own router
          // nav rewrites the URL. Left stale, the address bar names the
          // PREVIOUS org while state runs the new one, and the next reload
          // reconciles back to the URL's org — a flip-flop. `rewriteOrg` is
          // THE one caller allowed to move the org segment: state is settled
          // to the new org here, so the URL is the stale party. (Everywhere
          // else a path-vs-state org mismatch means a cross-org deep-link
          // whose reconcile hasn't run — sync no-ops there.) syncWorkspaceUrl's
          // parse also strips the OLD path's workspace segment against the
          // OLD org's own list, which the retired manual prefix-splice got
          // wrong when the resolver missed.
          shellSyncWorkspaceUrl(
            {
              workspace: resolved,
              org,
              // Incoming list + the outgoing org's scope, so the OLD
              // workspace segment in the URL is recognisable as stale
              // whether it is a raw `ws_` id or a plain slug.
              workspaces: orgSwitchStaleWorkspaces(
                outgoingWorkspace,
                outgoingWorkspaces,
                Array.isArray(s.root.workspaces) ? s.root.workspaces : []
              )
            },
            { rewriteOrg: true }
          )
        } catch (err) {
          console.warn('[shell] org-switch workspace URL sync failed:', err?.message || err)
        }
      }
      // SAME TICK as the `root.org` re-scope on the REAL user path: the org
      // switcher hands `sdk.switchOrg` a `homeWorkspaceId`, whose workspace hop
      // completes BEFORE this event is emitted — so the switch already KNOWS
      // its target workspace, and `payload.workspaceHop.workspaceId` is that
      // identity. Stamping it here needs no fetch at all: a raw `ws_<id>`
      // handle is a first-class URL handle (the resolver + the stale-segment
      // strip both read it by shape), and the callback below refines it to the
      // workspace's slug for free once the record lands. Without this the URL
      // waited on the workspace RESOLVE (one to two round trips, measured
      // 0.9-3.4 s on the dev box) even when nothing needed resolving.
      // A programmatic `sdk.switchOrg(id)` with no hop carries no target, so it
      // falls back to the state record and heals on the callback below.
      // `sdk.switchWorkspace` returns `newWorkspaceId` on a real hop and
      // `workspaceId` on its unchanged early return — read BOTH, or the
      // same-tick stamp silently never fires (measured: it fell back to the
      // list-row callback, +990 ms).
      const hopWorkspaceId =
        workspaceHop && workspaceHop.ok !== false
          ? String(workspaceHop.newWorkspaceId || workspaceHop.workspaceId || '')
          : ''
      const hopWorkspace = hopWorkspaceId
        ? orgSwitchHopWorkspace(hopWorkspaceId, [
            s.root.orgWorkspaces?.[String(newOrgId)],
            s.root.orgWorkspaces?.[newOrgId],
            s.root.workspaces
          ])
        : null
      syncOrgSwitchedUrl(hopWorkspace || s.root.workspace)

      try {
        await onWorkspaceChange(s.root, resolveSdk(), nextClaims, {
          onWorkspaceResolved: syncOrgSwitchedUrl
        })
      } catch (err) {
        console.warn('[shell] onWorkspaceChange threw:', err?.message || err)
        // The pipeline threw before its reveal — clear whatever raise is
        // current so the loader can't wedge (home grid waits on it for 120s).
        endDataLoadFailsafe(s.root)
      }
      // Corrective pass — a no-op whenever the callback above already landed
      // the right URL, and the healer when the pipeline resolved the workspace
      // on a path that never called back (early return, superseded pass).
      syncOrgSwitchedUrl(s.root.workspace)
    }
    try {
      el.sdk.rootBus.on('sdk.orgSwitched', el._handleOrgSwitched)
    } catch (err) {
      console.warn('[shell] orgSwitched listener init failed:', err?.message || err)
    }

    // Workspace-only switch propagation. A same-org workspace pick goes through
    // sdk.switchWorkspace (NOT switchOrg, which no-ops on an unchanged org), so
    // it emits `sdk.workspaceSwitched` rather than `sdk.orgSwitched`. Mirror the
    // org-switch handler: invalidate the fetch cache, advance the active-workspace
    // claim optimistically, and refire the in-place refetch so the UI re-scopes to
    // the new workspace WITHOUT a reload. Without this, switchWorkspace re-mints
    // the JWT but the UI keeps showing the previous workspace's data.
    el._handleWorkspaceSwitched = async ({ newWorkspaceId, previousWorkspaceId } = {}) => {
      if (!newWorkspaceId || newWorkspaceId === previousWorkspaceId) return
      // A cross-org switch federates via switchOrg → inner switchWorkspace, which
      // emits BOTH workspaceSwitched and orgSwitched. The org switcher flags that
      // window so we defer to the orgSwitched handler here and don't double-refetch.
      if (el.sdk?.__switchingOrg) return
      // Same replay-settled guard as the org handler above: the claims stamp
      // is synchronous below (line ~1990), so claims already naming the
      // target workspace means this event is a bus replay (remount) or a
      // duplicate — never a fresh switch, which always arrives with the OLD
      // workspace still in claims.
      if (s.root.jwtClaims?.active_workspace_id === newWorkspaceId) return
      // Loader up at the ABSOLUTE entry (see the org-switch handler note),
      // then park off any outgoing-manifest module route before the wipe.
      // Epoch-owned raise — see the org-switch handler.
      beginDataLoad(s.root)
      _leaveModuleRouteForSwitch(el, s, moduleRoutesFromRoot(s.root))
      try {
        queryClient.invalidateQueries()
      } catch (err) {
        console.warn('[shell] queryClient.invalidate failed:', err?.message || err)
      }
      const nextClaims = {
        ...(s.root.jwtClaims || {}),
        active_workspace_id: newWorkspaceId
      }
      // `__fetchScope` advances WITH the claim, not a network round-trip later
      // (isolation audit #9): onWorkspaceChange used to write it only after
      // its awaited getWorkspace resolved, so any element mounting in that
      // window built fetch-cache keys with the PREVIOUS tenant's scope — a
      // two-way poison (old rows painted into the new workspace, new rows
      // cached under the old tenant's key and served on a switch-back). The
      // id is known synchronously here; onWorkspaceChange re-writing the same
      // value later is a no-op.
      s.root.update({ jwtClaims: nextClaims, __fetchScope: String(newWorkspaceId) })
      // Name the new workspace in the address bar. `/w/<org-slug>` identifies
      // only the ORG, so in a multi-workspace org the URL stayed identical
      // across the switch — and the next reload resolved it back to the org's
      // HQ workspace, silently undoing the pick. Resolved from `workspaces`
      // rather than `root.workspace`, which the refetch pipeline has not
      // advanced yet at this point.
      try {
        // Advance THIS tab's pointer. `sdk.switchWorkspace` writes only the
        // shared `localStorage` key, but under MWT the per-tab pointer lives in
        // sessionStorage — so without this the tab kept reporting the PREVIOUS
        // workspace to every pointer reader (sdk.js's activeWorkspaceId
        // resolver, the next boot's fallback) after a switch.
        shellSetActiveWorkspacePointer(newWorkspaceId)
        // Best-effort IMMEDIATE stamp — instant address-bar feedback when the
        // target is already present in the (possibly not-yet-refreshed)
        // governance-claims `workspaces` list. See the corrective re-sync
        // below for when it isn't.
        const list = Array.isArray(s.root.workspaces) ? s.root.workspaces : []
        const next = list.find((w) => String(w.id ?? w._id ?? '') === String(newWorkspaceId))
        if (next) shellSyncWorkspaceUrl({ workspace: next, org: s.root.org, workspaces: list })
      } catch (err) {
        console.warn('[shell] workspace URL sync failed:', err?.message || err)
      }
      try {
        await onWorkspaceChange(s.root, resolveSdk(), nextClaims)
      } catch (err) {
        console.warn('[shell] onWorkspaceChange (workspace switch) threw:', err?.message || err)
        endDataLoadFailsafe(s.root)
      }
      // Corrective re-sync from the DEFINITIVELY-resolved workspace record.
      // The pre-emptive stamp above reads `s.root.workspaces` (the governance-
      // claims list) SYNCHRONOUSLY, the instant `sdk.workspaceSwitched` fires —
      // when the target isn't in that list yet (a workspace this session
      // hasn't seen, or a claims list still scoped to a stale org), `next` is
      // `undefined` and the `if (next)` guard silently skips the sync
      // entirely, leaving the address bar's `?ws=` naming the PREVIOUS
      // workspace. Live-confirmed: switching the same-org HQ → GreenIG left
      // `?ws=<raw HQ id>` in the URL (2026-07-30) — a stale handle a reload,
      // bookmark, or shared link then resolves straight back to (bootShell's
      // `resolveActiveWorkspace` reads the URL first), re-seeding every
      // workspace-scoped boot read (dashboard prefs, local cache) from the
      // WRONG workspace before anything corrects it. `onWorkspaceChange` has,
      // by this point, fetched + adopted the exact target record onto
      // `s.root.workspace` (its own `getWorkspaceCached` call, cross-org
      // guarded) — re-deriving the handle from THAT instead of the list
      // lookup can't miss. The identity check guards the generation race
      // (`onWorkspaceChange`'s own supersession guard can no-op this call if
      // a newer switch started meanwhile — `s.root.workspace` would then
      // already reflect the NEWER target, not this one, and the newer
      // switch's own invocation owns re-syncing it). Idempotent no-op when
      // the pre-emptive stamp above already got it right.
      try {
        const resolved = s.root.workspace
        if (resolved && String(resolved.id ?? resolved._id ?? '') === String(newWorkspaceId)) {
          shellSyncWorkspaceUrl({
            workspace: resolved,
            org: s.root.org,
            workspaces: Array.isArray(s.root.workspaces) ? s.root.workspaces : []
          })
        }
      } catch (err) {
        console.warn('[shell] workspace URL re-sync failed:', err?.message || err)
      }
    }
    try {
      el.sdk.rootBus.on('sdk.workspaceSwitched', el._handleWorkspaceSwitched)
    } catch (err) {
      console.warn('[shell] workspaceSwitched listener init failed:', err?.message || err)
    }

    // WORKSPACE-ENFORCED INTERFACE LANGUAGE — re-resolve on every workspace
    // upgrade, not on any one switch path.
    //
    // A workspace whose admin set `settings.languagePolicy: 'enforced'` owns
    // the interface language inside itself, which makes that level of the
    // precedence the ONLY one that can change without a reload: it belongs to
    // a workspace, not to the person. bootShell resolves once, for whichever
    // workspace was active at boot — so without this, enforcement applied to
    // that workspace and no other.
    //
    // Subscribed to `onActiveWorkspaceFull` rather than to
    // `sdk.workspaceSwitched` beside it, DELIBERATELY. There are four ways the
    // active workspace changes (this handler, the org-switch handler,
    // switchOrg's inner switchWorkspace, and the cross-tab storage echo) plus
    // a fifth non-switch case that needs the same correction — the boot
    // record arriving AFTER bootShell already resolved. All five funnel
    // through `setActiveWorkspaceRecord`, which is what publishes this event,
    // so one subscription covers them and nothing has to be kept in step with
    // a list of switch paths.
    //
    // `applyWorkspaceLangPolicy` recomputes the WHOLE precedence and no-ops
    // when the answer is unchanged, so a redundant fire costs nothing and
    // LEAVING an enforced workspace restores the normal order for free.
    // Stable identity (a named element property, not an inline arrow) because
    // `onActiveWorkspaceFull` de-dupes by function identity.
    // Through `applyShellLangPolicy` (functions/shellLangPolicy.js): a
    // signed-out shell has no workspace, and the policy's English answer
    // overwrote the public language bootShell stamped for /login.
    el._applyLangPolicyOnWorkspace = () => {
      Promise.resolve(el.call('applyShellLangPolicy')).catch((err) =>
        console.warn('[shell] workspace language policy failed:', err?.message || err)
      )
    }
    try {
      el._unsubLangPolicy = onActiveWorkspaceFull(el._applyLangPolicyOnWorkspace)
      // The event announces an UPGRADE, so it does not fire for a workspace
      // that was already full when this listener attached (a remount, or a
      // boot whose record landed first). Run once now for that case; the
      // no-op guard makes it free when boot already got it right.
      el._applyLangPolicyOnWorkspace()
    } catch (err) {
      console.warn('[shell] language policy listener init failed:', err?.message || err)
    }

    // Per-user socket subscription. When `sdk.setActiveOrganization`
    // fires in ANY session for this user (this tab, another tab,
    // another device), the core server's `PATCH /auth/me/active-org`
    // handler broadcasts `active-org-changed` on the user-socket
    // `user:<id>` room. We mirror it locally by calling
    // `sdk.switchOrg(newOrgId)` — which trips the same rootBus
    // pipeline as a local click, refetching org-scoped data.
    //
    // Transport (socket.io-client) lives inside the SDK as
    // `sdk.subscribeUserEvents(handlers)`. The workspace shell never
    // imports `socket.io-client` directly. The SDK's auth-aware
    // `getAuthToken()` + base URL resolution handle the handshake.
    //
    // The `sdk.switchOrg` guard inside the bus handler (skip when
    // `currentOrgId === newOrgId`) makes the echo to the originating
    // tab a no-op; only other sessions actually re-key.
    //
    // IDLE-DEFERRED (iPad P1 boot audit): the socket.io construct +
    // handshake ran inside the first-paint boot task for an event that
    // only matters AFTER boot. Dropping ≤2.5s is safe by transport
    // semantics — the server broadcasts `active-org-changed` to the
    // `user:<id>` room (server/packages/socket/user.js); socket.io has
    // NO replay for clients not yet in the room, so an event in the
    // deferral gap is simply not delivered. Consequence: a cross-session
    // org switch fired in that exact window doesn't converge this tab —
    // but boot itself just read the Mongo active-org truth via getMe, any
    // LATER switch event still arrives, and the pre-deferral connect
    // handshake had the same-class blind window anyway. The SDK reads the
    // auth token at call time (fail-soft no-op unsub when absent), so
    // late invocation is strictly fresher.
    el._unsubUserRealtime = deferIdleBoot('user-realtime', () =>
      el.sdk.subscribeUserEvents({
        'active-org-changed': ({ orgId } = {}) => {
          if (!orgId) return
          // I6 (§6c): under MWT every tab is pinned to its own org from boot, so it
          // ignores a cross-device org switch. Gate on the flag itself, NOT the URL:
          // the `/w/<slug>` prefix is absent during the pre-canonicalize boot window,
          // so a URL check would let a concurrent socket event yank a freshly-loaded
          // tab off its org (the boot-window race). No-op while the flag is off.
          if (shellIsMwtEnabled()) return
          // WHITELABEL-STRICT-TENANT-SCOPE-1 — a white-label tab is bound to its
          // HOST's tenant. This event is the ACCOUNT's pointer moving on another
          // host (an org switch on my.symbols reaches every socket of the user);
          // following it would move a customer's domain onto that org — and
          // custom domains run with MWT off, so the guard above never fires
          // there.
          if (isCustomDomainHost()) return
          if (el.sdk._context?.activeOrgId === String(orgId)) return
          try {
            el.sdk.switchOrg(String(orgId))
          } catch (err) {
            console.warn('[shell] socket-driven switchOrg failed:', err?.message || err)
          }
        }
      })
    )

    // Notification permission + realtime subscribe + initial fetch — this
    // trio previously only ran from IntranetAppHeader's onRender, but that
    // component is dead code (nothing mounts `IntranetAppHeader` — the shell
    // renders the shared `AppHeader` directly; see components/index.js's
    // barrel export vs. this file's actual AppHeader slot below), so no
    // notification ever subscribed and the browser-permission prompt never
    // fired. `currentUserEmail` isn't populated synchronously at shell mount
    // (it lands async via loadPrivateData/subscribeAuthSession), so poll
    // briefly rather than firing on an empty user — same wait-for-value
    // idiom tickets/pages/index.js's _ensureLoaded uses. One-shot: clears
    // itself once it fires (or gives up).
    {
      let _notifBootAttempts = 0
      el._notifBootTimer = setInterval(() => {
        const email = s.root?.currentUserEmail
        if (!email && ++_notifBootAttempts < 30) return
        clearInterval(el._notifBootTimer)
        el._notifBootTimer = null
        if (!email) return
        try {
          el.call('requestBrowserPermission')
          el.call('subscribeNotifications')
          el.call('fetchNotifications')
        } catch (err) {
          console.warn('[shell] notifications boot failed:', err?.message || err)
        }
      }, 200)
    }

    // Chat realtime at shell scope — previously the SSE stream only opened
    // once /chat was visited (chat/pages/index.js onCreate), so a message in
    // a channel the user never opened this session raised no toast anywhere
    // else in the app. subscribeRealtime is idempotent + workspace-keyed
    // (root._chatRealtimeChannel, guarded on the active workspace id) so
    // calling it here and again from /chat's own onCreate is a safe no-op
    // once the first call has a live connection. Firing at mount — before
    // `root.workspace` may have resolved — can open the stream unscoped;
    // _ensureShellRealtime below (wired into _syncShellRoute) detects that
    // drift the same way a real workspace switch does and re-opens it
    // correctly scoped, so this doesn't need to wait on anything either.
    try {
      el.call('subscribeRealtime')
    } catch (err) {
      console.warn('[shell] subscribeRealtime boot failed:', err?.message || err)
    }

    // Mail realtime at shell scope, the SAME correction as chat's above and
    // for the same reason: the mail SSE stream used to open only from the
    // /mail route or the header drawer, so `navUnreadCounts.mail` — the count
    // the envelope's dot and the navbar badge both read — had no producer on
    // any other route, and an unread inbox showed nothing anywhere
    // (MAIL-BADGE-NOTIFICATIONS-WIDGET-1, architecture/MAIL.md §3.6).
    // subscribeMailStream is idempotent + workspace-keyed on root._mailStream,
    // so this call and /mail's own are a no-op once one connection is live.
    // FLAG-GATED on the SAME predicate as the header envelope
    // (AppHeader HeaderActions.Mail `hide`): a workspace with mail off must
    // not pay for a stream nothing can read.
    _ensureMailRealtime(el, s.root)

    // Online-presence subscription at shell scope so CalOnlineUsersBar
    // in AppHeader shows online team members on EVERY route — including
    // workspace-shell pages (/dashboard, /data/*, /admin/*, /marketplace)
    // where no sub-app or per-page subscribe runs. useOnlinePresence is
    // ref-counted, so canvas/app.js's own subscribe and per-page calls
    // in /calendar, /tickets/board, /documents add themselves to the
    // same channel without re-subscribing. The shell holds the first
    // reference and only releases it on session teardown — so navigating
    // away from /chat doesn't kill presence for /dashboard.
    //
    // IDLE-DEFERRED (iPad P1 boot audit): opens a second /user-socket
    // connection — pure post-boot chrome data. Loss-free by protocol:
    // every `presence:sync` carries the FULL roster (presence.js
    // re-derives onlineUsers from the complete state each sync), so
    // joining ≤2.5s late misses nothing — the first sync catches up
    // entirely. Cost is cosmetic: presence dots (and our own "online"
    // announce to peers) appear up to 2.5s later. _bootChannel already
    // self-retries (60×1s) waiting for sdk/auth, so it is designed for
    // late invocation; ref-count symmetry is order-independent.
    el._presenceUnsub = deferIdleBoot('presence', () =>
      el.call?.('useOnlinePresence', { app: 'shell' })
    )

    // Background notification-count polling. Initial value comes from
    // loadPrivateData; this interval keeps the badges fresh between
    // sign-in and the next org switch. Cross-org by design — not reset
    // on workspace switch.
    el._notifPollTimer = setInterval(() => {
      fetchOrgNotifCount(s.root).catch((e) => console.warn('[shell] notif-poll fetch failed', e))
    }, NOTIF_POLL_MS)

    // CALL-INVITATION (tickets/fable.md) — a cheap root-state nudge so
    // CallInvitationBanner's wall-clock-only conditions (an event's endAt
    // quietly passing while the banner is already showing, with no other
    // state change in between) get re-evaluated even on an otherwise-idle
    // tab. The banner's "appear" moment needs NO timer at all — the
    // realtime `notification-created` push (subscribeNotifications.js)
    // already lands new rows straight into `root.notifs`, which is
    // reactive on its own; this is purely the self-expire polish.
    // Visibility-gated (hidden tabs throttle timers anyway — this is a
    // deliberate skip, not a hope): a backgrounded tab does no work here.
    el._meetingInviteTickTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      s.root?.update?.({ _meetingInviteTick: Date.now() }, { preventFetch: true })
    }, MEETING_INVITE_TICK_MS)

    // Shell-level meet realtime so the "Start a call" icon can badge an ONGOING
    // call in this workspace even before /meet is opened. subscribeMeetRealtime
    // is idempotent (scope-guarded) — /meet's own subscribe reuses the channel.
    // The room-INSERT branch and the meet.snapshot branch both fill
    // root.rooms; the participant branch fills root.liveParticipants; the
    // NewCall OngoingCallBadge derives the count.
    //
    // IDLE-DEFERRED (iPad P1 boot audit): subscribeMeetRealtime itself is
    // still deferred by up to 2.5s here for boot-perf reasons (unchanged).
    // What USED to follow from that — "a room INSERT during the gap is
    // missed" — no longer holds: FIXED (tickets/opus.md "`meet` realtime
    // discards the server's meet.snapshot, so a reconnect recovers
    // nothing", 2026-08-09). The server's connect-time `meet.snapshot`
    // frame used to be swallowed outright by the SDK's event map
    // (WorkspaceProjectService.subscribeMeetSse), with no dispatch branch
    // on this side either; both are fixed now, so the moment this deferred
    // subscribe finally opens, its first frame is a fresh snapshot of
    // whatever rooms exist AT THAT INSTANT — including any created during
    // the pre-subscribe idle-defer window. The badge no longer needs a
    // live room event or a /meet visit to pick those up. (`liveParticipants`
    // is unaffected either way: the server never streams a `participant`
    // kind at all, snapshot or live — D6, see subscribeMeetSse's own doc
    // comment — so the participant count still only updates from a live
    // join/leave.) Errors are caught + warned by the deferIdleBoot runner.
    // (The workspace-switch re-scope in loadPrivateData.js re-invokes the
    // same scope-keyed subscribe and composes with this via its own
    // idempotence.)
    //
    // SESSION-GATED (persona audit OV-2): `_ensureMeetRealtime` opens nothing
    // without a session and a workspace. A boot that is not there yet at the
    // idle moment opens the channel when the full workspace record lands
    // (`onActiveWorkspaceFull` — a sign-in on this page included) or at the
    // next route sync (`_ensureShellRealtime`).
    el._meetRealtimeUnsub = deferIdleBoot('meet-realtime', () => _ensureMeetRealtime(el, s.root))
    el._ensureMeetOnWorkspace = () => _ensureMeetRealtime(el, s.root)
    try {
      el._unsubMeetOnWorkspace = onActiveWorkspaceFull(el._ensureMeetOnWorkspace)
    } catch (err) {
      console.warn('[shell] meet realtime listener init failed:', err?.message || err)
    }

    // TEMP probe — for browser-side modal verification.
    if (typeof window !== 'undefined') {
      window.__win = {
        call: (fn, arg) => el.call(fn, arg),
        modalRoot: () => el?.getRoot?.()?.ModalRoot,
        stack: () => el?.getRoot?.()?.ModalRoot?.state?.stack
      }
      // Live root element for AI-driven platform actions (cloud client tools).
      // simoneAiTransport's onToolCall calls window.__wsRoot.call(fn, …args) to
      // run navigate/theme/search/confirm in the workspace UI. Refreshed here on
      // every (re)mount so it never goes stale after an auth/shell remount.
      window.__wsRoot = el
    }

    // Marketplace install modal — windows-API watcher. Pushes / pops
    // the ModalRoot layer when `root.installModal` flips non-null/null.
    // All existing setters across shared/functions/installable.js,
    // MarketplaceIsland.js, canvas/functions/canvas/installMarketplaceItem.js
    // still write `host.update({ installModal: … })` — this watcher is
    // the bridge between that state and the @symbo.ls/windows API,
    // replacing the previous shell-root `MarketplaceInstallModal: {}`
    // slot whose own reactive `display:` factory toggled visibility.
    //
    // Polled at 100ms — cheap, no signal-subscription plumbing needed,
    // matches the pattern used by calendar's hash sync timers. The
    // install modal opens infrequently (clicking install on a catalog
    // tile) so the polling cost is negligible.
    let _installLayerOpen = false
    el._installModalTimer = setInterval(() => {
      const open = !!s.root?.installModal
      if (open && !_installLayerOpen) {
        _installLayerOpen = true
        // MODAL-X-OVERFLOW-1: openCustomModal supplies a safe default
        // maxHeight so the Close X can't be pushed off-screen.
        el.call('openCustomModal', {
          id: 'marketplace-install',
          content: { extends: 'MarketplaceInstallModal' },
          dismissOnBackdrop: true,
          onCancel: () => {
            try {
              el.call('closeInstallModal', s.root)
            } catch (_) {}
          }
        })
      } else if (!open && _installLayerOpen) {
        _installLayerOpen = false
        el.call('closeModal', 'marketplace-install')
      }
    }, 100)
}

const shellApp = {
  extends: ['appShellStyles'],
  // Docked module rail (chrome.rail — bellforge et al): the SHELL root
  // insets left by the rail's live width, so EVERY in-flow page — home,
  // route mounts, future surfaces — sits beside the rail like the chat
  // sidebar column, with zero per-page clearance code (2026-08-14 per Nika
  // "fix the layout on all pages for bellforge sidebar - just like … chat
  // sidebar … make workspace more abstract and modular to allow such
  // tweaks"). Fixed chrome (navbar, composer, docks, PageSlot) ignores
  // root padding by construction — the slot keeps its own
  // --module-rail-clear insets. Deliberately STATIC, not a factory: the
  // var IS the reactivity — ModuleRailHost's ResizeObserver maintains it
  // (rail width when rendered, 0px when hidden/absent, :root initial 0px),
  // so no signal tracking is involved and non-module workspaces pay 0px.
  paddingLeft: 'var(--module-rail-clear, 0px)',
  // Per-workspace stage colour (chrome.stage — the second module chrome
  // key after chrome.rail; contract note in workspaceProjects.js
  // moduleStageColor). When the ACTIVE workspace's module declares a stage
  // colour, the shell root paints it, so the strip behind the floating
  // module rail AND every in-flow shell page (home, studio, tickets, route
  // mounts) share ONE per-workspace surface (2026-08-16 per Nika: "root bg
  // should match workspace bg — consistent and manageable").
  // STATIC var reference ON PURPOSE — the first cut used a background
  // FACTORY here and it painted its boot-time value forever, because
  // root-level props do not re-evaluate when workspaceProject lands late
  // (measured live 2026-08-16). Same lesson as paddingLeft above: the var
  // IS the reactivity — _setStageVar maintains it from ModuleRailHost's
  // workspaceProject subscription, and a var() string passes the
  // css-in-props token table untouched.
  // ONE chain for every ground (BF-WIDGET-INK-1): `--stage-bg` is the
  // per-workspace override, `--color-stage` the design-system default. The
  // home page/board and every canvas Stage read the SAME chain
  // (canvasBackground.js STAGE_BACKGROUND), so the root can never differ
  // from what sits on it. This declaration loses to `theme: 'document'`'s
  // class rule — _setStageVar writes the same chain inline; kept here so
  // the contract is visible at the paint site and holds pre-hydration.
  background: 'var(--stage-bg, var(--color-stage))',
  height: '100dvh',
  overflow: 'hidden',
  // A stacking context of its own, so AmbientBackdrop (zIndex -1, below)
  // paints ABOVE this ground and BELOW every page layer — the order
  // HOME_AMBIENT_SKIN_SPEC §2 names (under the page stage, above the ground).
  // Every overlay mounts inside this root, so their z-order is unchanged;
  // only nodes appended to <body> itself (drag ghosts) now always sit above
  // the whole shell, which is where a drag ghost belongs.
  isolation: 'isolate',

  // Multi-workspace-tabs (Phase 3) — the initial hard-load render bypasses
  // context.functions.router (smbls calls the default router directly), so strip
  // the `/w/<org-slug>` prefix here too, so a deep-linked /w/acme/tickets matches
  // /tickets. No-op when the MWT flag is off (mirrors smbls' default
  // onRouterRenderDefault behavior).
  onRenderRouter: mwtOnRenderRouter,

  // SEARCH-1 (2026-08-14): @symbo.ls/router's router() dispatches its own
  // `routeChanged` DOMQL event (`onRouteChanged`) on the routes-bearing
  // element UNCONDITIONALLY, on every el.router() call — including the
  // client-side navigations openInSlot.js fires for forced routes (tickets/
  // tasks) and the search dropdown's navigateSearchResult. This is the shell
  // root (the element `app.routes` gets attached to by smbls' createDomql),
  // so it's the ONE place that sees every navigation regardless of which
  // page or slot ends up handling it. Bumping a counter here (rather than
  // duplicating URL-parsing logic per consumer) gives any single-shell-root-
  // instance component — TicketsDetailModal chief among them, see its
  // _restoreTicketFromRouteChange — a reactive "the URL may have changed"
  // signal to re-check against, the same problem PageSlot's own `_slotNudge`
  // solves for its keep-alive hosts.
  onRouteChanged: (el, s) => {
    s.root.update({ _routeNudge: (s.root._routeNudge || 0) + 1 })
    // D2 §5 (P1): a context menu is anchored to viewport coordinates on the
    // surface it was opened from. Measured on staging, the home widget menu
    // survived TWO page changes and sat over the module's rail. Closing here
    // covers every navigation, because this event is the router's own and
    // fires unconditionally. See functions/homeContextMenu.js.
    el.call('closeContextMenuOnRouteChange')
    // Page/route motion (HOME_AMBIENT_SKIN_SPEC §6): a NEW page root rises in —
    // fadeInUpShort at C on springBezier (functions/routeTransition.js; skips the
    // boot route, a query-only change, reduced motion and a hidden tab).
    el.call('routeTransitionEnter', el)
  },

  // Page/route EXIT motion (HOME_AMBIENT_SKIN_SPEC §6; smbls 07c4b483b,
  // FW-ROUTER-PRE-SWAP-HOOK-FOR-EXIT-MOTION-1): the router calls this BEFORE
  // the swap with the page that is leaving and awaits the returned promise
  // for at most `exitTimeout` (300 ms); it skips the hook itself under reduced
  // motion and in a hidden tab. The leaving page fades (functions/
  // routeTransition.js routeTransitionExit: opacity only, B on defaultBezier,
  // resolves on finish or cancel); the new page then rises in through
  // onRouteChanged above.
  onRouteExit: (el, s, ctx, exit) =>
    exit && exit.page ? el.call('routeTransitionExit', exit.page) : undefined,

  // THE shell's ONE document keydown listener (HOME_AMBIENT_SKIN_SPEC §5),
  // declared here so DOMQL owns its lifecycle — registered once when the
  // shell root takes its node, torn down on dispose, never a raw
  // document.addEventListener (FA503). Bubble phase: a surface's own key
  // handling (a card's onKeyDown, an AppDialog's Esc) runs first and claims
  // the key with preventDefault, which the registry honours. Two steps, in
  // the order the two listeners ran before the registry's raw line moved
  // here from wireGlobalListeners.js:
  //   1. Escape for the shared ContextDropdown singleton. The panel's own
  //      Escape lives on `Menu.onKeydown`, a DELEGATED handler — it only ever
  //      sees a keystroke when focus is already INSIDE the menu, which is a
  //      bounded-poll race (`focusHomeContextMenu`). Measured on staging:
  //      Escape did not close the stray menu. The shell root is mounted on
  //      every route, so this backstop is armed everywhere.
  //   2. The keyboard registry (functions/shortcuts.js): the guard, both
  //      switches and every binding. No state snapshot — the handler reads
  //      the shell's LIVE root state itself.
  onDocumentKeydown: (e, el) => {
    el.call('closeContextMenuOnEscape', e)
    el.call('handleShortcutKeydown', el, null, e)
  },

  // mailto: — the in-app composer is the DEFAULT action of a plain mailto
  // click (functions/mailtoComposeDefault.js). The same shape as the keyboard
  // above: ONE bubble-phase document listener DOMQL owns, so a surface's own
  // onClick runs first and claims the click with preventDefault (the CRM
  // contact dialog's Email action opens the CRM composer), which the shell
  // honours. It used to be a raw capture listener in wireGlobalListeners.js
  // that took every mailto click before any surface saw it.
  onDocumentClick: (e, el) => el.call('mailtoComposeDefault', e),

  // D2 §8.4 — below SPLIT_MIN_WIDTH_PX there is no split, and a WINDOW that
  // gets narrow is one of the ways to be below it. `openAppPage`'s width
  // guard only ever gated the opening of a second pane, so a split armed on a
  // wide window survived a resize: measured on the deployed staging shell at
  // a verified `window.innerWidth` of 1000, PanePrimary 491px + PaneSecond
  // 491px, while the module's own `vaultCanSplit()` answered false beside it.
  // Folding it here, at the shell root, covers the resize the same way the
  // opener covers a gesture — ONE floor, both directions. The function
  // self-guards (it writes only when something is actually open), so a resize
  // drag folds once and every later tick is a no-op. DOMQL owns the
  // listener's lifecycle — registered once, torn down on dispose, never a raw
  // window.addEventListener (FA503), same contract as the Escape backstop
  // above.
  onWindowResize: (e, el) => el.call('foldSplitOnResize'),

  // SPA navigation — BOTH guards, in their original arming order. AppShell's
  // patched history dispatches `locationchange` on push/replaceState;
  // `popstate` covers back/forward. One declared prop carries one handler per
  // event, so the composition lives here rather than in two registrations.
  // DOMQL owns both listeners: registered once with the node, torn down on
  // dispose — never a raw window.addEventListener (FA503), the same contract
  // as the Escape backstop and the resize fold above.
  onWindowLocationchange: _onSpaNavigation,
  onWindowPopstate: _onSpaNavigation,

  // THE ROOT WAITS FOR THE ONE BOOT. A parcel boot (bootShell) settled the
  // boot sequence before create(), so the shell's create pass runs at once,
  // exactly as before. On the published shell the boot plugin
  // (context.js `workspaceBoot`) started it in beforeCreate: the root applies
  // it first (bootApply — the session, org, workspace and language land on
  // the live root) and only then runs its own create pass, which reads
  // `context.preResolvedAuth` and the four synchronous SDK reads.
  onCreate: (el, s) => {
    const ctx = el.context || {}
    if (ctx.bootMode !== 'published' || !ctx.bootReady) return _shellOnCreate(el, s)
    // Three outcomes (one-boot review D2, D9):
    //  • the boot NAVIGATED away (a rejected session: logout + /login, or the
    //    fresh-token reload): the page is leaving — no create pass, nothing
    //    signed in runs or renders from a stored token the server refused;
    //  • the boot FAILED (a step threw: bootReady settled null): fail closed —
    //    signed out, no create pass;
    //  • otherwise the create pass runs — signed in after bootApply, or a
    //    signed-out visitor who stays (a guest on /login, an invite, a soft
    //    bounce), whose session subscription an in-place sign-in needs.
    return ctx.bootReady.then((res) => {
      if (!res) {
        ctx.bootSignedIn = false
        console.error('[shell] the boot failed — the shell stays signed out')
        return null
      }
      // the session check never answered (bootDeadline.js): the loader shows
      // its error + reload state; nothing signed in runs
      if (res.navigated || res.failed) return null
      return Promise.resolve(
        res.aborted ? null : el.call('bootApply', el, res, { seed: ctx.bootSeed })
      )
        .catch((err) => console.warn('[shell] bootApply failed:', (err && err.message) || err))
        .then(() => _shellOnCreate(el, s))
    })
  },
  onRemove: (el) => {
    el.call?.('uninstallAppShellSync')
    if (typeof el._unsubShellAuth === 'function') {
      try {
        el._unsubShellAuth()
      } catch (_) {}
    }
    try {
      if (el._handleOrgSwitched) el.sdk?.rootBus?.off?.('sdk.orgSwitched', el._handleOrgSwitched)
      if (el._handleWorkspaceSwitched)
        el.sdk?.rootBus?.off?.('sdk.workspaceSwitched', el._handleWorkspaceSwitched)
    } catch (_) {}
    try {
      el._unsubLangPolicy?.()
    } catch (_) {}
    try {
      el._unsubUserRealtime?.()
    } catch (_) {}
    try {
      el._presenceUnsub?.()
    } catch (_) {}
    try {
      el._unsubAnalyzingOrgSwitch?.()
    } catch (_) {}
    if (el._notifPollTimer) {
      clearInterval(el._notifPollTimer)
      el._notifPollTimer = null
    }
    if (el._notifBootTimer) {
      clearInterval(el._notifBootTimer)
      el._notifBootTimer = null
    }
    try {
      el._meetRealtimeUnsub?.()
      el._meetRealtimeUnsub = null
    } catch (_) {}
    try {
      el._unsubMeetOnWorkspace?.()
      el._unsubMeetOnWorkspace = null
    } catch (_) {}
    if (el._installModalTimer) {
      clearInterval(el._installModalTimer)
      el._installModalTimer = null
    }
    if (el._meetingInviteTickTimer) {
      clearInterval(el._meetingInviteTickTimer)
      el._meetingInviteTickTimer = null
    }
    try {
      el.call?.('stopWorkspaceVoice')
    } catch (_) {}
  },

  // A11y skip-link — keyboard-first nav past the chrome. FIRST child (first
  // tab stop): a skip link that tabs in after the content it skips is
  // useless, and axe's `region` skip-link exemption additionally requires
  // (a) the `#main-content` fragment target to RESOLVE (stamped on every
  // route def — context.js `_anchorMainContent`) and (b) the link to precede
  // the first page link in DOM order (axe `_isSkipLink`). Both verified live
  // (MARKETPLACE-AUDIT residue, tickets/fable.md 2026-08-09).
  // position:absolute — out of flow, so being first child changes no layout.
  // The target follows the open window (functions/skipToMainHref.js): with
  // the primary PageSlot pane open the link jumps into the pane, never to the
  // page behind it. `subscribeTo` re-renders the link when the pane's route
  // changes (the house gate for a root-key read here, PageSlot SplitBtn).
  SkipToMain: {
    tag: 'a',
    subscribeTo: ['_canvasSlot'],
    href: (el, s) => el.call('skipToMainHref', s.root),
    text: '{{ workspace.a11y.skipToMain | polyglot }}',
    position: 'absolute',
    top: '0',
    left: '0',
    zIndex: '99999',
    padding: 'Y B',
    background: 'document',
    color: 'title',
    fontSize: 'Z1',
    fontWeight: '700',
    transform: 'translateY(-100%)',
    ':focus': {
      transform: 'translateY(0)'
    }
  },

  // The ambient sky (DESIGN-WL-AMBIENT-SKIN-BACKDROP-1): one static,
  // aria-hidden, pointer-less layer under the page stage. Shown only under
  // the ambient skin by the injected sheet above; its own off switches (the
  // record's sky, reduced transparency, a ground override, the files-plane
  // mask) live in the component, on `hide:` — the component declares no
  // `if:`, so this mount's `if:` clobbers nothing.
  //
  // Staged at INTERACTIVE, not eager (the boot-task budget,
  // __tests__/shellStageLadder.test.js): the sky can never show before the
  // skin stamp that gates it, and ShellSkinSync lands at the same stage.
  //
  // THE TOP SKY + THE GROUND (DESIGN-WL-HOME-SKY-TOP-GROUND-1, Nika
  // 2026-10-01): the sky hangs from the top edge (`fadeTo: 'top'` — the
  // horizon band mirrored, fading out downward behind the greeting), and the
  // bottom belongs to AmbientGround — the loader's mountain range, which
  // rises into view over the last stretch of the home scroll (the sheet's
  // scroll-driven rules above). The sky paints on the home only in practice
  // (every app route's content host paints its own sheet over it), so one
  // placement serves every route. The ground's gate is the home route. The
  // band stops at the greeting's summary (`fadeAnchor`, measured — the clear
  // edge), and the person may keep the bottom band instead (Personalise →
  // Background; functions/ambientBackdrop.js ambientSkyPlacement). With the
  // head off (the greeting hidden, an app-mode workspace's default) the edge
  // is the first home row that has a box: the App strip, the boards row, the
  // grid (pages/main.js MainColumn order) — never the whole band behind them.
  AmbientBackdrop: {
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    fadeTo: 'top',
    fadeAnchor: ['HomeTopSummary', 'HomeAppStrip', 'AssistantBoardsRow', 'GridContainer']
  },
  AmbientGround: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  PhoneNavigation: {
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    canCompose: (el, s) => _composerAllowed(s.root, s.root?.currentPath, el),
    Dock: {
      onRender: (el) =>
        _trackClearVar(el, '_phoneObs', '--phone-clear', (r) => window.innerHeight - r.top),
      onWindowResize: (e, el) => el._phoneObsApply?.(),
      onBeforeRemove: (el) => _stopClearVar(el, '_phoneObs')
    }
  },

  AppHeader: {
    hide: (el, s) => {
      // Immersive module chrome: the module manifest opts matched members
      // out of the shell header entirely (chrome.immersive — admins always
      // keep it). Reactive via subscribeTo 'workspaceProject' below: the
      // key is absent until the module loads, so the factory alone would
      // register no dependency on it.
      if (moduleImmersiveActive(s.root)) return true
      const path = s.root?.currentPath
      return isChromelessShellPath(
        typeof path === 'string'
          ? path
          : typeof window !== 'undefined'
            ? window.location.pathname
            : '/'
      )
    },

    // Search dropdown over an open PageSlot (2026-08-07 per Nika "search
    // suggestions dropdown should be above pageslot"). The header is a 1100
    // stacking context BY DESIGN — fullscreen slots (PageSlot 1300, top:0)
    // must cover it — so no zIndex on the dropdown itself can ever escape
    // the cap. Instead the WHOLE header lifts into the popover band
    // (> Suggestions 1349 / ReasoningPill 1350) only while a header search
    // is open: that state cannot co-occur with a fullscreen slot, because
    // under one the header is unreachable (pointer-blocked below 1300), so
    // the fullscreen-covers-header contract is never violated. Local mount
    // override — same surgical no-shared-edit pattern as NewCall below.
    // _moduleChromePref: the admin chrome toggle's reactive mirror — the
    // declared list is the binding contract, so without it the header never
    // remounts when an admin toggles the shell chrome back (measured).
    // 'currentPath' — item 11: moduleImmersiveActive is route-gated now, so
    // the header must re-resolve when navigating between the module surface
    // ('/') and own-sidebar routes (/studio, /infra) or it stays hidden.
    subscribeTo: [
      '_navSearchOpen',
      '_headerSearchOpen',
      'workspaceProject',
      '_moduleChromePref',
      'currentPath',
      // the navbar's place: both measurements below re-run when it moves (a
      // top → bottom move keeps the box size, so no ResizeObserver fires)
      '_homeLook',
      'userPrefs',
      '_isPhoneViewport'
    ],
    zIndex: (el, s) => (s.root?._navSearchOpen || s.root?._headerSearchOpen ? '1360' : '1100'),

    // Maintain --nav-clear: viewport TOP → this header-navbar's BOTTOM edge.
    // --slot-top derives from it (+ --nav-gap), so the PageSlot's top gap to
    // the navbar EQUALS its bottom gap to the composer — both live-measured,
    // never an approximated constant (2026-07-12 per Nika "equal top and
    // bottom gaps from navbars").
    // …and --nav-edge, off the SAME observer (the tracker's second variable):
    // the navbar's reach from its own edge once the person moves it to a side
    // or the bottom (_measureHeaderNavEdge; 0 at the top). onUpdate re-runs
    // both when the place moves — a top → bottom move keeps the box size, so
    // the observer alone never fires.
    onRender: (el) =>
      _trackClearVar(el, '_ncObs', '--nav-clear', (r) => _measureHeaderNavClear(el, r), {
        varName: '--nav-edge',
        measure: (r) => _measureHeaderNavEdge(el, r)
      }),
    onUpdate: (el) =>
      _trackClearVar(el, '_ncObs', '--nav-clear', (r) => _measureHeaderNavClear(el, r), {
        varName: '--nav-edge',
        measure: (r) => _measureHeaderNavEdge(el, r)
      }),
    // The resize half of the measurement above (FA503 — framework-owned).
    onWindowResize: (e, el) => el._ncObsApply?.(),
    // Preserve the shared AppHeader.onRemove (its island observer cleanup).
    onBeforeRemove: (el) => _stopClearVar(el, '_ncObs'),

    // Ongoing-call badge on the "Start a call" icon — lit when there's a LIVE
    // call in THIS workspace the user hasn't joined (shell-level meet realtime,
    // see onCreate). Local override of the shared NewCall (same surgical pattern
    // as the demo lockdown below — no shared-library edit). Clones
    // Messenger.UnreadBadge's red pill; NewCall extends IconButton
    // (position:relative) so the absolute badge anchors. subscribeTo re-renders
    // NewCall when the room/participant signals change so the badge tracks live.
    // NOTE: a single HeaderLeft key — DOMQL merges nested objects, but this is
    // a plain JS object LITERAL: two top-level `HeaderLeft:` keys in the same
    // AppHeader override would silently clobber each other (last one wins),
    // not merge. The ongoing-call badge below and the demo-lockdown mirror
    // further down both have to live in this ONE block.
    HeaderLeft: {
      // The logo opens a FRESH Fly on the home in Fly mode (Nika 2026-10-23:
      // "clicking logo or home icon should be opening fresh fly screen";
      // functions/assistantFlyWorkspace.js assistantFlyHomeFresh). It stays
      // a link: a modified or middle click opens the home in a new tab.
      Logo: {
        onClick: (e, el) => el.call('assistantFlyHomeClick', e)
      },
      HeaderActions: {
        // Unread-mail dot on the header envelope (architecture/MAIL.md §3.6).
        // Local override of the shared Mail button — the same surgical
        // no-shared-edit pattern as NewCall's badge below.
        //
        // The AppNavbarButton.UnreadBadge idiom EXACTLY (shared/components/
        // AppNavbar.js): a bare red dot, not a count. A dot carries no text,
        // so the 3:1 non-text contrast floor applies and brand `red` clears
        // it — the deepened `redDeep` that Messenger/NotificationBell must use
        // is only needed because those badges print a NUMBER on the plate.
        // Mail is deliberately the quieter signal: an inbox count is noise
        // beside a chat mention, and the exact number is one click away in
        // the drawer's own sidebar pill.
        //
        // `subscribeTo` sits on the BADGE, not on Mail. The shared button
        // already declares its own list ('_featureFlags', 'workspace',
        // '_mailDrawerOpen') and an array does not merge — restating it here
        // to append one key would silently freeze if that list ever changed.
        // The child subscribes to what only the child reads, which is the
        // idiom mail's own MailFolderRow.UnreadBadge uses.
        //
        // Mail extends IconButton (position: relative), so the absolute dot
        // anchors to the button, exactly as OngoingCallBadge does on NewCall.
        Mail: {
          MailUnreadBadge: {
            subscribeTo: ['navUnreadCounts'],
            hide: (el, s) => !Number(s.root?.navUnreadCounts?.mail || 0),
            // The dot is decorative to the eye and load-bearing to a screen
            // reader — the button's own label says only "Mail". `role:
            // 'status'` announces the change when the count first arrives.
            role: 'status',
            aria: { label: (el) => el.call('polyglot', 'workspace.header.mail.unread') },
            position: 'absolute',
            top: '0',
            right: '0',
            width: 'Y',
            height: 'Y',
            round: '50%',
            background: 'red',
            pointerEvents: 'none'
          }
        },
        // Human/Agent mode is selected in the account menu.
        // Chat has one shell surface: the MessengerOverlay. Keep the header
        // entry point from creating a second chat in the PageSlot when a
        // workspace navbar also exposes /chat.
        Messenger: {
          onClick: async (e, el, s) => {
            e.stopPropagation()
            const root = s && s.root
            if (!root) return
            const opening = !root._chatDrawerOpen
            if (opening) {
              const okFold = await el.call('ensureChatContext')
              if (okFold === false) return
              root.update({
                _canvasSlot: null,
                _canvasSlotSecond: null,
                _slotFullscreen: null,
                _chatDrawerOpen: true,
                activeChannelId: null
              })
              return
            }
            root.update({ _chatDrawerOpen: false })
          }
        },
        NewCall: {
          subscribeTo: ['rooms', 'liveParticipants', 'activeRoomId'],
          OngoingCallBadge: {
            if: (el, s) => _liveCallCount(s.root) > 0,
            position: 'absolute',
            top: '0',
            right: '0',
            minWidth: 'Z',
            height: 'Z',
            padding: '0 V',
            round: 'radiusPill',
            background: 'red',
            color: 'fillInk',
            fontSize: 'W',
            fontWeight: '700',
            lineHeight: '1',
            align: 'center center',
            pointerEvents: 'none',
            text: (el, s) => {
              const n = _liveCallCount(s.root)
              return n > 9 ? '9+' : String(n)
            }
          }
        }
      },

      // DEMO LOCKDOWN mirror — the phone "one deck" merge (2026-08-02) mounts
      // a SECOND AppOrgSwitcher as this island's mobile leading-zone identity
      // affordance (AppHeader.js). Same isDemo + foreign-URL neutralization as
      // HeaderRight's instance below, restated here because it's a genuinely
      // separate mount (its own Trigger/InviteBtn DOM), not reachable through
      // the HeaderRight override path — without this a demo session, or a
      // visit to a URL naming an org you're not a member of, could still
      // org-switch (or show YOUR org's name) from the leading zone.
      AppOrgSwitcher: {
        Trigger: {
          pointerEvents: (el, s) => (_neutralizeOrgSwitcher(s.root) ? 'none' : null),
          cursor: (el, s) => (_neutralizeOrgSwitcher(s.root) ? 'default' : 'pointer'),
          'aria-haspopup': (el, s) => (_neutralizeOrgSwitcher(s.root) ? null : 'menu'),
          'aria-label': (el, s) => el.call('polyglot', _orgSwitcherAriaLabel(s.root)),
          // + the navbar rail's narrow posture: a monogram, no chevron
          // (shared functions/navbarLayout.js navbarRailLabel)
          Icon: {
            hide: (el, s) =>
              _neutralizeOrgSwitcher(s.root) ||
              (el.call('navbarLayoutRead', s.root) || {}).railMode === 'narrow'
          },
          Label: {
            text: (el, s) => el.call('navbarRailLabel', _orgSwitcherLabel(el, s.root), s.root)
          }
        },
        InviteBtn: {
          hide: (el, s) => _neutralizeOrgSwitcher(s.root)
        }
      }
    },

    // The home icon, like the logo: a fresh Fly on the home in Fly mode.
    HeaderTabs: {
      HomeBtn: {
        onClick: (e, el) => {
          e.preventDefault()
          e.stopPropagation()
          el.call('assistantFlyHomeFresh')
        }
      },
      // Apply the action to real tab rows; a HeaderTab child here renders
      // an empty extra button beside the app launcher.
      Tabs: {
        childProps: {
          scope: {
            openTab: async (e, el, s) => {
              if (s.placeholder || !s.path || s.root?._navbarReorder) return
              e.preventDefault()
              if (s.path !== '/chat') {
                el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
                return
              }
              s.root.update({ _chatDrawerOpen: false })
              el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
            }
          }
        }
      },
      MobileTabs: {
        scope: {
          openTab: async (e, el, s) => {
            if (s.placeholder || !s.path || s.root?._navbarReorder) return
            e.preventDefault()
            if (s.path !== '/chat') {
              el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
              return
            }
            s.root.update({ _chatDrawerOpen: false })
            el.call('openAppPage', s.path, { preferSlot: true, split: e.metaKey || e.ctrlKey })
          }
        }
      }
    },

    // DEMO LOCKDOWN + FOREIGN-URL NEUTRALIZATION — lock the org-identity
    // chrome to a static, non-interactive label whenever it would otherwise
    // lie about whose org you're looking at:
    //   • `root.isDemo` — lock the workspace to the single demo org so a demo
    //     visitor can ONLY ever see their own demo org.
    //   • `_foreignOrgUrl(root)` — the URL names an org you're not a member
    //     of (tickets/workspace.md "neutral chrome…", residual from
    //     9deb1386). `root.org` still holds YOUR active org (no switch is
    //     possible — `mwtOrgToReconcile` correctly refuses it), so left alone
    //     the Trigger would show YOUR org's name beside a stranger's content.
    // The shared AppOrgSwitcher's dropdown is what exposes switch-to-other-
    // org / + Create organization / Delete organization / Admin; the
    // InviteBtn exposes invite/join. We neutralize BOTH here, surgically and
    // reactively, WITHOUT editing the shared component:
    //   • Trigger → render as a STATIC, generic label: drop pointer-events
    //     (its onClick that opens the dropdown can't fire), reset the
    //     cursor, hide the chevron, clear the menu a11y hints, and swap the
    //     visible label for the shared component's own "no specific org
    //     resolved" fallback string ('Organization') — reusing existing
    //     product copy rather than inventing new text, and never showing
    //     either party's actual org name in the foreign-URL case (see
    //     `_orgSwitcherLabel`'s comment for why NOT read-only owner
    //     branding). Neither gate touches the other's session (every
    //     override reactively re-derives from live root state).
    //   • InviteBtn → hidden.
    // Real, non-demo sessions on a URL naming their OWN org (or no org at
    // all) keep the full switcher + invite affordances, unchanged.
    HeaderRight: {
      AppOrgSwitcher: {
        Trigger: {
          pointerEvents: (el, s) => (_neutralizeOrgSwitcher(s.root) ? 'none' : null),
          cursor: (el, s) => (_neutralizeOrgSwitcher(s.root) ? 'default' : 'pointer'),
          'aria-haspopup': (el, s) => (_neutralizeOrgSwitcher(s.root) ? null : 'menu'),
          'aria-label': (el, s) => el.call('polyglot', _orgSwitcherAriaLabel(s.root)),
          // Chevron signals "opens a menu" — hide it so the static label
          // doesn't imply a switcher.
          // + the navbar rail's narrow posture: a monogram, no chevron
          // (shared functions/navbarLayout.js navbarRailLabel)
          Icon: {
            hide: (el, s) =>
              _neutralizeOrgSwitcher(s.root) ||
              (el.call('navbarLayoutRead', s.root) || {}).railMode === 'narrow'
          },
          Label: {
            text: (el, s) => el.call('navbarRailLabel', _orgSwitcherLabel(el, s.root), s.root)
          }
        },
        InviteBtn: {
          hide: (el, s) => _neutralizeOrgSwitcher(s.root)
        }
      }
    }
  },

  // DEMO LOCKDOWN — route guard. Even with the org-switcher dropdown
  // neutralized, a demo visitor could still reach admin / other-org / cross-org
  // surfaces by typing the URL (or via a stale history entry). This invisible
  // route-reactive sentinel redirects `root.isDemo` sessions away from those
  // entry points back to `/`. Gated strictly on isDemo, so real users are
  // never bounced. Uses el.router (SPA, framework-correct) — no full reload.
  DemoRouteGuard: {
    tag: 'div',
    display: 'none',
    subscribeTo: ['currentPath', 'isDemo', '_authReady'],
    onCreate: (el, s) => _guardDemoRoute(el, s),
    onUpdate: (el, s) => _guardDemoRoute(el, s)
  },

  // Invisible route-kind sync — keeps <html data-shell-route> current so
  // the injected content CSS offsets app pages below the AppHeader.
  // subscribeTo + onUpdate is the reliable route-reactive path; a reactive
  // style/grid wouldn't re-apply on navigation in v3.14.
  ShellRouteSync: {
    tag: 'div',
    display: 'none',
    // `workspaceApps` + `workspaceProject`: the route kind reads the INSTALLED
    // app routes (_isProjectCanvasPath → moduleRoutesFromRoot), and on a cold
    // load they arrive after the first sync. Without these keys a deep link to
    // a 2-segment app page (/validators/networks) stayed classified as an
    // <owner>/<project> canvas — full-bleed under the header — until some
    // other key moved (Storm on dev, 2026-10-04).
    // `_moduleComposerHidden`: a module page's composer latch moves the
    // <html data-prompt> mirror below (functions/moduleShellComposer.js).
    // `_isPhoneViewport` + `_phoneComposerOpen`: the phone dock's park moves
    // the same mirror (_composerVisible). `assistantMode` + `_homeThreadOpen`:
    // the home's Build tab — the app builder — stands the composer down too
    // (functions/homeBuild.js homeBuildShown).
    subscribeTo: [
      'currentPath',
      'workspaceApps',
      'workspaceProject',
      '_moduleComposerHidden',
      '_isPhoneViewport',
      '_phoneComposerOpen',
      'assistantMode',
      '_homeThreadOpen',
      '_chatDrawerOpen',
      '_mailDrawerOpen',
      'assistantOpen',
      'assistantDock',
      'assistantWidth',
      '_canvasSlot',
      '_canvasSlotSecond',
      // the navbar's place (<html data-nav-place>, read through the look)
      '_homeLook',
      'userPrefs'
    ],
    onCreate: (el, s) => {
      // Reflect device pointer type onto <html data-pointer="coarse|fine"> once
      // at shell mount, so touch CSS + the @coarse/@touch media tokens resolve
      // from first paint. Idempotent (wires its media listener once).
      syncPointerCapability()
      _syncShellRoute(s.root, el)
    },
    onUpdate: (el, s) => _syncShellRoute(s.root, el)
  },

  // Invisible skin sync — keeps <html data-skin> on the workspace's choice
  // (see _syncShellSkin): the record arriving, a workspace switch, the admin
  // card's write, a branded host resolving, a workspace module (and its own
  // ground) loading. Staged at INTERACTIVE, not eager
  // (the boot-task budget, __tests__/shellStageLadder.test.js): the record it
  // waits for lands after first paint anyway, and one macrotask later costs
  // the stamp nothing.
  ShellSkinSync: {
    tag: 'div',
    display: 'none',
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    subscribeTo: ['workspace', 'workspaceSettings', 'whitelabelBrand', 'workspaceProject'],
    onCreate: (el, s) => _syncShellSkin(s.root),
    onUpdate: (el, s) => _syncShellSkin(s.root)
  },

  // Invisible thread-URL sync — every main-thread conversation has its own
  // URL, `?ai=<conversationId>`: a pasted link, a reload and Back / Forward
  // open that thread, and the thread writes its URL (components/
  // AssistantThreadUrlSync.js + functions/assistantThreadUrl.js hold the rule).
  // Staged at INTERACTIVE: a link waits for the AI service and the workspace
  // anyway, and both land after first paint.
  AssistantThreadUrlSync: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // Compact "AI is thinking" pill — shown above the composer when a GENERAL
  // Simone turn is live but the assistant drawer is closed (e.g. a PageSlot /
  // split page is open), so the user still sees what the AI is doing without the
  // full chat. Clicking it opens the drawer. One row at the control height: its
  // words float above it as bubbles, with a step that waits for the person's OK
  // nearest the bar, and a landed answer turns its head into "Answer ready" +
  // Open in main / Open in sidebar (functions/assistantAnswerPopover.js — the
  // separate answer capsule that used to stack over it is gone). (Home +
  // /add-app render the timeline inline in their own surfaces.)
  SimoneReasoningPill: { if: _atStage(SHELL_STAGE.CHROME) },
  // INSPECT & ASK's layer over the page (components/AiInspectLayer.js,
  // functions/aiInspect.js; DESIGN-COMPOSER-FOCUS-POPOVER-CONTEXT-INSPECT-1).
  // It gates itself on the mode (`if:` — its document listeners exist only
  // while inspecting); a mount-site gate here would replace that one.
  AiInspectLayer: {},

  // SimoneSuggestions — the IDLE companion to the pill — is NO LONGER mounted
  // here as a position:fixed sibling (O1, opus.md 2026-08-16): it now renders
  // IN-FLOW inside CanvasPromptTextarea.PromptBand, the one owner of the strip
  // above the composer, ordered after the AiContextBar. Same gating
  // (_promptFocused / assistantOpen / running turn); no longer paints over the
  // composer subtree at zIndex 1449.

  // AI prompt — GLOBAL (2026-06-06 per Nika): the bare
  // CanvasPromptTextarea mounted once at the shell root — no
  // BottomPrompt gradient band — so it shows on EVERY page, canvas
  // pages included (their own mounts were removed; CanvasEditor +
  // studios now rely on this one). Hidden only on auth routes.
  // position:fixed (native is absolute) anchors it to the viewport
  // regardless of the shell's scroll state.
  CanvasPromptTextarea: {
    // Delegates to _composerVisible (module scope, above) — the SAME
    // predicate _syncShellRoute mirrors onto <html data-prompt> for the
    // app-page bottom-padding CSS rule, so the two can never drift. Strips
    // the multi-workspace-tabs `/w/<slug>` prefix internally BEFORE
    // route-matching — on `/w/bellforge/add-app` the raw path never equals
    // '/add-app', so the composer floated over that page's own prompt
    // (verified live 2026-07-24). Hidden on auth routes and on surfaces that
    // already have their OWN conversation composer, where the global
    // CanvasPrompt is a redundant second prompt that overlaps the page's own
    // input (/add-app — Simone chat in the left column; /chat — the
    // channel/DM message composer at the bottom of the conversation), on
    // /meet-guest (anonymous guests have no workspace AI), and for signed-in
    // non-members (2026-07-29 per Nika "canvas prompt is only visible for
    // members" — same gate the header actions + presence dock use).
    hide: (el, s) => !_composerVisible(s.root, s.root?.currentPath, el),
    // Immersive-chrome reactivity: _composerVisible reads
    // root.workspaceProject.chrome, a key absent until the module loads —
    // the factory alone registers no dependency on it. Same for a module
    // page's composer latch (`_moduleComposerHidden`, read behind earlier
    // returns) and the pane routes it is bound to. The phone dock's park
    // (`_isPhoneViewport`, `_phoneComposerOpen`) is read last, behind every
    // other return. The home's Build tab (`assistantMode`, `_homeThreadOpen` —
    // functions/homeBuild.js homeBuildShown) is read behind the route checks.
    subscribeTo: [
      'workspaceProject',
      '_moduleComposerHidden',
      '_canvasSlot',
      '_canvasSlotSecond',
      '_isPhoneViewport',
      '_phoneComposerOpen',
      'assistantMode',
      '_homeThreadOpen'
    ],
    // DESIGN-WL-ASSISTANT-MAIN-VIEW-1 — the recent boards as chips in the
    // strip above the composer (the PromptBand owns that strip), bottom-most
    // so they sit on the composer. They gate themselves (Page mode on the
    // home, a board to go back to): no `if:`/`hide:` here — a mount-site gate
    // REPLACES the component's own.
    PromptBand: {
      AssistantThreadChips: {},
      // What the member HANDED the assistant — an inspected region, a dropped
      // ref, a picked record — right above the field, focused or not (the
      // context itself lives in the focus panel; DESIGN-COMPOSER-FOCUS-
      // POPOVER-CONTEXT-INSPECT-1). Last, so it sits on the composer. The
      // strip floats over the page, so it sits on the composer's own `pill`
      // plate like AssistantThreadChips: a chip's wash alone let a table row
      // read through the "Fleet health" chip (measured on dev 2026-10-04).
      AiContextBar: {
        attachedOnly: true,
        theme: 'pill',
        round: 'radiusPill',
        padding: 'X',
        alignSelf: 'flex-start',
        maxWidth: '100%'
      }
    },
    // Bridge for the landing hand-off (WS-WORKSPACE-CONSUMES-THE-PROMPT-
    // QUERY-FROM-THE-LANDING-HERO-COMPOSER-1). The shell root's own onCreate
    // (WS-LANDING-PROMPT-HANDOFF, above this component in the tree) already
    // owns reading/stripping/capping `?prompt=` — it must stay the ONE
    // reader, or a second independent read races it and strips the param out
    // from under whichever loses. That handler runs the AI transport in a
    // DIFFERENT DOMQL state scope than this composer's own local `draft`
    // (declared on THIS component, canvas/components/CanvasPromptTextarea.js
    // `state: { draft: '', … }`), so it reaches in through this one function
    // instead of a competing URL read. Registered directly on root (not
    // `s.root.update`) because a function is dropped by state's clone.
    // This component still never reads the URL — it reads the PARK the root
    // writes when it finds no bridge, which keeps the single-reader rule and
    // fixes the 716 ms the root used to lose the sentence in.
    onCreate: (el, s) => {
      const prefill = (text) => {
        if (!text) return false
        s.update({ draft: text })
        // Deferred a frame: the textarea node for this fresh s.update isn't
        // guaranteed painted yet (same reasoning as aiComposerKeys.js's
        // applyAiMentionPick caret restore, and the same windowless-runtime
        // fallback — a bare requestAnimationFrame reference throws under bun
        // test / bypass mode).
        const raf =
          typeof requestAnimationFrame === 'function'
            ? requestAnimationFrame
            : (fn) => setTimeout(fn, 16)
        raf(() => {
          const ta = el.node && el.node.querySelector && el.node.querySelector('textarea')
          if (!ta) return
          ta.focus()
          try {
            ta.setSelectionRange(text.length, text.length)
          } catch (_) {}
        })
        return true
      }
      s.root._prefillHomePrompt = prefill
      // DRAIN. The shell root reads `?prompt=` ~716 ms before this component
      // exists (measured, dev.my 2026-09-23), so the bridge above is almost
      // always registered too late for that read. The root parks the sentence
      // when nobody takes it; this is where the composer picks it up. One
      // shot — `takeParkedInboundPrompt` removes the row as it reads it, so a
      // reload never prefills twice.
      let _parkStore = null
      try {
        _parkStore = typeof window !== 'undefined' ? window.sessionStorage : null
      } catch (_) {
        /* sandboxed frame — nothing was parked either */
      }
      prefill(takeParkedInboundPrompt(_parkStore))
    },
    position: 'fixed',
    // Above the PageSlot frame (1300) so the composer never sits under the
    // slot sheet's shadow scrim (2026-08-06 per Nika "make sure chattexarea
    // is higher zIndex as pageslot ... means zIndex not position"); still
    // under Modal (2000) and every dropdown/window layer.
    zIndex: '1400',
    // Maintain --prompt-clear (registered with the @property vars above): the
    // distance from the viewport bottom to this composer's TOP edge, so the
    // PageSlot's bottom lands exactly on the textarea line. A ResizeObserver
    // (not a one-shot) because the textarea grows with multiline input; the
    // value is height + bottom-inset, so window resizes don't change it.
    // documentElement.style is shell-owned here (same file already injects the
    // shell <style>); skip while hidden (rect collapses to 0).
    // Maintain --prompt-clear: viewport BOTTOM → this composer's TOP edge
    // (value = height + bottom inset, so window resizes don't move it; the
    // resize re-apply covers textarea reflow on width changes).
    //
    // RESTING pill only (2026-09-02 per Nika "when canvastextarea is
    // expanded, lets not resize sidebars and pageSlot - lets textarea
    // increase above them"): an Expand used to push --prompt-clear to the
    // expanded card's top, and every --slot-bottom consumer (PageSlot, the
    // module rail, the app sheet, the messenger list) shrank with it. The
    // expanded card is a transient overlay — it sits at z 1400, above the
    // slot band — so the measurement now returns `null` (= keep the last
    // resting value) while `size` is not 'small'. Minimize re-measures the
    // pill through the same RO. A multi-line SMALL draft still grows the
    // clearance: that is the pill itself getting taller, not an overlay.
    //
    // The SAME observer also publishes the resting pill's WIDTH as --prompt-w
    // (the fifth argument, _PROMPT_WIDTH): the island docked above the composer
    // takes it (the shell rule on [data-gauge="composer"]); the same null keeps
    // the resting value while the composer is expanded.
    //
    // The clearance reaches the top of the composer's STACK: the rows docked
    // on the pill count while they show (_composerStackTop), and the band is
    // watched too (the sixth argument) — a row coming or going resizes the
    // band, never the pill.
    onRender: (el) =>
      _trackClearVar(
        el,
        '_pcObs',
        '--prompt-clear',
        (r) =>
          el.state && el.state.size && el.state.size !== 'small'
            ? null
            : window.innerHeight - _composerStackTop(el, r),
        _PROMPT_WIDTH,
        el.PromptBand && el.PromptBand.node
      ),
    // The resize half of the measurement above (FA503 — framework-owned).
    onWindowResize: (e, el) => el._pcObsApply?.(),
    onBeforeRemove: (el) => _stopClearVar(el, '_pcObs'),
    // Resting shadow comes from the canvas-card-glass theme — the SAME
    // layer the HeaderLeft/Tabs/Right islands and the HeaderSearch pill
    // rest on — instead of the old custom '0 12px 32px rgba(0,0,0,0.35)'
    // override, which read heavier than the islands and dropped the
    // theme's glass inset ridge (iPad, 2026-08-01 per Nika "canvas prompt
    // textarea not consistent with others"). Nika's resting drop shadow
    // (2026-06 "lost when the BottomPrompt wrapper went away") is still
    // there — it's the theme's own drop layer now. Focus swaps in the
    // ring stack below exactly like HeaderSearch's :focus-within.
    // Soft focus glow on the WHOLE prompt card when the textarea
    // inside is focused — reads cleaner than the default
    // browser outline drawn only around the textarea node. Two
    // stacked shadows:
    //   • inner 1px ring in `blue` at 35% opacity for the
    //     accent edge
    //   • outer 24px halo in the same blue at 16% opacity for
    //     the diffuse glow that fades into the page background
    // Plus the existing canvas-card-glass drop shadow underneath
    // (kept via the layered comma — DOMQL replaces `boxShadow`
    // wholesale, so the base shadow has to be re-stated here).
    // Transition: the 160ms ease smooths the glow on/off; the rest is
    // the component's own motion list (canvas CanvasPromptTextarea.js),
    // re-stated because DOMQL replaces `transition` wholesale too. Never
    // `all`: a skin is a surface, not a state change — `B all` here eased
    // the fill and the blur from the solid to the glass values after a
    // reload (DESIGN-WL-SKINS-AMBIENT-DEFAULT-SOLID-WHITELABEL-1;
    // composerNoSkinFade.test.js holds the two lists equal).
    transition:
      '160ms defaultBezier box-shadow, ' +
      'B height defaultBezier, B max-height defaultBezier, B min-height defaultBezier, ' +
      'B width defaultBezier, B min-width defaultBezier, B padding defaultBezier, ' +
      'B border-radius defaultBezier, B transform defaultBezier',
    ':focus-within': {
      boxShadow:
        '0 0 0 1px rgba(57, 124, 255, 0.45), 0 0 0 6px rgba(57, 124, 255, 0.16), 0 12px 32px rgba(0, 0, 0, 0.35)'
    },

    // Override the canvas-mode toolbar for the home prompt. The
    // canvas Inspect button (`sf cursor` glyph, dispatches
    // `setCanvasMode('inspectMode')`) doesn't apply outside the
    // studio, so we hide it. In its place we surface a
    // `ParamsBtn` (`params` glyph) declared AFTER the inherited
    // children so DOMQL's merge places it at the end of the
    // toolbar — last icon in the row.
    PromptButtons: {
      // Inspect (sf cursor → setCanvasMode('inspectMode')) only makes
      // sense over a live PROJECT canvas. Gate strictly on the path
      // being `/<owner>/<project>` — exactly two segments whose first
      // isn't a reserved app route (2026-06-08 per Nika: "InspectBtn
      // should be only visible on project canvas /*/*"). Path-based
      // rather than the old `_projectCanvasOpen` flag, which could leak
      // true after navigating away and showed the button on /studio etc.
      InspectBtn: {
        display: null,
        // Not on phones: a project page's expanded 390 row
        // is 260.9 px and needs 311.7 with Inspect + Sidebars (design-d2's
        // joint pass, 2026-09-29) — picking an element on a phone canvas is
        // not the phone's job. `display: null` leaves no inline display for
        // the rule to fight.
        '@mobileL': { display: 'none' },
        hide: (el, s) => {
          const p =
            s.root?.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
          return !_isProjectCanvasPath(p, s.root)
        }
      },
      // Quick model switcher — THE shared AiModelSwitcherTrigger (one
      // implementation with the assistant header's AiModelSwitcher;
      // shared/components/AiModelSwitcher.js — 2026-08-12 per Nika
      // "canvasPromptTextarea and ai assistant should be using same model
      // switcher and settings buttons"). Same display/hide pattern as
      // ParamsBtn below; also hidden in local mode (the simone-bridge agent
      // ignores modelMode). The shell has the dropdown plugin, so the
      // trigger's default self-anchored toggle is overridden with the house
      // openDropdown panel hosting the same AiModelSwitcherBody.
      ModelBtn: {
        extends: 'AiModelSwitcherTrigger',
        // Opt OUT of the row's childExtends (NavbarButton): its square
        // icon-button geometry turned the old TEXT pill into a 79×79 block
        // beside 27px siblings (2026-08-18 per Nika "fix '✦ Auto' button
        // size in CanvasPromptTextarea"). Since design-d2's 789772f71 the kit
        // button is "✦" + caret on every width, so it takes the row's square
        // (the childProps padding Y) — the label-era `padding: 'Y Z2'` that
        // widened it to ~50 px is gone.
        ignoreChildExtends: true,
        display: null,
        // The inherited trigger's open state reads root._modelMenuOpen — the
        // SELF-ANCHORED composite's flag (the assistant header). This host
        // opens the plugin dropdown instead, so without this pin the
        // composer pill lit up whenever the DRAWER's picker opened
        // (cross-surface leak, verified live 2026-08-13).
        isOpen: () => false,
        // Same cross-surface reasoning as `isOpen` above, for the ARIA half
        // (2026-09-02): the inherited trigger reports `aria-expanded` from
        // root._modelMenuOpen — the DRAWER composite's flag, which this host
        // never writes. Reporting it here would announce "expanded" whenever
        // the assistant's own picker opened. The plugin panel owns (and
        // announces) its lifecycle; `aria-haspopup="menu"` still ships.
        ariaExpanded: () => null,
        // Local mode no longer hides the pill (2026-09-02): the switcher
        // body carries the Provider group with the Local-agent row — the
        // one exit from local mode — so hiding it here would trap the mode.
        hide: (el, s) => s.size === 'small',
        // ArrowDown/ArrowUp open the menu — the menu-button keyboard
        // contract. The inherited trigger's own openByKey drives the
        // SELF-ANCHORED flag this host does not use, so it is replaced here
        // with the same plugin open the click path runs. Enter/Space need no
        // branch: this is a real <button>, so both fire onClick already.
        onKeydown: (e, el) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
          e.preventDefault()
          e.stopPropagation()
          _openAiModelDropdown(el)
        },
        onClick: (e, el) => {
          e.preventDefault()
          e.stopPropagation()
          _openAiModelDropdown(el)
        }
      },
      // THE shared AiSettingsButton (one implementation with the assistant
      // header's SettingsBtn — shared/components/AiSettingsButton.js).
      // No Chat ancestor here, so its context-aware onClick opens the house
      // @symbo.ls/dropdown panel with the SAME AiSettingsPanel body the
      // sidebar assistant's SettingsDrawer renders — both surfaces identical
      // by construction. Key stays `ParamsBtn` (legacy data-key anchor).
      ParamsBtn: {
        extends: 'AiSettingsButton',
        // Hidden in the super-minimized (small) pill — returns on expand.
        // `display: null` clears the NavbarButton inline `display` so the
        // reactive `hide` can win (same pattern as InspectBtn above).
        // Reversible: drop these two lines (2026-06-14 per Nika).
        display: null,
        hide: (el, s) => s.size === 'small'
      },

      // ── The project canvas's sidebar toggle — project canvas only ──
      // Same path-gate + `display: null` + reactive `hide` pattern as InspectBtn
      // (a bare hide loses to NavbarButton's inline display). Hidden in the
      // minimized `small` pill too. ADDITIVE: absent on every non-project route,
      // so the closed-canvas toolbar is unchanged. THE PAGE-SIDEBAR RULE (Nika
      // 2026-10-29, "Same rule for all"): it drives the project page's one
      // per-page choice (shared functions/pageSidebar.js), like the rail's own
      // hide button and the call bar's sidebar button (meet ControlBar.js) —
      // pressed while the rail shows, the glyph on the rail's side, gone where
      // nothing can remember the choice.
      SidebarsBtn: {
        display: null,
        subscribeTo: [
          '_homeLook',
          'userPrefs',
          '_isPhoneViewport',
          'currentPath',
          '_canvasSlot',
          '_canvasSlotSecond'
        ],
        hide: (el, s) => {
          const now = el.call('pageSidebarRead', s.root, el) || {}
          if (s.size === 'small' || !now.toggleable) return true
          const p =
            s.root?.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
          return !_isProjectCanvasPath(p, s.root)
        },
        icon: (el, s) =>
          (el.call('pageSidebarRead', s.root, el) || {}).side === 'right'
            ? 'lnSidebarRight'
            : 'lnSidebarLeft',
        title: (el, s) =>
          el.call(
            'polyglot',
            (el.call('pageSidebarRead', s.root, el) || {}).hidden
              ? 'workspace.navbar.sidebars.show'
              : 'workspace.navbar.sidebars.hide'
          ),
        'aria-label': (el) => el.call('polyglot', 'workspace.navbar.sidebars.toggle'),
        'aria-pressed': (el, s) =>
          (el.call('pageSidebarRead', s.root, el) || {}).hidden ? 'false' : 'true',
        onClick: (e, el) => {
          e.preventDefault()
          e.stopPropagation()
          el.call('pageSidebarToggle')
        }
      }
    },
    pointerEvents: 'auto',
    // Skip freestyleConnect — the home prompt routes through
    // sdk.ai.dispatch (homeAiSend) which honors the user's
    // provider mode (simone/providers/local). The SDK opens the
    // local bridge WebSocket itself when mode='local'; cloud
    // modes don't need a local connection. /studio still calls
    // freestyleConnect('canvas') for the legacy canvas-tool path.
    onInit: () => {},

    // CircleButton (the chevron send affordance) — override the
    // inherited onClick so it dispatches homeAiSend instead of
    // freestyleSend. Same draft-read / draft-clear contract as
    // CanvasPromptTextarea._sendPrompt; just routes through the
    // unified AI surface.
    CircleButton: {
      onClick: (e, el) => {
        const st = el.state
        // Dropped files / entity refs / images are part of the prompt. This
        // handler used to read `st.draft` alone and clear only the draft, so a
        // dropped chip rendered, was silently discarded on send, and then sat
        // in the gallery as if it had gone. buildPromptPayload is the shared
        // serialiser (shared/functions/promptAttachments.js) — the canvas
        // composer's own _sendPrompt builds the identical body.
        const { body, attachments, isEmpty } = el.call('buildPromptPayload', el, st)
        if (isEmpty) return
        // ONE send, ONE generation (Nika 2026-10-28): the prompt goes to the
        // agent (homeAiSend) and nowhere else — the parallel blind page
        // generator a "create/build" prompt used to start is gone.
        //
        // Selection-aware prompting (2026-08-08 per Nika "ai should understand
        // selected artboards"): prepend a compact "Selected artboards: …" line
        // to the dispatched text so the general AI surface (homeAiSend →
        // simoneAiTransport → sdk.ai.dispatch) reads the canvas selection
        // (canvas functions/projectCanvasSidebar.js). getSelectionAiContext is
        // canvas's eager-slice fn — reachable here because this mount already
        // needs getCanvasV2/setCanvasMode from the same slice (InspectBtn
        // above). Byte-identical to `body` when nothing is selected —
        // promptPrefix is '' off-canvas or with an empty selection.
        const selection = el.call('getSelectionAiContext')
        const dispatchBody = (selection?.promptPrefix || '') + body
        // `selection.attached` (spec §3.8) is the canvas lane's STRUCTURED
        // payload for this turn — the selected nodes + connections, or the
        // whole active board when nothing is selected. Optional: the field
        // does not exist off-canvas, and it is passed through UNTOUCHED (that
        // lane owns its own caps). Fourth slot — see homeAiSend's signature
        // note for why it sits after the boot-race counter.
        el.call('homeAiSend', dispatchBody, attachments, 0, selection?.attached || null)
        // The emptied composer returns to its resting height on its own — one
        // row in the pill, the E2 floor when expanded (2026-07-24 per Nika
        // "prompt textarea should get back relative initial height"): framework
        // set 10 (element 3.14.794) re-measures the autoGrow height on this
        // state write. The hand `style.height = ''` that stood here (a state
        // write used to fire no re-measure) is gone (2026-10-02, measured live:
        // a late clear dropped the pill to the brand Textarea's E1 class
        // height, 153 px). Never touch the min-height either: it is the
        // composer's own size floor, written inline by the framework
        // (canvas/components/CanvasPromptTextarea.js, 2026-09-29).
        if (st && st.update) st.update(el.call('clearedPromptState'))
      }
    },

    Textarea: {
      // The field names what a send makes — one short line per AI mode
      // (AI_ASSISTANT_SPEC §8: "placeholder per mode, never wrapping"); the
      // kit's assistantModePlaceholder picks the key from root.assistantMode.
      // NEVER CUT (design-d2's review of deploy 8): the long line when it fits
      // the field's measured text room, else the mode's short line
      // (assistantModePlaceholderFit) — re-chosen once the field has a box
      // and on every resize.
      placeholder: (el, s) => el.call('assistantModePlaceholderFit', s.root),
      onWindowResize: (e, el, s) => el.call('assistantModePlaceholderRefit', s.root),
      // Suppress the browser's default focus outline on the
      // textarea node — the CanvasPromptTextarea wrapper above
      // owns the focus signal now via :focus-within glow.
      outline: 'none',
      ':focus': { outline: 'none', boxShadow: 'none' },
      // NO rows / height node-write here, ever (`el.node` reads, never
      // manipulates — FA511). The component declares `rows: 1` (canvas
      // CanvasPromptTextarea.js) and the framework emits that attribute since
      // smbls 3.14.811 (FW-TEXTAREA-ROWS-ATTR-HIJACKED-BY-GRID-ALIAS-1), so
      // the old mount-site `onRender` — `if (el.node.rows !== 1) { rows = 1;
      // style.height = 'auto' }`, a workaround for the attribute bug — stopped
      // firing. That dead write was the ONLY thing overwriting autoGrow's
      // create-time `height: 0px`, and the composer booted with a 14px box
      // under a 26px line box for every member
      // (WS-COMPOSER-TEXTAREA-HEIGHT-ZERO-AFTER-BOOT-1). The component owns NO
      // `height` factory either (design-d2's review, 2026-09-29 — a second
      // writer never survived autoGrow's own re-measure): autoGrow itself now
      // writes no height before the node has layout, and measures on the
      // post-attach flush, since smbls 3.14.812
      // (FW-AUTOGROW-FIRST-MEASURE-WRITES-ZERO-ON-DETACHED-NODE-1).
      // onRender keeps the one call that is still live here: now and once the
      // composer row has settled (the field's room is final only after it
      // lays out — shared assistantModePlaceholderSettle).
      onRender: (el, s) => el.call('assistantModePlaceholderSettle', s && s.root),
      // Enter submits via homeAiSend instead of freestyleSend.
      // Mirrors the CanvasPromptTextarea.Textarea.onKeydown shape
      // (read draft from state, dispatch, clear) so the inherited
      // onInput draft binding and the component's autoGrow stay untouched.
      //
      // ⚠ This override REPLACES the composer's own onKeydown — a mount-site
      // handler wins outright, DOMQL never chains the two. So every key branch
      // the composer declares is dead in the SHELL mount. Measured live
      // 2026-09-02 on /login (the composer renders on every route): pressing
      // ArrowDown in the textarea with the '+' attach menu open did nothing,
      // and Arrow/Enter/Escape with the '@' mention list open did nothing,
      // because the composer's own branches never ran.
      //
      // Re-declaring branches here is what let the two mounts drift, so this
      // override DELEGATES instead: `aiComposerKeydown`
      // (shared/functions/aiComposerKeys.js) owns every key that belongs to an
      // open panel — the '+' attach menu's arrows and the whole mention-picker
      // traversal — and reports whether it consumed the keystroke. The
      // composer's own onKeydown (canvas/components/CanvasPromptTextarea.js)
      // calls the SAME function behind the same guard, so a new panel key is
      // added once and both mounts get it.
      //
      // Only the send leg differs, and it stays below: the delegate consumes
      // Enter ONLY while a mention picker with options is open (that Enter
      // inserts the mention). With no picker open it returns false and Enter
      // reaches homeAiSend exactly as before — one send, never two.
      onKeydown: (e, el) => {
        if (_wsShared.functions.aiComposerKeydown(e, el, el.state)) return
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          const st = el.state
          // Same shared serialiser as the CircleButton path above — see the
          // comment there for why draft-only was wrong.
          const { body, attachments, isEmpty } = el.call('buildPromptPayload', el, st)
          if (isEmpty) return
          // ONE send, ONE generation (see the CircleButton path above).
          // Selection-aware prompting — same prefix as the CircleButton path
          // above (see its comment for the full rationale).
          const selection = el.call('getSelectionAiContext')
          const dispatchBody = (selection?.promptPrefix || '') + body
          // Same structured-attachment forward as the CircleButton path above.
          el.call('homeAiSend', dispatchBody, attachments, 0, selection?.attached || null)
          // The emptied composer re-measures through the framework — no hand
          // height clear (see the CircleButton path above).
          if (st && st.update) st.update(el.call('clearedPromptState'))
        }
      }
    }
  },

  // Zoombar — mounted at the shell root (2026-06-08 per Nika "make
  // Zoombar in the root, present on all pages"). It's the shared/global
  // zoom control: its +/- buttons drive whichever canvas is active via
  // the canvas global-scope per-frame loop (canvas/globalScope.js), so a
  // single root mount serves every canvas surface (project canvas,
  // studios board, legacy). The per-canvas mounts (CanvasEditor /
  // CanvasLegacy / studios) are removed in favor of this one. Shows on
  // every page (inert % readout on non-canvas surfaces); hidden only on
  // auth routes. `position: fixed` (vs the per-page `absolute`) anchors
  // it to the viewport bottom-right regardless of the page's layout.
  Zoombar: {
    // Staged out of the boot task — canvas chrome, never the first pixel of a
    // shell boot, and its `hide:` below still owns visibility once built.
    if: _atStage(SHELL_STAGE.CHROME),
    subscribeTo: [
      'currentPath',
      '_isPhoneViewport',
      '_phoneNavPanel',
      '_canvasRailOpen',
      '_ticketsRailOpen',
      '_filesRailOpen',
      '_moduleRailOpen'
    ],
    // Show ONLY on the studios board (/studio) and live project canvases
    // (/<owner>/<project>) — hidden on home, tickets, chat, etc. (2026-06-08
    // per Nika "show ZoomInNavbar only on /studio and project canvas
    // screens"). Was previously shown on every non-auth page.
    hide: (el, s) => {
      if (
        s.root?._isPhoneViewport &&
        (s.root._phoneNavPanel ||
          s.root._canvasRailOpen ||
          s.root._ticketsRailOpen ||
          s.root._filesRailOpen ||
          s.root._moduleRailOpen)
      )
        return true
      const p =
        s.root?.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
      if (isChromelessShellPath(p)) return true
      return !(
        _isStudioCanvasPath(p) ||
        _isTicketsCanvasPath(p) ||
        _isTeamCanvasPath(p) ||
        // /infra is a pan/zoom canvas like the rest — it was the only one
        // missing the shared zoom control (2026-07-03 per Nika "same canvas
        // functionality" across /studio /infra /tickets /agents).
        _isInfraCanvasPath(p) ||
        _isProjectCanvasPath(p, s.root) ||
        _isModuleCanvasPath(p, s.root)
      )
    },
    fontSize: 'Z1',
    position: 'fixed',
    bottom: 'X2',
    right: 'X2',
    top: 'auto',
    left: 'auto',
    width: 'auto',
    // INVISIBLE-OVERLAY-EATS-CLICKS-1 #2: CreditBlocker's full-viewport gate
    // (zIndex 10300) used to sit ABOVE this bar, so a credit-exhausted
    // workspace visually showed live, enabled zoom controls that no click
    // could ever reach — every click fed the gate's own backdrop-dismiss
    // handler instead. Pan/zoom cost nothing (CreditBlocker's own header:
    // "pure reads... AI, builds and metered actions are paused"), so the fix
    // is NOT a route exemption — that would also un-gate editing/saving/AI
    // on the same route, a billing hole. It's narrower: let this pure-read
    // control sit reachable ABOVE the gate while the gate itself, and
    // everything it actually exists to block, is untouched. 10310 sits
    // between CreditBlocker (10300) and ModalRoot (10500, workspace/
    // components/CreditBlocker.js's own documented ladder) — an auth/account
    // modal still correctly covers this bar; the credit gate no longer does.
    zIndex: '10310',
    pointerEvents: 'auto',
    borderWidth: '0',
    // --zoombar-clear (root CSS above): the bar's top from the viewport
    // bottom, zeroed while `hide` keeps it display:none.
    onRender: (el) => _trackClearVar(el, '_zbObs', '--zoombar-clear', _clearFromTop),
    onWindowResize: (e, el) => el._zbObsApply?.(),
    onBeforeRemove: (el) => _stopClearVar(el, '_zbObs'),
    '@mobileL': {
      left: '50%',
      right: 'auto',
      // Clear the composer AND the phone chip row above it — see --chip-row.
      // (Was a flat D1, which landed the bar inside the chips' band.)
      bottom: 'calc(var(--slot-bottom) + var(--chip-row))',
      transform: 'translateX(-50%)',
      // Preserves the pre-existing base<mobile relationship (was 60/80)
      // one step above the new base, same reasoning as above.
      zIndex: '10320'
    },
    NavbarButtonSet: {
      Settings: {
        onClick: (ev, el, s) => {
          ev.stopPropagation()
          const root = s.root
          if (!root) return
          // Studios board → Workspace tab; tickets board → Tickets tab;
          // project canvas → Project tab. Always recomputed fresh from the
          // CURRENT board path — SETTINGS-WRONG-CONTENT-1: falling back to
          // `root.settingsTab` here reused whichever tab a PRIOR board
          // context last left behind (it's never reset on modal close), so
          // opening Settings on the studios board after having opened it on
          // a project canvas showed the stale Project tab instead of
          // Workspace. `root.settingsTab` is still written below so
          // SettingsModalBody's own in-modal tab switcher keeps working for
          // this open instance.
          const p =
            root.currentPath || (typeof window !== 'undefined' ? window.location.pathname : '/')
          const tab = _isTicketsCanvasPath(p)
            ? 'tickets'
            : _isStudioCanvasPath(p) || _isModuleCanvasPath(p, root)
              ? 'workspace'
              : 'project'
          root.update({ settingsTab: tab })
          // MODAL-X-OVERFLOW-1: openCustomModal supplies a safe default
          // maxHeight so the Close X can't be pushed off-screen.
          el.call('openCustomModal', {
            id: 'settings',
            width: 'overlayXl',
            content: { extends: 'SettingsModalBody' }
          })
        }
      }
    }
  },

  TranslationWatcher: {
    visibility: 'hidden',
    position: 'fixed',
    pointerEvents: 'none',
    width: '0',
    height: '0',
    overflow: 'hidden',
    fetch: {
      from: 'i18n.translations',
      method: 'subscribe',
      params: (el, s) => ({ lang: s.root?.lang || 'en', namespace: 'all' })
    }
  },

  WorkspaceActivationStatus: {},

  // One visual through startup, the in-app twin of the pre-bundle overlay
  // (index.html #boot-spiral): the same mountain range filling from its base
  // to its summit on the real stages, the same mark above it, the same
  // concept switch (<html data-loader>). Later blocking operations (a
  // workspace switch, sign-in) reuse it. The motion lives in the injected
  // sheet (see THE LOADER there), keyed on the data-stage this stamps.
  MainLoader: {
    display: (el, s) => {
      const active = _syncBootLoader(el, s)
      // The native overlay announces itself (index.html sets the flag while it
      // is on screen and clears it the moment its ready fade starts), so this
      // visual stays hidden underneath it without querying the document.
      const nativeVisible =
        typeof window !== 'undefined' &&
        !window.__smblsBootReady &&
        window.__smblsBootOverlay === true
      return (active || el._readyHold) && !nativeVisible ? 'flex' : 'none'
    },
    // The ready fill's end lets the held loader leave (other layers' ends do not).
    onAnimationend: (e, el, s) => {
      if (e && e.animationName === 'wsLoaderFrontReady' && el._readyHold)
        _endReadyHold(el, (s && (s.root || s)) || el._bootReadyOwner?.root)
    },
    onRemove: (el) => {
      _cancelBootReady(el)
      _clearLoaderMax(el)
      if (typeof window !== 'undefined' && window.__smblsBootOwner === el._bootReadyOwner?.token)
        window.__smblsBootOwner = null
      el._bootReadyOwner = null
      el._loaderSeenActive = false
    },
    opacity: (el, s) => (_isLoaderActive(s) || _loaderHolding(el, s) ? '1' : '0'),
    pointerEvents: (el, s) => (_isLoaderActive(s) ? 'auto' : 'none'),
    visibility: (el, s) => (_isLoaderActive(s) || _loaderHolding(el, s) ? 'visible' : 'hidden'),
    animation: 'none',
    transform: 'none',
    overflow: 'auto',
    transition: 'opacity A ease',
    '@reducedMotion': { transition: 'none' },
    attr: { 'data-stage': (el, s) => _loaderFillStage(s.root || s) },
    // where the current stage's fill starts (see _trackLoaderMotion); set by
    // `display` above in the same render
    '--ws-loader-from': (el) => _loaderFrom(el),
    // Pure black in dark mode, pure white in light mode (Nika 2026-09-29):
    // the `loaderGround` token pair. Inline on purpose: the brand
    // MainLoader's `theme: 'document'` class would win over a static
    // declaration (the shell root's note on the same kind of ground).
    background: () => 'loaderGround',
    // THE RANGE — the progress. The ambient mountains mask through the files
    // plane, the loader's ink token, the bottom band of the screen. Display
    // none by itself: the injected sheet shows it under the ambient skin and
    // never on a tenant host. The clip is the stage-1 milestone (nothing);
    // the sheet moves it.
    LoaderRange: {
      display: 'none',
      position: 'absolute',
      inset: 'auto 0 0 0',
      height: 'loaderRange',
      pointerEvents: 'none',
      overflow: 'hidden',
      aria: { hidden: 'true' },
      // The front: one feather taller than the band, its top fading in over
      // the feather; the sheet slides it up by transform (the base hides it).
      RangeFront: {
        position: 'absolute',
        left: '0',
        right: '0',
        top: 'loaderFrontTop',
        height: 'loaderFrontHeight',
        maskImage: 'linear-gradient(to bottom, transparent 0, black 44px)',
        transform: 'translateY(100%)',
        // The art counter-moves inside the front, so it stays put while the
        // feathered edge rises through it.
        RangeArt: {
          position: 'absolute',
          left: '0',
          right: '0',
          top: 'loaderFront',
          bottom: '0',
          background: 'loaderInk',
          maskImage: (el) => el.call('ambientMaskImage', el.call('ambientSkyUrl', 'mountains')),
          maskSize: 'auto 100%',
          maskRepeat: 'repeat-x',
          maskPosition: 'center bottom',
          transform: 'translateY(calc((55vh + 44px) * -1))'
        }
      }
    },
    // The brand spiral gives way to the range.
    LoaderRatio: { hide: true, display: 'none' },
    LogoLoader: {
      width: 'loaderMark',
      height: 'loaderMark',
      fontSize: 'loaderLogoFont',
      top: 'loaderCenter',
      animation: 'none',
      aria: { hidden: 'true' }
    },
    // "rise": a second LogoLoader — the solid mark over the first one's faint
    // silhouette, filled from the bottom at the range's rate. Display none
    // unless <html data-loader="rise"> (the sheet).
    LogoLoader_1: {
      display: 'none',
      position: 'absolute',
      top: 'loaderCenter',
      left: '50%',
      transform: 'translate3d(-50%, -50%, 0)',
      zIndex: 3,
      width: 'loaderMark',
      height: 'loaderMark',
      fontSize: 'loaderLogoFont',
      clipPath: 'inset(100% 0 0 0)',
      aria: { hidden: 'true' }
    },
    Text: {
      role: 'status',
      aria: { live: 'polite', atomic: 'true' },
      position: 'absolute',
      top: 'loaderStatusTop',
      left: '0',
      width: '100%',
      padding: '0 B',
      textAlign: 'center',
      color: 'title',
      opacity: '0.8',
      fontSize: 'loaderStatusFont',
      lineHeight: 20 / 13,
      // One translated line from the shared bundle while any phase is active.
      // None while an in-page sign-in owes the session language: the word
      // would speak the sign-in page's language, not the workspace's.
      text: (el, s) =>
        _loaderStatus(s.root || s) && (s.root || s)._langOwed !== true
          ? el.call('polyglot', 'common.state.loading')
          : ''
    }
  },

  // Full-viewport "Switching to <org>…" overlay. Mounted at shell scope
  // (not inside a sub-app) so it covers the entire UI during the JWT
  // refresh round-trip. Driven by `root._wsSwitching`.
  AppOrgSwitchOverlay: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // Toast container for `el.call('openNotification', …)`. Mounted at
  // SHELL scope (not inside the workspace-project sub-app) so toasts
  // also render on auth pages — /login, /signup, /reset-password —
  // which are reached during the auth-bounce path before any sub-app
  // is mounted into Slot. Reads from `s.notifications`. The auth-bounce
  // codepath in workspace/index.js seeds `state.notifications = []`
  // when missing, but the shell context's `state` (composed in
  // workspace/context.js) already inherits `notifications: []` from
  // wpState so this is just a defensive backstop.
  //
  // A ROW OF THE COMPOSER'S STACK: the stack docks ON the stack line (the
  // composer's top and its docked rows + the gap — never --slot-bottom, which
  // it lifts: a toast on it would chase its own clearance). Its box holds the
  // room it takes: the call panel under the toasts (--call-clear, so a toast
  // never covers Accept / Decline) and one --nav-gap over them. The box's
  // HEIGHT is --toast-clear while it holds a toast (0 while it holds none:
  // the empty box keeps its padding), so every sheet and view ends one gap
  // above the toasts (--slot-bottom, the root CSS above). The box paints
  // nothing and takes no pointer (the shared component's container).
  GlobalNotification: {
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    bottom: 'var(--stack-line)',
    paddingTop: 'var(--nav-gap)',
    paddingBottom: 'var(--call-clear, 0%)',
    onRender: (el) =>
      _trackClearVar(el, '_tcObs', '--toast-clear', (r) => {
        const list = el.state && el.state.notifications
        return Array.isArray(list) && list.length ? r.height : 0
      }),
    // The resize half of the measurement above (FA503 — framework-owned).
    onWindowResize: (e, el) => el._tcObsApply?.(),
    onBeforeRemove: (el) => _stopClearVar(el, '_tcObs')
  },

  // THE ticket detail modal — the ONLY mounted instance (2026-08-06
  // consolidation; was page-mounted 14× across home/chat/meet/inbox + the
  // tickets pages, so any route WITHOUT a mount — calendar, documents,
  // files, studio, admin/* — silently no-op'd a ticket open, e.g. a
  // GlobalNotification toast click for a ticket while on /calendar).
  //
  // TicketsDetailModal extends Modal with stateKey 'selectedTicket':
  // openTicketDetailModal (functions/ticketDetailModal.js) ONLY seeds the
  // carrier and this instance flips visible reactively — an instant display
  // flip on every route. Modal stateKey semantics make ANY second mounted
  // instance a double-open (both react to the same carrier), so pages must
  // NOT re-mount it.
  //
  // Why a bare key mount, not @symbo.ls/windows and not a lazy context fold:
  //   • windows' Slot.set(content) re-prepares the ~4,000-line def
  //     synchronously on EVERY open — a measured 15-40s main-thread freeze
  //     (framework ticket: tickets/smbls.md). Banned path.
  //   • the def already ships in the tickets EAGER slice
  //     (sharedLibraries.js) — laziness here saves zero bundle, and a bare
  //     key cannot defer to a context fold anyway (extends resolves at
  //     parent parse time — see MessengerOverlaySlot below).
  //   • prepare cost = ONE page-mount-equivalent hit, and REMOVED from every
  //     subsequent page navigation (home re-paid it on every home load since
  //     2026-06-08). Since WS-SHELL-MOUNT-TREE-BUILD-2700MS-1 that one hit is
  //     no longer paid at app create either — the `if:` latch below defers it
  //     to the first open, so a boot that never opens a ticket never pays it.
  //
  // The ?ticket=<id> URL restore (loaded-row poll + 5s SDK-fetch fallback)
  // lives in the component's own onCreate, so deep links restore on EVERY
  // route — /w/<org>?ticket=<id> included. /ticket/:id (tickets/pages/
  // view.js) reuses this same instance via root._ticketFullPage.
  TicketsDetailModal: {
    // Built on FIRST NEED, not at boot — see the deferred-shell-subtrees
    // note at the top of this file. Need = the `selectedTicket` carrier is
    // seeded (openTicketDetailModal, every toast/menu/page opener), OR the
    // /ticket/:id full-page route flipped `_ticketFullPage`, OR the URL
    // already carries a `?ticket=<id>` deep link at this evaluation.
    if: needTicketDetailModal,
    // `_routeNudge` is bumped by the shell's own route watcher; reading it
    // re-runs the predicate on every client-side navigation, so an in-app
    // deep link to `?ticket=<id>` mounts the modal exactly like a cold boot.
    ifDeps: [(el, s) => s?.root?._routeNudge]
  },

  // THE create-agent modal — the ONLY mounted instance (2026-09-26). It was
  // page-mounted twice on one carrier (`_createAgentOpen`): the home page
  // (pages/main.js) and the agents canvas (canvas/pages/team-and-agents.js).
  // /agents also opens as a PageSlot WINDOW over home (_APP_WINDOW_PAGES), so
  // both pages were mounted at once and one "+ → Agent" opened TWO dialogs
  // (the close-audit counted two non-nested "Create agent" dialogs). Same law
  // as TicketsDetailModal above: Modal stateKey semantics make any second
  // mounted instance a double-open, so pages must NOT re-mount it. Every
  // opener (the home header +, the Your-agents create tile, the agents "+"
  // menu) only sets the carrier.
  CreateAgentModal: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // The ? keyboard sheet (HOME_AMBIENT_SKIN_SPEC §5) — ONE shell-root instance,
  // AppDialog v2 centre (components/ShortcutsOverlay.js). Opened by `?` through
  // the shortcut registry (functions/shortcuts*.js) or the palette's row; the
  // dialog owns its Esc, veil and Close.
  ShortcutsOverlay: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // Install / configure modal for canvas integration/library/mcp/plugin
  // (Removed 2026-06-01: MarketplaceInstallModal moved to @symbo.ls/windows.
  // The shellApp.onCreate now polls `root.installModal` at 100ms and
  // pushes / pops the ModalRoot layer when it flips. All existing
  // setters via `el.call('openInstallableModal', opts)` keep writing
  // `host.update({ installModal: … })` unchanged.)

  // (Removed 2026-06-10: NewProjectPickerOverlay moved to @symbo.ls/windows.
  // Openers — home's EmptyProjectCard / ProjectStarter, StudioNavbar's
  // "+ New project" menu, the studios empty-board tutorial — seed the
  // `root.newProjectPicker` carrier and push a custom-template layer whose
  // content extends `NewProjectPickerBody` (canvas/pages/studios.js, bridged
  // via workspace/components/index.js) with openModal id 'newProjectPicker';
  // the layer onClose clears the carrier. ModalRoot below owns visibility.)

  // (Removed 2026-06-01: AppCreateOrgModal moved to @symbo.ls/windows.
  // Callers dispatch via `el.call('openCreateOrgModal')` — the helper
  // resets the `_createOrg*` draft fields (kept as data carriers) and
  // pushes a custom-template layer whose content extends
  // `AppCreateOrgModal` (now body-only). Close: `closeCreateOrgModal`.)

  // Unified modal/dialog system — @symbo.ls/windows. Shell-scope mount
  // because the wp sub-app wrapper never instantiates under the 3.14
  // auto-router, so any modal/dialog needs to live at this shell level
  // to render across every route. ModalRoot owns a 5-layer stack and
  // registers an ESC handler scoped to its lifecycle (not module-side-
  // effect). Templates + helpers arrive via sharedLibraries → shared/
  // components + shared/functions re-exports.
  //
  // Callers use `el.call('openModal' | 'confirmDialog' | 'alertDialog' |
  // 'promptDialog', opts)` instead of writing per-modal state fields.
  // Migrated 2026-06-01 from the legacy `root.confirmAction` /
  // ConfirmModal pattern — every former trigger site now routes through
  // confirmDialog (see git history for the old shape).
  //
  // zIndex override (local — the windows lib is read-only): the shared
  // `Modal` base ships at zIndex 2000, the SAME as ModalRoot's host. Equal
  // z-index → DOM paint order decides, so a page modal mounted after the
  // shell (e.g. TicketDetailModal) painted OVER the dialog host and a
  // confirmDialog opened from inside it rendered invisibly behind the modal
  // (#113). Bespoke page modals climb as high as 10001 (TicketMentionModal),
  // so lift the global dialog host above that whole interactive band — it's
  // a blocking interrupt and must always be top-most. Stays below the
  // transient drag overlays (99998/99999, pointer-events:none, never
  // co-occur with a dialog). The ModalLayer slots add their own +N*10 on top.
  ModalRoot: {
    // Be explicit here instead of relying on bare-key auto-extension. The
    // deferred `if:` mount is prepared after boot and recent smbls builds can
    // otherwise materialise only this local override, producing an empty host
    // with none of the windows ModalLayer children or lifecycle hooks.
    extends: 'ShellModalRoot',
    zIndex: '10500',
    // The deferred shell override can mount before the windows library's
    // base props are merged, leaving an empty host as a normal hit-testing
    // block. Keep the host transparent to input; visible ModalLayer children
    // opt back into pointer events when a dialog is actually open.
    pointerEvents: 'none',
    // Built right AFTER first paint, not inside the boot task — see the
    // deferred-shell-subtrees note at the top of this file. `openModal` is a
    // CALL, not a state carrier, and smbls flushes effects on a microtask, so
    // a first-open latch could not materialise the host synchronously for the
    // `resolveModalRoot` that the same call makes. bootShell flips
    // `_modalHostReady` on its bounded `yieldToPaint` immediately after the
    // shell mounts — before any input and before any SDK continuation that
    // could push a layer — which takes the 246 elements out of the boot task
    // without changing a single opener.
    if: (el, s) => _latchOnce(el, '__wsDeferredMounted', !!s?.root?._modalHostReady)
  },

  // Voice input — full-screen Apple-style listening overlay behind the mic
  // buttons on CanvasPromptTextarea + the assistant composer (shared
  // VoiceOverlay; session engine in shared/functions/voiceSession.js —
  // architecture/VOICE_COMMANDS.md). One band above ModalRoot: an active
  // voice session is always top-most. Inline fixed-position defense per the
  // shell-root grid-overlay rule.
  VoiceOverlay: {
    position: 'fixed',
    inset: '0',
    zIndex: '10600',
    if: _atStage(SHELL_STAGE.OVERLAYS)
  },

  // Fixed bottom-left presence dock (moved from the AppHeader right cluster,
  // 2026-07-25 per Nika) — active members online on every route; overflow
  // dropdown opens upward. Self-gating (signed-in / active-member / auth
  // routes) inside the component.
  OnlineMembersDock: { if: _atStage(SHELL_STAGE.CHROME) },

  // ── Immersive module rail (workspace-abstraction: a module REPLACES shell
  // chrome for its members) ────────────────────────────────────────────────
  // When the active module's chrome manifest declares a `rail` component and
  // the caller is in immersive mode (chrome.immersive matches — admins never
  // are), the shell mounts that module component as a PERSISTENT left rail
  // on EVERY route including '/' — the module's nav IS the app chrome for
  // these members; the immersive PageSlot opens BESIDE it (its left edge
  // insets by --module-rail-clear, live-measured below). The component
  // resolves from the module's injected components by bare-key extends and
  // carries the module's OWN design-system context (same getter-closure
  // contract as the home board's widget bodies — root._moduleDsCtxGet).
  ModuleRailHost: {
    // Always-on once the module declares chrome.rail — admins included
    // (immersive only governs the top/bottom chrome, not the rail). A comm
    // host (comm.symbols.app, docs/COMM_HOST.md) never shows it: the module's
    // pages open on the workspace host.
    hide: (el, s) => el.call('isCommHost') || !moduleRailActive(s.root),
    subscribeTo: [
      'workspaceProject',
      'themeMode',
      // themeResolved: the LIVE dark/light resolution (watchThemeResolved).
      // themeMode stays the string 'auto' across an OS scheme flip, so
      // without this key the rail (and _setStageVar riding its renders)
      // never repaints the stage on an OS flip (BF-THEME-SURFACES-1).
      'themeResolved',
      '_slotFullscreen',
      '_canvasSlotSecond',
      '_slotSplitIntent',
      // Item 11 route gate: moduleRailActive now reads root.currentPath
      // (rail only on the workspace surface — never over /studio, /infra
      // or other own-sidebar routes). Declared lists are the binding
      // contract — without this key the hide: factory never re-runs on a
      // route change.
      'currentPath',
      // The phone drawer (_moduleRailDrawer): the viewport answer, the
      // dock's open flag, and the workspace the dock waits for.
      '_isPhoneViewport',
      '_moduleRailOpen',
      'workspace'
    ],
    position: 'fixed',
    left: '0',
    // The phone drawer at rest: out of sight, out of the hit test and of the
    // tab order. Body slides its last 6.5% in (below) — never this host, whose
    // transform would become the containing block of the fixed RailScrim.
    // Every other state inherits, exactly as before the drawer existed.
    dataRailDrawer: (el, s) => _moduleRailDrawer(s.root),
    visibility: (el, s) => (_moduleRailDrawerShut(s.root) ? 'hidden' : 'inherit'),
    pointerEvents: (el, s) => (_moduleRailDrawerShut(s.root) ? 'none' : 'inherit'),
    // Little top/bottom offsets in the edge (immersive/expanded) state —
    // matches PageSlot's .isSlotFullscreen Z band (Nika 2026-08-16: "do
    // not put on the edge"). The chrome-inset class below still wins when
    // the shell chrome is live.
    top: 'Z',
    bottom: 'Z',
    // Per-module reversal of the 2026-08-16 offsets (Nika 2026-09-02 via
    // the bellforge lane: "put navbar to the edge"): a module whose
    // manifest sets `chrome.railEdge` ships a flat full-height band, so
    // its EDGE state runs flush — 0/0 vertical, no left gutter (the right
    // Z stays as the content gutter). The class attaches ONLY when the
    // rail is genuinely flush — railEdge AND the edge state (the same
    // predicate isRailChromeInset negates below): with the shell chrome
    // live, top/bottom keep the --slot band AND the left gutter keeps Z,
    // otherwise a rounded rail card sat flush-left with an inset top and
    // bottom and showed paper in its left corners (bellforge lane's
    // measurement, 2026-09-02). _railFlush is also what the mounted rail
    // receives as its `railFlush` prop (Body.__mountModuleRail below).
    isRailEdge: (el, s) => _railFlush(s.root),
    '.isRailEdge': {
      top: '0',
      bottom: '0',
      padding: '0 Z 0 0'
    },
    // Vertical geometry contract (2026-08-14 per Nika "sidebar and pageslot
    // should have same height and top and bottom positions"): whenever the
    // shell's top/bottom chrome is LIVE (admin, non-fullscreen), the rail
    // shares the PageSlot card's exact vertical band — top --slot-top
    // (navbar bottom + --nav-gap), bottom --slot-bottom (composer top +
    // --nav-gap) — so it can never run under the navbar or composer.
    // The predicate mirrors PageSlot's isSlotFullscreen decision verbatim:
    // immersive members and an admin's edge-to-edge toggle drop to the
    // base full-height geometry, exactly when the slot itself goes 0/0.
    isRailChromeInset: (el, s) => {
      // Full-height ONLY for edge-to-edge (immersive) fullscreen. An admin's
      // EXPANDED slot (B1 default) keeps --slot-top/--slot-bottom, so the
      // rail keeps sharing that exact band — the 2026-08-14 "same height and
      // top and bottom" contract holds in the expanded state too.
      return !_railEdgeState(s.root)
    },
    '.isRailChromeInset': {
      top: '--slot-top',
      bottom: '--slot-bottom'
    },
    // Below the PageSlot band (1300) — the slot never overlaps (it insets
    // left by this rail's live width), and slot popovers may still float
    // over the rail edge.
    zIndex: '1290',
    display: 'flex',
    // Horizontal padding ONLY (was 'Z' on all four sides). The vertical
    // padding inset the rail card INSIDE the --slot-top/--slot-bottom band
    // while the PageSlot card sits FLUSH at those edges — so the two never
    // aligned (2026-08-16 per Nika: "the sidebar alignment does not align
    // with pageslot — remove those paddings or add them to pageslot").
    // Zero vertical padding puts the rail's top and bottom on the exact
    // same lines as the slot card in every chrome state.
    padding: '0 Z',
    overflow: 'hidden',
    // NO background at all (2026-08-15 per Nika "remove bg behind sidebar
    // should be transparent"): the host paints nothing, so the padding gap
    // around the floating rail shows whatever page is genuinely behind it —
    // the immersive paper desk for members, the dark shell board for
    // admins — with no strip in either theme. Deliberately the ABSENT prop
    // rather than a 'transparent'/null value: an unknown colour word can be
    // token-table-mangled and a null factory can leave the previous painted
    // fill behind on re-render; an element that never declares a background
    // is transparent by construction. (Supersedes the earlier
    // gray16/document two-stage fill from "remove black bg behind
    // sidebar" — the rail card itself carries its own surface.)
    // Live-measured left clearance — same RO recipe as --nav-clear; zeroes
    // when hidden so admin fullscreen slots stay edge-to-edge. The phone
    // drawer floats over the page, so it never insets it: 0 there as well.
    onRender: (el, s) => {
      _setStageVar(el, s)
      _trackClearVar(el, '_mrObs', '--module-rail-clear', (r) =>
        _moduleRailDrawer(s.root) ? 0 : r.right
      )
      _pushRailFlush(el, s)
    },
    // The resize half of the measurement above (FA503 — framework-owned).
    onWindowResize: (e, el) => el._mrObsApply?.(),
    // Belt for the re-arm: node re-creation can land in an UPDATE pass
    // (subscribeTo workspaceProject) — same-node calls return early.
    onUpdate: (el, s) => {
      _setStageVar(el, s)
      _trackClearVar(el, '_mrObs', '--module-rail-clear', (r) =>
        _moduleRailDrawer(s.root) ? 0 : r.right
      )
      _pushRailFlush(el, s)
    },
    onBeforeRemove: (el) => _stopClearVar(el, '_mrObs'),
    // Escape closes the open phone drawer — only when it is the top layer
    // (shared/functions/shellOverlayOpen.js answers 'mobileRail' for it).
    onDocumentKeydown: (e, el, s) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (!_moduleRailDrawer(s.root) || !s.root._moduleRailOpen) return
      if (el.call('shellOverlayOpen') !== 'mobileRail') return
      e.preventDefault()
      s.root.update({ _moduleRailOpen: false }, { preventFetch: true })
    },
    // The drawer's veil: the whole viewport under the rail card (fixed, and
    // behind the card inside the host's own layer), tap closes. Same token
    // and fade as the MobileRailSheet scrim.
    RailScrim: {
      // Per-element subscriptions: the host's list re-runs the host's own
      // factories, not this child's.
      subscribeTo: ['_isPhoneViewport', '_moduleRailOpen', 'workspace', 'workspaceProject'],
      hide: (el, s) => !_moduleRailDrawer(s.root),
      position: 'fixed',
      inset: '0',
      zIndex: '-1',
      background: 'scrimSoft',
      opacity: (el, s) => (s.root._moduleRailOpen ? '1' : '0'),
      transition: 'opacity B defaultBezier',
      onClick: (e, el, s) => s.root.update({ _moduleRailOpen: false }, { preventFetch: true })
    },
    Body: {
      display: 'flex',
      minHeight: '0',
      // The phone drawer's motion: the brand budget (6.5% travel, the
      // MobileRailSheet card's own), in on D along the spring, out on C.
      transform: (el, s) => (_moduleRailDrawerShut(s.root) ? 'translateX(-6.5%)' : 'none'),
      dataRailDrawerOpen: (el, s) => _moduleRailDrawer(s.root) && !!s.root._moduleRailOpen,
      transition: 'transform C defaultBezier',
      '&[data-rail-drawer-open]': { transition: 'transform D springBezier' },
      // The stash arrives AFTER first render on a cold boot, so a one-shot
      // onRender mount left the rail permanently empty (measured live
      // 2026-08-14: host in DOM, railActive true, zero children). Mount on
      // BOTH render and update — subscribeTo re-fires onUpdate when
      // workspaceProject publishes; the __railSig guard keeps it idempotent.
      // The drawer keys re-run the motion factories above.
      subscribeTo: ['workspaceProject', '_isPhoneViewport', '_moduleRailOpen', 'workspace'],
      onRender: (el, s) => {
        const mount = el.__mountModuleRail
        if (mount) mount(el, s)
      },
      onUpdate: (el, s) => {
        const mount = el.__mountModuleRail
        if (mount) mount(el, s)
      },
      onInit: (el) => {
        el.__mountModuleRail = (elm, s) => {
          const wp = s.root?.workspaceProject
          const rail = wp && wp.chrome && wp.chrome.rail
          if (!rail || elm.__railSig === rail) return
          elm.__railSig = rail
          // `railFlush`: the host's resolved flush state, handed to the rail
          // as a prop (see _railFlush). The def is set once per rail
          // signature, so later flips ride ModuleRailHost's onRender/
          // onUpdate (_pushRailFlush) instead of a re-mount.
          const def = { extends: rail, height: '100%', railFlush: _railFlush(s.root) }
          // Keep the host's last-pushed value in step with what the mount
          // carries, so a flip back to this value is never skipped as
          // "unchanged" by _pushRailFlush.
          if (elm.parent) elm.parent.__railFlush = def.railFlush
          // WS-MODULE-STATE-SCOPE-1: the module context also carries the
          // module's STATE scope now (a plugin on `modCtx`), so the rail
          // renders inside the module's own state root with no def change —
          // see functions/moduleStateScope.js.
          const modCtx = s.root?._moduleDsCtxGet?.(wp.id)
          if (modCtx) def.context = modCtx
          try {
            elm.set(def)
          } catch (err) {
            elm.__railSig = null
            console.warn('[shell] module rail mount failed:', rail, err?.message)
          }
        }
      }
    }
  },

  // Unified dropdown system — @symbo.ls/dropdown. Single panel mounted
  // at shell root reads `root._dropdown` and renders whichever caller
  // last opened. Same shell-mount rationale as ModalRoot: position:fixed
  // panels need viewport-space coords, which a transformed ancestor
  // would otherwise clip (see the old AiModePicker overflow bug for the
  // exact failure mode). Callers route via
  //   el.call('openDropdown', { anchorEl: el, content: {...} })
  // or the declarative `extends: 'DropdownTrigger'` wrapper.
  //
  // zIndex override (local — the plugin default is 1500, "above app
  // chrome"): bespoke page surfaces in this shell climb far past that
  // (drawers, gridstack drag shadows, page modals up to 10001), so a
  // dropdown opened from the header — the notification center being the
  // daily case — painted UNDER them. A dropdown is always the most recent
  // user intent; it beats every persistent surface. 10000 keeps it below
  // the blocking-interrupt dialog host (ModalRoot 10500) and the transient
  // drag overlays (99998+), which must stay supreme. 10010, not a flat
  // 10000: the AssistantDrawer host sits AT 10000, and an equal z-index
  // hands the win to DOM paint order (#113's exact failure shape) — a
  // dropdown is the most recent click, so it outranks the persistent
  // drawer by a hair.
  //
  // The org switcher's Panel pins to the viewport's right edge — see
  // functions/orgSwitcherPanelPin.js (every other dropdown is unchanged).
  DropdownRoot: {
    zIndex: '10010',
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    PanelHost: { Panel: orgSwitcherPanelPin }
  },

  // Local-AI connect modal — shell-root overlay opened from either AI-settings
  // surface (canvas ParamsBtn dropdown / sidebar SettingsDrawer) when the user
  // picks the Local agent. Mounted here (not inline in the dropdown) because the
  // dropdown Panel's transform+overflow would clip a fixed child and inline
  // growth shifts the dropdown; gated on root._aiBridgeSetupOpen.
  //
  // NO STAGE LATCH HERE, deliberately (AI-BRIDGE-MODAL-BLOCKS-SIGNED-OUT-1).
  // A mount-site `if:` DISCARDS the component's own `if:`, it does not AND
  // with it: smbls create() runs applyExtends first (the component's gate
  // lands on `element.if`), then `if (definition.if !== undefined) element.if
  // = definition.if` overwrites it. With `if: _atStage(OVERLAYS)` here — a
  // ONE-WAY latch — the modal's own gate (`_aiBridgeSetupOpen && local`) was
  // gone, so this fixed inset:0 z-index:10600 scrim mounted on EVERY load,
  // signed out included, swallowed every click underneath (a mouse-only
  // visitor could not reach Sign in) and its Done button was a silent no-op.
  // The latch buys nothing here anyway: create() returns early on a false
  // `if:`, so the component's own boot-false gate ALREADY costs zero elements,
  // and registerIfEffect builds the subtree when the flag flips. A staged
  // subtree that must gate itself uses `hide:` instead — different key, no
  // clobber (AiActionConfirmCard, same OVERLAYS stage).
  AiBridgeModal: {},

  // Unified tooltip system — @symbo.ls/tooltip. Same shell-mount
  // rationale as DropdownRoot. Replaces the workspace's prior pattern of
  // a per-app `NavbarTooltip` element + imperative
  // `setNavbarTooltipPosition` updater. Hover triggers route through
  // `el.call('openTooltip', { anchorEl, text })` or extend
  // `TooltipTrigger`. Mounted with no callers initially — existing
  // NavbarTooltip / setNavbarTooltipPosition keep working until the
  // call-site migration sweep lands.
  //
  // zIndex (local, the DropdownRoot reasoning above): the plugin's 2500 sits
  // under every layer of this shell that carries tooltip triggers — the
  // AssistantDrawer (10000, its header AssistantIconButtons), dropdown panels
  // (10010, the QuickAdd NavbarButtons), dialogs (10500) — so those tooltips
  // painted BEHIND the surface that owns their trigger. A tooltip is the most
  // transient surface there is and never takes a pointer: 10700 clears the
  // voice overlay (10600) and stays under the drag overlays (99998+) and the
  // toast host.
  //
  // Panel — the brand's floating chrome: components/ShellTooltipRoot.js.
  TooltipRoot: {
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    zIndex: '10700',
    extends: 'ShellTooltipRoot'
  },

  // THE MAIN AREA OVER A PAGE (AI_ASSISTANT_SPEC §12.2, main thread =
  // "Everywhere"): the main thread, a generated board or a live deck over the
  // page sheet of every route but the home (the home keeps them in its grid
  // area). Below the PageSlot (1300) and the composer (1400); the page stays
  // mounted underneath. Built on the FIRST main view, not at boot (the
  // deferred-subtree latch above) — a one-way latch, like the drawer's. A
  // call's Fly state (`meetFly`, meet functions/meetFly.js) counts too: the
  // call's theater is a main view, and a page asked in the call chat, by voice
  // or by another participant opens it in a tab that never opened a thread, a
  // board, a deck or a Fly canvas.
  AssistantMainLayer: {
    // Inline fixed-position defense at the mount site (the shell-root
    // grid-overlay rule — CanvasSplitView, ModalRoot and the drawer do the same).
    position: 'fixed',
    if: (el, s) =>
      _latchOnce(
        el,
        '__wsDeferredMounted',
        !!(
          s?.root?._homeThreadOpen ||
          s?.root?._aiBoard ||
          s?.root?._aiDeck ||
          s?.root?._aiFly ||
          s?.root?.meetFly
        )
      )
  },

  // One local picker for both Fly hosts. Its lightweight lifecycle opens an
  // empty Fly workspace on mode entry, before any model request exists — and,
  // Fly being the home's default view, on the first arrival on the home
  // (the route) where the person can ask (the flags).
  AssistantFlyWorkspace_picker: {
    presentation: 'picker',
    if: _atStage(SHELL_STAGE.INTERACTIVE),
    // homeModeSync first (BLD-13): the home tab's mode on the home, the
    // person's own pick anywhere else — then the canvas follows that mode.
    subscribeTo: [
      'workspace',
      'assistantMode',
      '_aiFly',
      'currentPath',
      '_featureFlags',
      '_homeMode'
    ],
    onMounted: (el, s) => {
      el.call('homeModeSync')
      el.call('assistantFlyWorkspaceMounted', el, s)
    },
    onUpdate: (el, s) => {
      el.call('homeModeSync')
      el.call('assistantFlyWorkspaceMounted', el, s)
    }
  },
  AssistantFlyWorkspace_return: {
    presentation: 'return',
    if: _atStage(SHELL_STAGE.INTERACTIVE)
  },

  // Blur layer for an open PageSlot. A SIBLING of the slot, not a child: the
  // slot's own z-index makes it a stacking context, so a descendant backdrop
  // could only ever blur the chrome too. See PageSlotBackdrop for the z map.
  PageSlotBackdrop: {},

  // The shell modal layer — all geometry/behavior lives in the component
  // (canvas/components/PageSlot.js); opened via root._canvasSlot(/Second).
  PageSlot: {},

  // A home widget's "+" form as a modal sheet over the board — never the
  // app's whole page (components/HomeWidgetFormHost.js, pick 10 C).
  HomeWidgetFormHost: {},

  // AI action confirmation card — shown when sdk.ai.dispatch returns
  // intent:'action' in authMode='ask'. Reads root._aiActionProposals;
  // Confirm fires sdk.ai.executeActions. Mounted once at the shell
  // root so every consumer (home prompt, AppAssistant, ticket AI
  // editor) surfaces a consistent confirmation UI.
  AiActionConfirmCard: { if: _atStage(SHELL_STAGE.OVERLAYS) },

  // (Removed 2026-06-03: AiModePicker legacy shell-mount migrated to
  // the centralized @symbo.ls/dropdown DropdownRoot above. ParamsBtn
  // now dispatches `el.call('openDropdown', { content: { extends:
  // 'AiModePickerBody' } })` and the plugin's single Panel renders
  // the body. State carriers _aiModePickerTick (for active-checkmark
  // re-render) + _aiSvcReady (for SDK-ready gate) stay in state-app
  // because AiPickerRow.onClick still bumps the tick. The legacy
  // shell mount is unmounted; AiModePickerBody is the new entry
  // point (shared/components/AiModePicker.js).)

  // (Removed 2026-06-01: PersonDetailModal moved to @symbo.ls/windows.
  // Callers dispatch via `el.call('openPersonDetailModal', person)` —
  // the helper writes the person to `root._personDetail` (kept as data
  // carrier; the body's `_person(s.root)`, `_teamsFor`, etc. still read
  // from it) and pushes a custom-template layer. Close:
  // `closePersonDetailModal` — also clears `_personDetailShowAdd` +
  // `_personDetailAddTeamId` (the inline Add-to-Team panel state).)

  // Simone assistant drawer — shell-scope mount rationale matches
  // ConfirmModal / AppCreateOrgModal above. AppAssistantToggle lives in
  // AppHeader (mounted at shell scope) and flips `root.assistantOpen`;
  // the AppAssistant drawer reads that flag to slide in. wp/app.js still
  // declares an AppAssistant slot, but the wp sub-app wrapper never
  // instantiates under the 3.14 auto-router, so without this shell-scope
  // mount the toggle was a no-op visually (state flipped, nothing on
  // screen). `aiStream` resolution still flows through the merged
  // context.functions (wp registers it), so the drawer's Send path
  // works once mounted.
  // Workspace AI drawer = simone's Chat component (imported via
  // shared/components/assistantSimone.js from extensions/packages/simone).
  // Single source of truth for the chat UI across the chrome extension
  // and the workspace shell. Its composer reads `chatInput`, its message
  // list reads `chatMessages`, its composer dispatches `sendPrompt(text)`
  // which calls `window.__simoneSendOverBridge` — workspace installs
  // that handle to route through sdk.ai (see installSimoneAiTransport).
  //
  // We anchor it via a wrapping container that pins it to the right
  // edge as a drawer and gates visibility on `assistantOpen` so existing
  // openers (the magic-star toggle button, homeAiSend's auto-open) work
  // unchanged. Simone's Chat itself takes 100%/100% of the wrapper.
  AssistantDrawer: {
    // Built on FIRST OPEN, not at boot — see the deferred-shell-subtrees
    // note at the top of this file. Every opener in the monorepo (the
    // magic-star toggle, homeAiSend, ReasoningPill, Suggestions, the ticket
    // and event detail modals, YourAgentCard, aiAssistant's askUser path)
    // writes `assistantOpen: true` on the root state, so ONE latch on that
    // carrier covers all of them with zero opener changes. The latch is
    // one-way: after the first open the `hide:` predicate below owns
    // visibility again, exactly as before.
    if: (el, s) => _latchOnce(el, '__wsDeferredMounted', !!s?.root?.assistantOpen),
    // Visible only when the assistant is open AND not minimized. 'minimized'
    // collapses it into the SimoneReasoningPill above the composer (the pill's
    // own hide rule is dock-aware). Closed → hidden entirely. Feature flag:
    // aiAssistant off at /flags kills the drawer wholesale.
    hide: (el, s) =>
      !shellFeatureEnabled(s.root, 'aiAssistant') ||
      !s.root?.assistantOpen ||
      s.root?.assistantDock === 'minimized',
    // THE DOCKED SIDEBAR (Nika 2026-09-26: "make assistant looking as it was
    // back as sidebar with no rounds" — this reverses the floating panel of
    // DESIGN_SPEC §9 / Q7 A): flush to the screen edge from the top of the
    // viewport to the bottom, exactly over the column the shell reserves for
    // it, square corners. It appears with the content column's own reflow
    // (the grid track eases over .22s) and no motion of its own, as it did.
    // The one change kept from the redesign is not about the shape: the
    // divider and the depth are the `overlay` elevation token (a 1px ring,
    // which is the edge line against the content, plus two soft layers), not
    // a hairline border and a raw `-16px 0 32px rgba(0,0,0,.35)` shadow.
    position: 'fixed',
    top: '0',
    height: '100dvh',
    shadow: 'overlay',
    // Dock EDGE and WIDTH are driven by the injected shell CSS keyed on <html
    // data-assistant-dock> — NOT by props here — because v3.14 freezes
    // reactive layout-prop factories (width/left/right) at mount, and the
    // drawer width must reference the shared --assistant-width var so it stays
    // in lockstep with the content's reserved grid column (a raw var() on
    // `width` also empty-rules in the atomic compiler). The user drags the
    // ResizeHandle to override the width; the dragged value persists across
    // re-renders via root.assistantWidth and is republished to
    // --assistant-width by _syncShellRoute. Default 640px lives in the CSS
    // (:root var fallback).
    zIndex: '10000',
    // GLASS (AI_ASSISTANT_SPEC v1 §8 blur policy, Nika's pick 7 A): the house
    // floating-glass recipe — the brand `canvas-card-glass` theme (a
    // translucent fill + backdrop blur, the same one the menus and popovers
    // wear) instead of the opaque `document` sheet; the docked edge keeps
    // the `overlay` ring above, so no second border.
    theme: 'canvas-card-glass',
    borderWidth: '0',
    color: 'title',
    flow: 'y',
    // Cursor + select lock during drag — same trick the legacy
    // AppAssistant used: reactive prop on the drawer root flips to
    // col-resize / none while dragging, so the cursor stays correct
    // even when the pointer escapes the handle's bounding box.
    cursor: (el, s) => (s.root?.assistantResizing ? 'col-resize' : ''),
    userSelect: (el, s) => (s.root?.assistantResizing ? 'none' : ''),

    // Drag handle — narrow column on the LEFT edge, col-resize cursor,
    // pointer-captured during drag so the mousemove + mouseup land here
    // even when the pointer leaves the handle's bounding box. Min/max
    // clamps match the drawer's minWidth/maxWidth so the resize stays
    // visually consistent with the layout constraints.
    ResizeHandle: {
      tag: 'div',
      position: 'absolute',
      left: '0',
      top: '0',
      bottom: '0',
      width: '6px',
      cursor: 'col-resize',
      background: 'transparent',
      zIndex: '1',
      ':hover': { background: 'selected' },
      // A real control (AI_ASSISTANT_SPEC v1 §8 "adjustable width"): a focusable
      // vertical separator with its value, moved by the Arrow keys (Shift for
      // a longer step), Home / End to the bounds (functions/assistantWidth.js
      // assistantWidthForKey). The width is REMEMBERED per person when a drag
      // or a key ends (setAssistantWidth).
      role: 'separator',
      tabindex: '0',
      ariaOrientation: 'vertical',
      ariaLabel: (el) => el.call('polyglot', 'workspace.assistant.resize'),
      ariaValuemin: '320',
      ariaValuenow: (el, s) =>
        String(Number(s.root?.assistantWidth) > 0 ? s.root.assistantWidth : 640),
      ':focus-visible': { background: 'selected', outline: 'none' },
      onKeydown: (e, el, s) => {
        const root = s.root
        if (!root) return
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
        const current = Number(root.assistantWidth) > 0 ? root.assistantWidth : 640
        const next = el.call('assistantWidthForKey', current, e.key, {
          dock: root.assistantDock,
          shift: !!e.shiftKey,
          vw
        })
        if (next == null) return
        e.preventDefault()
        if (next !== current) el.call('setAssistantWidth', next)
      },

      onPointerdown: (e, el, s) => {
        e.preventDefault()
        const parentRect = el.parent?.node?.getBoundingClientRect?.()
        el._dragStartX = e.clientX
        el._dragStartW = parentRect ? parentRect.width : 640
        s.root?.update?.({ assistantResizing: true })
        if (el.node?.setPointerCapture && e.pointerId != null) {
          try {
            el.node.setPointerCapture(e.pointerId)
          } catch (_) {}
        }
      },

      onPointermove: (e, el, s) => {
        if (!s.root?.assistantResizing) return
        if (el._dragStartX == null) return
        // Handle sits on the drawer's INNER edge. Right dock → handle on the
        // left edge, dragging LEFT widens (dx = start - clientX). Left dock →
        // handle on the right edge, dragging RIGHT widens (dx = clientX -
        // start). Flip the sign by the current dock so both feel natural.
        const leftDock = s.root?.assistantDock === 'left'
        const dx = leftDock ? e.clientX - el._dragStartX : el._dragStartX - e.clientX
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
        const next = Math.max(320, Math.min(Math.floor(vw * 0.5), el._dragStartW + dx))
        if (next === s.root.assistantWidth) return
        s.root.update({ assistantWidth: next })
      },

      onPointerup: (e, el, s) => {
        if (!s.root?.assistantResizing) return
        el._dragStartX = null
        el._dragStartW = null
        s.root.update({ assistantResizing: false })
        // The drag ends: remember the width for this person.
        if (Number(s.root.assistantWidth) > 0) el.call('setAssistantWidth', s.root.assistantWidth)
        if (el.node?.releasePointerCapture && e.pointerId != null) {
          try {
            el.node.releasePointerCapture(e.pointerId)
          } catch (_) {}
        }
      },

      onPointercancel: (e, el, s) => {
        if (!s.root?.assistantResizing) return
        el._dragStartX = null
        el._dragStartW = null
        s.root.update({ assistantResizing: false })
      }
    },
    // Workspace AI drawer = simone's canonical Chat, reused wholesale. The
    // modern 3-section header (Sessions ▾ · TargetSwitcher · Provider ▾ +
    // Avatar) and the composer toolbar now live in simone's Chat.js as the
    // single source of truth — NO inline header override here (the old copy
    // duplicated ext-ui's home.js and overlapped in the narrow drawer).
    // `inspectorTools` stays false in workspace state, so the composer's
    // Pick/Inspect buttons (chrome-extension only) stay hidden.
    // Slot override (sanctioned consumer pattern — same as ext-ui's
    // Title.Icon.onClick): append a close X at the far right of the header's
    // Actions cluster. Drawer-specific affordance, workspace app ONLY —
    // standalone simone and the extension panels are full-surface and have
    // no drawer to close, so this never lands in the shared Chat component.
    Chat: {
      Header: {
        // Drawer width can be narrow on any viewport. Keep every control
        // reachable by wrapping the header and its action group locally.
        flexWrap: 'wrap',
        Title: {
          Icon: null,
          flex: '1 1 auto',
          minWidth: '0',
          maxWidth: '100%',

          Sessions: {
            minWidth: '0',
            Trigger: {
              minWidth: '0',
              maxWidth: '100%',
              Label: {
                minWidth: '0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }
            }
          }
        },
        // THE HEAD KEEPS FOUR CONTROLS IN VIEW (the AI overhaul, sidebar
        // finding 2, 2026-10-28 — it carried eight): Chat history (the
        // Sessions trigger above), the mode chip, Minimize and Close. The
        // other four — Switch AI model, Open in main, Dock left/right, AI
        // settings — are the rows of ONE overflow menu
        // (AssistantIconButton_more below); each row runs the handler its old
        // button ran and keeps its label and glyph.
        Actions: {
          flexWrap: 'wrap',
          maxWidth: '100%',
          minWidth: '0',
          // The shared Chat head's AI-settings button leaves the drawer head:
          // its menu row opens the same settings sheet.
          SettingsBtn: null,
          // THE MODEL MENU'S ONE HOST stays: the composite's picker, opened by
          // `root._modelMenuOpen` — the flag the menu row writes, and the chat
          // error row's "Switch model" too. Its trigger leaves the head, and
          // the composite draws no box of its own: an empty flex item would
          // add one more gap to the row, and its picker would hang from a
          // 0 × 0 box. The picker anchors to the HEAD instead (the shared
          // Chat header is the positioned parent; this row is not), its right
          // edge inset by the head's own inline padding (Z), so it hangs
          // under the row's right end on a wide drawer. On the 390 px sheet
          // the four-control row is ~170 px wide and the head is the sheet's
          // width: anchored to the row, the picker was squeezed to the row's
          // width (169 px, two lines a row); anchored to the head it takes
          // its full width inside the screen (222 px, measured live).
          AiModelSwitcher: {
            display: 'contents',
            AiModelSwitcherTrigger: null,
            Picker: { right: 'Z' }
          },
          // THE MODE CHIP (design-d2's AssistantModeChip, pick 5 B): Assistant ·
          // Dashboard · Apps — reads root.assistantMode, writes through
          // setAssistantMode, hides itself while the writer is absent. The
          // drawer IS a thread surface (the concierge for Nika 2026-10-28:
          // "when a thread is on screen, the chip names the view"): an ask
          // typed here stays in this chat, so the chip names Chat over a
          // stored Dashboard; Apps keeps its name (its thread is Build).
          AssistantModeChip: { flexShrink: '0', threadSurface: true },
          // THE OVERFLOW — the assistant chrome's icon button by its key: icon
          // only (the house ••• `moreHorizontal`), a menu button
          // (aria-haspopup="menu", aria-expanded while its menu is open), its
          // name and tooltip from the bundle. Enter and Space click the real
          // <button>; ArrowDown / ArrowUp open the menu too (the menu-button
          // contract); a second click closes it. The dropdown layer owns
          // Escape, the outside press and the focus return to this button.
          AssistantIconButton_more: {
            icon: 'moreHorizontal',
            title: (el) => el.call('polyglot', 'common.action.moreActions'),
            ariaLabel: (el) => el.call('polyglot', 'common.action.moreActions'),
            ariaHaspopup: 'menu',
            ariaExpanded: 'false',
            flexShrink: '0',
            // the open menu: the `selected` wash while aria-expanded (the
            // mode chip's trigger state)
            '&[aria-expanded="true"]': { background: 'selected', color: 'title' },
            scope: {
              // The four rows, built when the menu opens, so each label is
              // read in the live language:
              //   • Switch AI model — opens the model menu's one host (the
              //     composite's picker above) and refreshes the providers'
              //     diagnostics, as its trigger did;
              //   • Open in main (AI_ASSISTANT_SPEC v1 §1, §12.2) — the SAME
              //     thread moves to the main area of the page the person is on
              //     (functions/assistantMainThread.js). ALWAYS offered — every
              //     route, an empty chat too (Nika 2026-09-30: "why I dont see
              //     move to main when ai is in sidebar"). Its glyph is the
              //     workspace set's `lnGrid` (shared designSystem/icons.js), a
              //     shell-registered icon, so no device renders it blank;
              //   • Dock left / right — ONE side toggle (2026-08-12 per Nika
              //     "remove two sidebar (left/right) icons in assistant and
              //     keep only that does the action"): the row names the side
              //     the drawer will MOVE TO — docked right → "Dock left";
              //     docked left → "Dock right". setAssistantDock persists it;
              //   • AI settings — the shared AiSettingsButton's toggle in a
              //     Chat surface: Chat's own settings sheet.
              // A row carries DATA only — its `act`, a flat string. A
              // lowercase prop holding a function is wrapped into a getter
              // that RUNS it on every read (Rule 70), and the handler reads
              // the act off the ROW, never a variable captured from this map
              // (frank FA205).
              headerMoreRows(el, s) {
                const left = !!(s && s.root && s.root.assistantDock === 'left')
                const rows = [
                  ['model', 'ai.settings.switchModel', 'magicstar'],
                  ['main', 'workspace.assistant.openInMain', 'lnGrid'],
                  left
                    ? ['right', 'workspace.assistant.dock.right', 'sidebarRightFill']
                    : ['left', 'workspace.assistant.dock.left', 'sidebarLeftFill'],
                  ['settings', 'ai.settings.panelLabel', 'preferences']
                ]
                return rows.map((r) => ({
                  key: r[0],
                  act: r[0],
                  text: el.call('polyglot', r[1]),
                  Icon: { name: r[2] },
                  onClick: (ev, row) => {
                    row.call('closeDropdown', 'assistantHeaderMore')
                    const root = row.state && row.state.root
                    const act = row.act
                    if (act === 'main') return row.call('assistantThreadToMain')
                    if (act === 'left' || act === 'right') return row.call('setAssistantDock', act)
                    if (!root || typeof root.update !== 'function') return
                    if (act === 'settings') return root.update({ showSettings: !root.showSettings })
                    if (act !== 'model') return
                    root.update({ _modelMenuOpen: true })
                    try {
                      row.call('fetchAiProviderDiagnostics')
                    } catch (_) {}
                  }
                }))
              },
              // aria-expanded follows the dropdown layer: its onOpen once the
              // layer is up, its onClose on every close — a row, Escape, an
              // outside press, or another dropdown replacing this one. Written
              // through the element's own update as `attr` (the mode chip's
              // path: the flat key is applied once, at create).
              headerMoreExpanded(el, open) {
                if (!el || !el.node) return
                el.update({ attr: { 'aria-expanded': open ? 'true' : 'false' } })
              },
              // THE HOUSE MENU (MenuPanel rows, its keyboard model and focus
              // arming) in the shell's dropdown layer, painted like the mode
              // chip's menu beside it: the glass and the elevation on the
              // layer, a body that paints nothing (the opaque `dialog`
              // surface under reduced transparency).
              headerMoreOpen(el, s) {
                el.call('openDropdown', {
                  anchorEl: el,
                  id: 'assistantHeaderMore',
                  placement: 'bottom-end',
                  round: 'radiusCard',
                  theme: 'search-glass',
                  shadow: 'overlay',
                  onOpen: () => el.scope.headerMoreExpanded(el, true),
                  onClose: () => el.scope.headerMoreExpanded(el, false),
                  content: {
                    extends: 'MenuPanel',
                    theme: null,
                    background: 'transparent',
                    backdropFilter: 'none',
                    shadow: 'none',
                    ':focus-visible': { boxShadow: 'none' },
                    '@reduceTransparency': { theme: 'dialog' },
                    ariaLabel: el.call('polyglot', 'common.action.moreActions'),
                    childExtends: 'MenuPanelItem',
                    children: this.headerMoreRows(el, s)
                  }
                })
              }
            },
            onKeydown: (e, el, s) => {
              if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
              e.preventDefault()
              e.stopPropagation()
              el.scope.headerMoreOpen(el, s)
            },
            onClick: (e, el, s) => {
              e.preventDefault()
              e.stopPropagation()
              // a second click closes it (the dropdown plugin's own trigger
              // toggles the same way); the open layer's id is on the root
              // (the shared openDropdown's `_dropdownLayer` mirror)
              if (s && s.root && s.root._dropdownLayer === 'assistantHeaderMore')
                return el.call('closeDropdown', 'assistantHeaderMore')
              el.scope.headerMoreOpen(el, s)
            }
          },
          MinimizeBtn: {
            extends: 'AssistantIconButton',
            icon: 'minimize',
            title: (el) => el.call('polyglot', 'workspace.assistant.minimize'),
            flexShrink: '0',
            onClick: (e, el, s) => setAssistantDock(s.root, 'minimized', el.sdk)
          },
          CloseBtn: {
            extends: 'AssistantIconButton',
            icon: 'crossmark',
            title: (el) => el.call('polyglot', 'workspace.assistant.close'),
            flexShrink: '0',
            onClick: (e, el, s) => s.root?.update?.({ assistantOpen: false })
          }
        }
      }
      // The composer-toolbar affordances used to be hidden here because they
      // had NO backend in the workspace assistant (they belonged to the
      // chrome-extension / bridge surface). Both are live now, so NOTHING is
      // overridden away — the history is kept because the failure mode was
      // subtle: the real buttons live at ComposerWrap > PromptTextarea >
      // PromptButtons, NOT directly under Chat, so an earlier shallow override
      // created a phantom hidden node while the live buttons stayed visible
      // and dead.
      //   #2326 SettingsBtn → RE-ENABLED 2026-09-02 ("ai sidebar textarea is
      //         missing attach buttons" per Nika): SettingsBtn is the shared
      //         context-aware AiSettingsButton now and the SettingsDrawer
      //         mounts on Chat itself.
      //   #2327 UploadBtn   → RE-ENABLED 2026-09-02: the images now TRANSMIT.
      //         Chat's composer `send` reads its own `state.images` and passes
      //         them to sendPrompt, which normalizes + caps them onto the
      //         envelope as `attachments`; simoneAiTransport forwards them to
      //         sdk.ai, the SDK posts them on the turn, and the server
      //         persists them as `image` content parts the multimodal
      //         providers send as real image blocks. The old rationale — "the
      //         picker would collect images that never leave the machine" — is
      //         no longer true, so the picker is no longer hidden.
    }
  },

  // Messenger — the AppHeader Messenger button flips `root._chatDrawerOpen`
  // (and bootstraps chat state). MessengerOverlay (from @symbo-ls/workspace-chat)
  // reads that flag and renders the floating chat list on the left — the same
  // glass float the /studio AppSidebar uses — plus a right-side conversation
  // window (like CanvasPageSlot) once a channel/DM is opened. Replaces the old
  // right-edge ChatDrawer slide-in / rail dock.
  // Chat bundle-fold (2026-08-01): the MessengerOverlay definition lives in
  // the lazily-folded chat context. A bare-key child here CANNOT defer to the
  // fold — DOMQL captures the extends resolution at PARENT parse time, so a
  // pre-fold mount instantiates an empty unknown-key div even if the fold
  // registers the component before an `if:` latch flips (verified live).
  // Instead this is an inert slot that ensureChatContext fills ONCE, post-
  // fold, via `slot.set({ extends: 'MessengerOverlay', … })` — the same
  // deferred-resolution mechanism lazyPage uses for route bodies. The set
  // definition restates the drawer + feature-flag hide (a mount-level hide
  // REPLACES the base component's own — verified live 2026-07-25).
  MessengerOverlaySlot: { display: 'contents', if: _atStage(SHELL_STAGE.CHROME) },

  // Mail — the AppHeader envelope flips `root._mailDrawerOpen` (through
  // headerOverlayPatch, so the Messenger / notifications / assistant close).
  // MailOverlay (from @symbo-ls/workspace-mail) reads that flag and renders
  // the docked mail list in the same left glass float the Messenger uses.
  // Same deferred-resolution shape as MessengerOverlaySlot above: the
  // definition lives in the lazily-folded mail context, so this is an inert
  // slot that ensureMailContext (functions/mailFold.js) fills ONCE, post-fold,
  // via `slot.set({ extends: 'MailOverlay', … })`.
  MailOverlaySlot: { display: 'contents', if: _atStage(SHELL_STAGE.CHROME) },

  // The App Store's remove dialog for the shell's own Remove (/add-app
  // "Installed apps"). Same deferred-resolution shape as MailOverlaySlot: the
  // dialog (MarketplaceUninstallDependentsDialog) lives in the lazily-folded
  // marketplace context, so this inert slot is filled ONCE, post-fold, by
  // ensureMarketplaceUninstallDialog (functions/marketplaceUninstallDialog.js).
  // The slot is the dialog's HOST: `_marketplaceHost` binds the store's
  // marketplaceHostOf here, and its own state keeps the store's flow
  // (uninstallDepsModal, the installed-apps cache) off the shell root.
  MarketplaceUninstallSlot: {
    display: 'contents',
    _marketplaceHost: true,
    state: { uninstallDepsModal: null, _activeWsApp: null },
    if: _atStage(SHELL_STAGE.INTERACTIVE)
  },

  // Global right-click context menu (the /files menu, promoted to shared).
  // Driven by root.contextDropdown — files + chat both write it.
  ContextDropdown: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // (Removed 2026-06-16: CapTableGrantModal + grant functions moved to /financials/
  // backup directory — no longer part of the active app.)

  // (Removed 2026-06-01: AppLightbox moved to @symbo.ls/windows. Callers
  // dispatch via `el.call('openLightbox', { url, mime, name })` — the
  // helper writes the descriptor to `root.lightbox` and pushes a
  // custom-template layer whose content extends `AppLightbox` (now a
  // body-only component). No shell-root slot needed.)

  // Onboarding overlay shown to signed-in users without a team-org. Intranet
  // is an org-level surface; solo users belong on canvas/marketplace.
  NoOrgOverlay: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // WHITE-LABEL HOSTS ONLY — the signed-in non-member gate (branded
  // "Request an invitation" screen; Nika 2026-08-30, option A). The key
  // resolves from the whitelabel barrel context.js spreads behind
  // isCustomDomainHost(), and the SAME classifier gates the mount here so
  // a platform host never even mounts the element — my.symbols.app stays
  // bit-identical. Its own hide:/display: pair does the membership gating.
  WhitelabelRequestAccess: {
    if: (el, s) => isCustomDomainHost() && _atStage(SHELL_STAGE.INTERACTIVE)(el, s)
  },

  // Invitee onboarding nudge — opens the light "pick your username" prompt
  // once after an invite acceptance (never org creation for invitees; see
  // NoOrgOverlay's invite suppression + functions/usernamePrompt.js).
  UsernamePromptGate: { if: _atStage(SHELL_STAGE.INTERACTIVE) },

  // THE NEW-ACCOUNT WELCOME (functions/onboarding.js). The gate holds the
  // setup surface over a new account's first load while its record is read,
  // then opens the wizard on it once, after the gates above have had their
  // turn; the surface is built on FIRST NEED (the hold or the flow) and stays
  // built; the tour's layer exists only while the tour runs — it makes the
  // page inert while it does.
  OnboardingGate: { if: _atStage(SHELL_STAGE.INTERACTIVE) },
  OnboardingWizard: {
    if: (el, s) =>
      _latchOnce(el, '__onboardingBuilt', !!(s?.root?._onboardingFlow || s?.root?._onboardingHold))
  },
  OnboardingTour: { if: (el, s) => !!s?.root?._onboardingTour },

  // Credit guard (tickets/fable.md, 2026-08-13): the out-of-credits
  // full-viewport blocker + the admin low-credits corner pill, both fed by
  // `_creditGuard` (functions/creditGuard.js — sdk.getCreditBalance on boot
  // + workspace switch). `display:'contents'` wrappers + fixed descendants,
  // same shell-grid-box hazard DemoClaimBanner below documents. Inert until
  // the balance loads; a metering error never blocks.
  CreditBlocker: { if: _atStage(SHELL_STAGE.INTERACTIVE) },
  CreditBanner: { if: _atStage(SHELL_STAGE.CHROME) },

  // Demo-session conversion bar — a slim bottom-left chrome bar shown ONLY to
  // unclaimed demo accounts (gated on root.isDemo, seeded by bootShell). Its
  // "Claim this workspace" button opens a self-contained claim Modal that
  // calls sdk.claimDemoAccount; on success it clears root.isDemo (the bar
  // retracts) + fires a success toast. Invisible to every real user.
  // position:fixed (own its viewport anchor), so it sits outside the AppShell
  // grid and never disturbs the AppHeader/content rows.
  DemoClaimBanner: { if: _atStage(SHELL_STAGE.CHROME) },

  // "Viewing as <role>" exit pill (tickets/fable.md PERSONA-6). Sibling of
  // routed content, never inside AppHeader/nav chrome, so a restricted
  // persona role can't take the exit down with it if its own chrome fails
  // to render. `display:'contents'` wrapper — same shell-grid-box hazard
  // DemoClaimBanner above documents. Inert today: `hide:`-gated on
  // `_personaSession`, which nothing writes yet (opus.md PERSONA-1/2,
  // sonnet.md PERSONA-4/5 — both still open) — landing this changes nothing
  // for any current session until that state exists.
  PersonaPill: {
    if: _atStage(SHELL_STAGE.CHROME),
    // The pill's own mount asks the persona oracle (GET /persona) at once —
    // also on a signed-out page, where the server answers 401 (persona audit
    // OV-2). This mount-site `onRender` replaces the shared pill's (a function
    // is never merged): the same ask, only with a session and its workspace
    // (functions/sessionStreams.js). The signed-in boot asks anyway, once its
    // workspace lands (loadPrivateData), so nothing is lost.
    onRender: (el, s) => {
      if (el.call('personaOracleReady', s.root)) el.call('refreshPersonaSession', { mount: true })
    }
  },

  // Picture-in-picture mini widget for an active meet call. Fixed-position,
  // hidden unless a LiveKit Room is connected and the user is off the call
  // view. Persists across route navigation because the Room lives as a
  // module variable in functions/meet/connectCall.js.
  // --calldock-clear (root CSS): its top edge from the viewport bottom. The
  // dock moves with --zoombar-clear without a size change, so every update
  // re-measures (the tracker settles a position-only move).
  MeetCallWidget: {
    if: _atStage(SHELL_STAGE.OVERLAYS),
    onRender: (el) => _trackClearVar(el, '_cdObs', '--calldock-clear', _clearFromTop),
    onUpdate: (el) => _trackClearVar(el, '_cdObs', '--calldock-clear', _clearFromTop),
    onWindowResize: (e, el) => el._cdObsApply?.(),
    onBeforeRemove: (el) => _stopClearVar(el, '_cdObs')
  },

  // The call's sound, beside the dock: one <audio> per remote member at the
  // shell root, so a live call keeps playing on every route (the tiles'
  // <audio> unmounted with /meet — meet components/CallAudio.js).
  MeetCallAudio: { if: _atStage(SHELL_STAGE.OVERLAYS) },

  // A spoken Fly ask in a call waits for its yes (Nika 2026-10-26): the card
  // with what the call heard, Show and Cancel — the asker's own, on every
  // route (meet components/MeetFlyConfirm.js, functions/meetFly.js).
  MeetFlyConfirm: { if: _atStage(SHELL_STAGE.OVERLAYS) },

  // Call-invitation banner (tickets/fable.md, Nika 2026-08-13): a
  // beautiful, time-triggered, app-wide banner when a scheduled meeting
  // reaches its due window — fed by `notifs` (the durable, server-swept
  // `meeting_starting` notification, see
  // server/.../meetingStartingScheduler.js) via functions/notifications/
  // meetingInviteBanner.js's `activeMeetingInvite`. `display:'contents'`
  // wrapper + fixed descendant, same shell-grid-box hazard DemoClaimBanner
  // above documents. Fixed top-right — the one corner not already claimed
  // by CreditBanner/DemoClaimBanner/PersonaPill (bottom-left),
  // GlobalNotification (bottom-center) or MeetCallWidget (bottom-right).
  CallInvitationBanner: { if: _atStage(SHELL_STAGE.OVERLAYS) },
  // The person called rings (Fly's People, "call <name>", the person card's
  // Call — functions/callPerson.js): the invite's own card with the caller,
  // Join call and Decline (components/IncomingCallBanner.js).
  IncomingCallBanner: { if: _atStage(SHELL_STAGE.OVERLAYS) },

  // THE shell call panel (comms-phone v2 — calls inside the workspace, Nika
  // 2026-10-01). One instance, here, so a call survives every route change;
  // hidden while `root._callPanel` is null. Docked bottom-right on the
  // composer-top line — clear of the toast stack (bottom-centre) and the
  // CallInvitationBanner. The voice session behind it starts from
  // _syncShellRoute (ensureWorkspaceVoice) and stops on a workspace switch
  // (loadPrivateData.js) and in onRemove below.
  WorkspaceCallPanel: { if: _atStage(SHELL_STAGE.OVERLAYS) },

  // AGENT MODE (WS-AGENT-MODE-1): the workspace TUI as the whole shell — a
  // fixed, opaque frame over the header, the rail and the page
  // (components/AgentModeFrame.js). Mounted only while it is on, for a
  // signed-in person: a shell that never enters it builds no element of it
  // and never loads the shared model package (functions/agentModeRuntime.js).
  AgentModeFrame: {
    // Every key read up front: a short-circuit would leave one untracked.
    if: (el, s) => {
      const signedIn = s.root.signedIn === true
      const chosen = s.root.agentMode === true
      const link = s.root._agentModeLink === true
      return signedIn && (chosen || link)
    }
  }

  // (Removed 2026-06-01: MeetNewRoomModal moved to @symbo.ls/windows.
  // Callers dispatch via `el.call('openNewRoomModal', initialDraft?)` —
  // the helper writes the draft to `root.newRoomDraft` (kept as data
  // carrier) and pushes a custom-template layer whose content extends
  // `MeetNewRoomModal` (now body-only). Close: `closeNewRoomModal`.)

  // (Removed 2026-06-01: CalProfileModal moved to @symbo.ls/windows.
  // Callers dispatch via `el.call('openProfileModal', email)` — the
  // helper writes the email to `root.profileEmail` (kept as data carrier
  // for chat hash sync + body reads) and pushes a custom-template layer.
  // Close: `closeProfileModal`.)

  // (Removed 2026-06-01: CalEventDetailModal moved to @symbo.ls/windows.
  // Callers dispatch via `el.call('openEventDetailModal', event)` —
  // the helper writes the event to `root.selectedEvent` (kept as data
  // carrier) and pushes a custom-template layer. Internal close paths
  // (delete-event, edit-event-and-open-new-booking, etc.) continue to
  // set `selectedEvent: null` inline; the body's subscribeTo+onUpdate
  // watcher detects the transition and pops the layer automatically.)

  // (SkipToMain moved to the FIRST child position, above AppHeader — a skip
  // link is only useful as the page's first tab stop, and axe's isSkipLink
  // recognition requires it to precede the first page link in DOM order.)
}

export default shellApp
